<script setup>
/*
  Die Beauty-Airways-Seite: gepinnte Bühne mit Logo→Flugzeug-Morph, Startbogen,
  Flugbahn durch die Stationskarten, Fallblattanzeige, Bordkarte.

  ── Daten ─────────────────────────────────────────────────────────────────
  Der Inhalt kommt aus Craft: das Template _/pages/beauty-airways.twig
  serialisiert den Single-Entry und übergibt ihn als `page`-Attribut
  (JSON-String, Muster wie StoryRoot). Fehlt das Attribut oder parst es nicht,
  greift beauty-airways.data.js — der Entwicklungs-Fallback. Die Struktur ist
  dort als Vertrag dokumentiert.

  Bilder erreichen die Komponente als FERTIGES MARKUP (media-optimized-image
  inkl. KI-Label, per v-html eingesetzt) — die Komponente kennt keine
  Bildpfade und kein Seitenverhältnis: Bilder laufen im hochgeladenen Format,
  die width/height-Attribute liefern die intrinsische Ratio fürs Layout.

  Die Hintergrundkarte ist bewusst KEIN CMS-Inhalt, sondern ein Projekt-Asset
  (chart.svg, per Vite gebündelt): skriptgeneriert aus Natural Earth
  (app/scripts/make-beauty-airways-chart.mjs), auf die Farbstimmung der Seite
  abgestimmt — ein Austausch wäre eine Gestaltungs-, keine Redaktionsfrage.

  ── Geometrie: die Knoten SIND die Stützstellen ───────────────────────────
  Die Seiten der Karten ALTERNIEREN, die Achslagen kommen aus dem Index
  (derive() unten) — die Redaktion bestimmt nur die Reihenfolge. Das CSS
  positioniert daraus die 0×0-Knoten, buildWaypoints() misst sie, und die
  gemessenen Punkte gehen als Stützstellen in die Catmull-Rom-Spline. Der
  Deckel limitSlope() gegen zackige Kurven darf die Punkte verschieben; der
  Knoten wandert dann mit der Korrektur mit — die Kurve wird gedeckelt, die
  Destination bleibt auf ihr.

  ── Schichtung ────────────────────────────────────────────────────────────
  Route, Kondensstreifen und Flugzeug liegen HINTER dem Inhalt: das Flugzeug
  taucht hinter einer Karte ab und kommt in der Lücke wieder hervor.

  Der Ablauf (Morph → Abflug → Flugbahn) und warum die Nähte nicht sichtbar
  sind, ist an den jeweiligen Funktionen ausführlich kommentiert.
*/
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { gsap, ScrollTrigger } from '@/composeables/gsap'
/*
  Der Morph: das Flugzeug entsteht aus den sechs Strichen links und rechts des
  Schriftzugs und zeigt mit der Nase nach unten.

  NOSPIN_PHASES muss mitgegeben werden — der Standard des Moduls ist
  DEFAULT_PHASES (drehende Balken, andere Silhouette).

  Es gab hier einmal eine Umschaltung zwischen zwei Logo-Animationen (`?logo=`
  und logo-variants.js). Beide Staende liegen als statische Seiten unter
  beauty-airways/archiv; das Projekt kennt nur noch diese eine.
*/
import {
    createMorph, PLANE_D, PLANE_BOX, VIEW_BOX, planeTransform,
    NOSPIN_PHASES, ACCENT,
} from './logo-plane-morph-erj2.js'

import {
    monotonizeY,
    limitSlope,
    splineThrough,
    sampleTable,
    lengthAtY,
    pointAt,
    clamp,
} from './flight-path.js'
import { fallback } from './beauty-airways.data.js'
import BoardingPass from './BoardingPass.vue'
/*
  Die Karte kommt als BITMAP, nicht als SVG — und bringt ihre Weichzeichnung
  schon mit. Beides aus demselben Grund wie bei den Wolkensprites: eine Ebene,
  die pro Frame bewegt wird, darf keinen Filter tragen, und WebKit legt das
  Ergebnis einer SVG-Rasterung nicht in den Bild-Cache.

  Erzeugt von app/scripts/make-beauty-airways-chart-bitmap.mjs aus der
  committeten chart.svg. Die SVG bleibt liegen: sie ist die Quelle des Bitmaps
  und die Provenienz der Karte (Natural Earth), nur eben nicht mehr das, was
  der Browser lädt. Ausschnitt oder Farben ändern heißt: chart.svg anfassen,
  dann das Bitmap-Skript laufen lassen.

  Kein `?url` mehr nötig — das war für vite-svg-loader, der sonst eine
  Vue-Komponente statt einer URL geliefert hätte.
*/
import chartUrl from './chart.webp'

gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
    // JSON-String, von Craft/Twig injiziert (statisches Attribut, da Vue
    // In-DOM-Templates keine dynamischen Objekt-Props aus Twig bekommen
    // können). Leer/kaputt -> statischer Fallback (Entwicklung ohne CMS).
    page: { type: String, default: '' },
})

/*
  Der Fallback ist ein reines Entwicklungsmittel (Route ohne CMS-Anschluss) und
  darf im CMS-Betrieb NIE einspringen: er trägt hartkodierte deutsche
  Demo-Texte und Bildpfade, die nur lokal existieren.

  Deshalb entscheidet allein, OB ein Payload ankam — nicht, wie viel darin
  steht. Eine Redaktion kann alle Stationen deaktivieren (`.all()` liefert nur
  aktivierte, und minEntries zählt gegen `status(null)`, greift hier also
  nicht); früher landete die Seite damit stillschweigend auf Demo-Inhalten.
  Jetzt bleibt sie schlicht ohne Reiseabschnitt.
*/
const page = (() => {
    if (props.page) {
        try {
            const parsed = JSON.parse(props.page)
            if (parsed && parsed.stage) return parsed
            console.warn('[BeautyAirwaysRoot] page-JSON ohne stage, nutze Fallback')
        } catch (e) {
            console.warn('[BeautyAirwaysRoot] page-JSON ungültig, nutze Fallback:', e)
        }
    }
    return fallback
})()

/*
  Achslagen und Seitenwechsel stehen NICHT mehr hier, sondern im Twig
  (_/pages/beauty-airways.twig): dort entsteht das Stations-Markup, und dort
  werden --axis-x, --node-y und die side-Klasse gesetzt. Sie kamen schon immer
  aus dem Index und nie aus den Daten; mit dem Umzug des Markups leben sie an
  genau einer Stelle statt an zweien.

  Diese Komponente MISST die Kaesten nur noch -- welche Achse eine Station
  bekommt, interessiert sie nicht.
*/
const { stage, terminus, labels } = page

// ── Tuning ────────────────────────────────────────────────────────────────
/*
  Anteil der Bühnen-Scrollstrecke für den Morph, der Rest ist der Abflug.

  Der Abflug muss nicht über eine Kuppe, sondern nur bis zur Achse der ersten
  Karte — bekäme er die halbe Strecke, kröche das Flugzeug. Die gesparte
  Strecke geht an den Morph, und der ist hier das Sehenswerte.
*/
const MORPH_END = 0.86
// Laenge des senkrechten Griffs am Logo, Anteil der Fallhoehe bis zur Uebergabe.
const GLIDE_LEAD = 0.504
// Grifflaengen an der Uebergabe: auslaufend an der Strecke zum ersten Knoten,
// einlaufend an der Fallhoehe des Anflugs. Getrennt, weil die Abschnitte
// verschieden lang sind und Verschiedenes wollen.
const HANDOFF_LEN = 0.4
const HANDOFF_LEN_IN = 0.4
// Hoehe, in die die Zeichen-Front nach der Uebergabe zurueckwandert.
const FRONT_REST = 0.66
/*
  Die Flugbahn liefert den Winkel in der Konvention „0 = Nase oben". Diese
  Kontur liegt in ihrer Vorlage mit der NASE NACH UNTEN, dort heisst 0 also das
  Gegenteil — daher eine halbe Drehung Versatz.
*/
const ANGLE_OFFSET = -180
// Bei diesem Morph-Fortschritt ist der Glanz auf dem Logo vollstaendig weg.
const FADE_SHEEN_BY = 0.45
/*
  Höhe der Zeichen-Front im Bild, als Anteil der Bühnenhöhe.

  Sie bestimmt zwei Dinge zugleich, und die müssen zusammenpassen: wo die
  gezeichnete Spitze der Bahn steht, und wo die Einflugspur liegt (sie sitzt
  eine Bühnenhöhe darüber, negativ gerechnet). Läuft das auseinander, stünde
  das Flugzeug an der Übergabe an einer anderen Stelle als die Zeichnung.
  Deshalb nur EINE Zahl für beide Stellen.
*/
/*
  Keine feste Konstante, sondern je Aufbau bestimmt — auf schmalen Breiten gibt
  das CSS einen eigenen Wert vor (--ku-front-at, siehe dort), ab l steht dort
  `auto` und der Standard hier gilt. build() setzt ihn ganz zu Beginn,
  buildWaypoints() und render() lesen ihn.
*/
const FRONT_AT_STD = 0.8
let FRONT_AT = FRONT_AT_STD
/*
  Lage des Stützpunkts der Einflugspur, als Anteil der Bühnenhöhe. War früher
  DASSELBE wie FRONT_AT — der Stützpunkt lag genau dort, wo im Moment der
  Übergabe die Zeichen-Front stand, damit beide Antriebe zusammenfielen.

  Seit der Umschaltpunkt aus der Front abgeleitet wird (siehe bogenLen), ist
  diese Kopplung nicht mehr nötig — und sie war teuer: FRONT_AT bestimmt, wie
  TIEF das Flugzeug fliegt, der Stützpunkt dagegen die FORM der Kurve. Beides
  an einer Zahl hieß, dass man die Flughöhe nicht ändern konnte, ohne die
  mühsam gefundene Kurve mitzuverstellen.
*/
const LANE_AT = 0.952
// Seite der Einflugspur; null = die natürliche (zur ersten Karte hin).
const LANE_SIDE_STD = 1
let LANE_SIDE = LANE_SIDE_STD
/*
  Neigung der vorgegebenen Tangente am Stützpunkt, in Grad — oder null für
  „gar keine, der Spline rechnet sie selbst". Aus dem CSS, weil die Antwort an
  der Breite hängt (siehe --ku-handoff-tilt).
*/
const HANDOFF_TILT_STD = 6
let HANDOFF_TILT = HANDOFF_TILT_STD
/*
  Ab wann die Copy ausblendet — in BILDHÖHEN SCROLLWEG SEIT DEM ABFLUG, aus
  dem CSS (--ku-copy-fade), weil der Wert an der Breite hängt.

  Nicht mehr im Bühnenfortschritt: der zählt während des Pins in Pin-Anteilen
  und danach in Bildhöhen, eine Zahl bedeutet also vor und nach dem Pin
  Verschiedenes. Der Abflug ist außerdem der Bezug, auf den es hier ankommt —
  die Copy soll gehen, kurz bevor das Flugzeug ihre Höhe erreicht.
*/
let COPY_FADE = 0.82
const LANE_RATIO = 0.0905
// Über wie viele Bühnenhöhen die Front nach der Übergabe auf FRONT_REST
// zurücksteigt.
const FRONT_SETTLE_SPAN = 1.6

// Parallax: maximaler Weg der Hintergrundebenen in Vielfachen der Bühnenhöhe.
// Die Wolken laufen weiter als die Karte — sie sind näher dran.
/*
  Von 0.55 heraufgesetzt: die Karte stand zu ruhig im Bild, um als Boden
  gelesen zu werden, an dem man vorbeifliegt. Damit rücken die beiden Ebenen
  näher zusammen (Wolken/Karte 1,55 -> 1,29), der Tiefeneindruck lebt aber
  weiter vom Größenunterschied der Bewegung UND vom stärkeren Zoom der Wolken.
*/
const CHART_TRAVEL_VH = 0.66
// Kleiner als zuvor (1.15): die Bankhöhe ist Viewport + Weg — je höher die
// Bank, desto stärker skaliert der Zoom sie hoch. 0.85 ist der Kompromiss
// zwischen spürbarem Parallax und sauber gezeichneten Flecken.
const CLOUD_TRAVEL_VH = 0.85
const CHART_ZOOM = 0.1
const CLOUD_ZOOM = 0.22

/*
  DIE WOLKENDECKE.

  Erst als Foto gebaut (eine Horizontaufnahme), dann verworfen — aus zwei
  Gründen, die beide grundsätzlich sind:

  1. PERSPEKTIVE. Ein Foto vom Boden aus zeigt Wolken von UNTEN. Das Flugzeug
     dieser Seite ist eine Draufsicht; beides zusammen geht nicht auf, und keine
     Unschärfe repariert das wirklich.
  2. KANTEN. Eine Decke, die sich teilt, indem zwei Bildhälften auseinander-
     fahren, hat zwangsläufig eine Kante — ein Foto lässt sich verschieben, aber
     nicht auflösen.

  Deshalb jetzt: ein Feld aus einzelnen Wolkenflecken. Jeder Fleck ist derselbe
  prozedurale Sprite (Turbulenz mit radial auslaufender Maske, also ohne jede
  Kante), nur in anderer Größe, Drehung und Deckkraft. Turbulenz hat keine
  Blickrichtung — sie IST damit die Draufsicht.

  Und das Aufreißen ist ein echtes Auflösen: die Flecken werden größer und
  gleichzeitig schwächer, von der Mitte nach außen gestaffelt. Nichts wandert
  zur Seite, es wird nur dünner, und darunter kommt mehr Karte zum Vorschein.
  Am Rand bleibt am meisten stehen.
*/
// Wie lange ein einzelner Fleck zum Auflösen braucht, im Fortschrittsraum.
const PUFF_SPAN = 0.42
// Staffelung Mitte -> Rand: der äußerste Fleck beginnt so viel später als der
// mittlere. Das ist der Grund, warum sich der Blick von innen nach außen öffnet.
const PUFF_STAGGER = 0.55

/*
  Das Wolkenfeld. x/y in % der Deckfläche, s = Kantenlänge in % der Deckbreite,
  r = Drehung, a = Grunddeckkraft, k = welcher der beiden Sprites.

  Die Flecken sind deutlich größer als ihr Abstand — nur durch die Überlappung
  wird aus einzelnen Flecken eine geschlossene Decke. Die Reihen sind
  gegeneinander versetzt, damit kein Raster entsteht.

  `a` ist die Grunddeckkraft im ungescrollten Zustand; das Auflösen rechnet von
  hier aus nach unten. Sie zu senken macht die Decke also am Anfang dunkler,
  ohne die Kurve des Auflösens anzufassen — und ohne die Karte mitzudämpfen,
  wie es eine stärkere Grundverdunklung täte.

  Alle Werte sind in zwei Schritten auf rund 60 % der ersten Fassung gefallen
  (0,29…0,20 -> 0,23…0,16 -> 0,17…0,12): in der Bühne lag die Decke so dicht
  über dem warmen Grundton, dass das Bild staubig wirkte statt bewölkt. Was auf
  der REISE übrig bleibt, hängt NICHT hieran — das sind REST_MID/REST_EDGE, und
  die sind absolut. An dieser Tabelle zu drehen ändert nur die Bühne.
*/
/*
  Von zwanzig auf fünfzehn: die Decke war in der Bühne zu voll. Gestrichen sind
  die fünf mittigsten (x 52, 42, 32, 48, 40) — je einer pro Reihe, aber auf
  verschiedenen x, damit kein senkrechter Streifen entsteht. Sie waren doppelt
  entbehrlich: in der Bühne standen sie hinter Logo und Text, und auf der Reise
  lösen sich gerade die mittigen als erste auf. Löcher hinterlässt das nicht —
  die Flecken sind rund doppelt so hoch wie der Reihenabstand, jede Stelle wird
  noch von zwei Reihen darüber und darunter überdeckt.
*/
/*
  HELLIGKEITSGEFÄLLE statt gleichmäßigem Feld. Alle Flecken auf demselben
  Niveau lasen sich als Tapete — ein Himmel lebt davon, dass einzelne Ballungen
  leuchten und der Rest dunkel bleibt. Das Gefälle folgt dem Licht: die oberen
  Reihen sind am dunkelsten (0,10), zur Mitte hin wird es hell (0,20), und in
  der Bühne liegt genau dieser helle Bereich auf Höhe des Scheins.

  Wo die Reihen im Bild landen: die Deckfläche ist Viewport + Weg (rund 1665 px
  bei 900 px Höhe), in der Bühne sieht man also ihre oberen 54 % — Reihe 1 bis
  3 ganz, von Reihe 4 die Oberkante. Reihe 5 kommt erst beim Scrollen dazu, sie
  ist deshalb wieder zurückgenommen.

  Die Größen streuen jetzt von 36 bis 62 statt von 42 bis 52. Gleich große
  Flecken waren der zweite Grund für den Tapeteneindruck.
*/
const PUFFS = [
    { x: 8, y: 6, s: 58, r: -8, a: 0.1, k: 0 },
    { x: 30, y: 2, s: 44, r: 14, a: 0.11, k: 1 },
    { x: 74, y: 3, s: 50, r: 26, a: 0.11, k: 1 },
    { x: 94, y: 9, s: 54, r: -12, a: 0.1, k: 0 },
    { x: 16, y: 24, s: 40, r: 32, a: 0.13, k: 1 },
    { x: 66, y: 26, s: 48, r: 8, a: 0.12, k: 1 },
    { x: 88, y: 22, s: 44, r: -34, a: 0.13, k: 0 },
    { x: 6, y: 44, s: 52, r: 18, a: 0.19, k: 1 },
    { x: 58, y: 42, s: 62, r: 24, a: 0.2, k: 1 },
    { x: 82, y: 46, s: 38, r: -22, a: 0.19, k: 0 },
    { x: 18, y: 66, s: 56, r: 10, a: 0.2, k: 1 },
    { x: 76, y: 68, s: 46, r: 20, a: 0.19, k: 1 },
    { x: 10, y: 86, s: 44, r: -16, a: 0.15, k: 0 },
    { x: 70, y: 84, s: 52, r: -10, a: 0.16, k: 0 },
    { x: 92, y: 90, s: 36, r: 16, a: 0.15, k: 1 },
]

/*
  Fortschrittsfenster des Auflösens.

  Der Beginn ist ein Anteil der Bühnen-Scrollstrecke: mitten im Morph wird die
  Karte „ein wenig sichtbar", während sich das Logo wandelt.

  Das ENDE hängt dagegen nicht mehr an der Bühne, sondern an der ersten Karte —
  fertig ist das Auflösen, wenn die in der Mitte des Sichtfelds steht. Vorher
  endete es exakt am Handoff, und das war zu früh: die Decke war schon dünn,
  bevor überhaupt Inhalt zu sehen war. Ein Anteil von q könnte das nicht
  ausdrücken, q ist am Handoff bei 1 — deshalb rechnet das Auflösen in Pixeln.
*/
const OPEN_FROM = 0.16

/*
  Deckel für die Steilheit der Bahn (dx pro dy). Kein LANE_SPREAD wie in
  Variante 1: die seitliche Lage kommt nicht mehr aus einer Konstante, sondern
  aus den Achsen der Stationen — das CSS entscheidet, wo ein Knoten liegt, das
  JS zieht die Kurve hindurch.

  Deshalb steht auch der Deckel im CSS (--max-slope auf .ku-route-wrap): er
  hängt am Umbruch. Ab l pendelt die Achse in einer Gasse innerhalb des
  Inhalts, dort ist er mit 0.82 eine Notbremse gegen Zickzack. Darunter geht
  der Seitenwechsel über die volle Containerbreite und ist Absicht — und weil
  die Karten mit wachsender Breite zugleich KÜRZER werden (weniger
  Zeilenumbrüche), wird der Weg länger und die Höhe dafür kleiner. Bei 834 px
  bräuchte der Wechsel 861 px und bekommt 664; mit 0.82 rutschten die
  Destinationen um bis zu 162 px von ihren Karten weg, mitten in den Text.

  Die Breakpoint-Pixel hier ein zweites Mal zu notieren wäre die Alternative
  gewesen — und wäre still auseinandergelaufen. Diese Konstante greift nur,
  wenn die Property fehlt.
*/
const MAX_SLOPE_FALLBACK = 0.82
// Anteil der Bahnlänge am Ende, über den das Flugzeug ausblendet. Deutlich
// kürzer als in Variante 1 (0.12): dort fliegt die Bahn ins Leere aus, hier
// endet sie auf dem Ring der Landung — das Flugzeug soll sichtbar ankommen.
const FADE_TAIL = 0.05

/*
  VORLAUF DER ROUTE — wie weit die leise Linie dem Flugzeug vorauseilt.

  EINE Front auf EINER Bahn. Startbogen und Flugbahn sind zwei SVG-Pfade in
  zwei Koordinatensystemen, für die Front zählen sie aber als ein einziger Weg:
  sie wird als Bogenlänge auf „Bogen, dann Flugbahn" gerechnet. Läuft sie über
  das Ende des Bogens hinaus, wird der Überhang auf der Flugbahn aufgedeckt —
  ab deren Anfang, und der ist geometrisch derselbe Punkt wie das Bogenende.

  Damit ist beides gleichzeitig erfüllt: die Front läuft dem Flugzeug voraus
  UND sie läuft an der Nahtstelle durch, ohne dort zu warten. Und sie kann
  nicht als zweite Linie erscheinen — die Flugbahn wird per Konstruktion erst
  aufgedeckt, wenn der Bogen vollständig aufgedeckt ist. Genau dieser
  Zusammenhang fehlte den früheren Fassungen: die eine hatte für beide Pfade
  eigene Zeitpläne (zwei Linien, die aufeinander zuwuchsen), die andere deckelte
  den Vorlauf am Bogenende (die Front blieb dort stehen).

  Die Länge wächst über den Startbogen von null auf ihr Maß. Sofort in voller
  Länge stünde beim ersten Scrollen schlagartig der ganze Bogen da.
*/
const LEAD_VH = 0.9

// Wolkendrift: zwei Sinus mit unteilbaren Perioden, unabhängig vom Scroll.
/*
  SEITWÄRTSDRIFT — nur solange die Bühne für sich steht.

  Vorher waren es zwei überlagerte Sinus. Die Idee war, dass zwei nie ganz
  gleichzeitige Schwingungen sich als echtes Ziehen lesen; tatsächlich wechselt
  jede Schwingung irgendwann die Richtung, und sobald der Scroll die Ebene
  zusätzlich nach oben zieht, ist das nicht mehr Wolkenzug, sondern Zappeln.

  Jetzt: eine konstante Geschwindigkeit in eine Richtung, deren Anteil mit dem
  Auflösen auf null geht — aufaddiert, nicht ausgerechnet. Der Unterschied ist
  wichtig: x = v·t·(1−f) würde rückwärts laufen, sobald f wächst. Aufaddiert
  bleibt die Bewegung monoton und steht am Ende einfach still. Ab dann bewegen
  sich die Wolken nur noch durch den Scroll.
*/
// %/s. Deutlich langsamer als zuvor (0.42) — der Zug soll spürbar sein, nicht
// sichtbar. DRIFT_MAX ist eine Notbremse, damit keine Kante des Wolkenfeldes
// ins Bild wandert; bei dieser Geschwindigkeit wird sie erst nach rund
// anderthalb Minuten erreicht. Vorher lag sie bei 6 % und war nach 14 Sekunden
// erreicht — dann standen die Wolken sichtbar still.
const DRIFT_SPEED = 0.15
const DRIFT_MAX = 14
// Die Karte darf leise weiterziehen: sie ist der Boden, sie steht nie still.
const CHART_SWAY = [{ amp: 0.009, period: 83000 }]

// 0..1 mit waagerechter Tangente an beiden Enden (smootherstep). Der Vorhang
// darf weder ruckhaft anfahren noch am Ende anschlagen.
const ease = (t) => t * t * t * (t * (t * 6 - 15) + 10)

/*
  DIE FALLBLATTANZEIGE am Seitenende — Bauart übernommen aus Variante A
  (TafelPage.vue), dort ist sie die Bühne, hier eine kleine Tafel für die vier
  Eckdaten.

  Zwei Dinge sind gegenüber dem Original vereinfacht: es gibt nur EINEN Zustand
  (die Tafel blättert einmal auf, wenn sie in den Blick kommt, statt über die
  Scrollstrecke mehrfach umzuspringen), und der Zeichenvorrat ist um Umlaute,
  Punkt und Bindestrich erweitert, weil die Angaben deutsch sind.

  Geblieben ist das Wesentliche: Blätter laufen nur VORWÄRTS durch die Trommel,
  deshalb fällt ein „Z → A" durch den ganzen Rest — das macht den Rhythmus
  ungleichmäßig und echt. MAX_STEPS deckelt die Umdrehung, damit keine Zelle
  eine halbe Sekunde nach allen anderen ankommt.
*/
const DRUM = ' ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÜ0123456789.-→'
const DRUM_CHARS = [...DRUM]
const N_DRUM = DRUM_CHARS.length
const DRUM_IDX = new Map(DRUM_CHARS.map((c, i) => [c, i]))
/*
  Spaltenzahl = längste Angabe. Alle Zeilen werden darauf zentriert gepolstert,
  damit die Spalten fluchten — das Raster ist der halbe Split-Flap-Effekt.

  Gemessen wird an den bereits VERSALIERTEN Zeilen: toUpperCase() kann Strings
  verlängern ('ß' -> 'SS'), und eine an den Rohdaten gemessene Spaltenzahl
  würde solche Zeilen später stumm abschneiden.
*/
const boardRows = (terminus.rows || []).map((r) => r.toUpperCase())
const COLS = Math.max(1, ...boardRows.map((r) => [...r].length))
const MAX_STEPS = 18
const STEP_MS = 42
const COL_STAGGER = 22
const ROW_STAGGER = 80

/*
  EIGENBEWEGUNG DER FLECKEN. Bisher bewegte sich das Wolkenfeld nur als Ganzes
  (Drift + Parallax), die Flecken standen darin fest. Jeder bekommt jetzt einen
  eigenen langsamen Weg und ein leises Atmen der Deckkraft — unterschiedliche
  Perioden, damit sich das Feld nie wiederholt.

  Der eigentliche Effekt ist nicht die Bewegung selbst, sondern was sie mit den
  ÜBERLAPPUNGEN macht: zwei halbdurchlässige Flecken, die sich gegeneinander
  verschieben, ergeben eine sichtbar andere Form. Genau deshalb reichen ein paar
  Pixel — und genau deshalb wird NICHT skaliert: `scale` würde den SVG-Sprite
  jedes Mal neu rastern (gemessen 39 statt 67 fps), `transform` und `opacity`
  sind reine Kompositionsänderungen.

  Die Werte kommen aus dem Index, sind also über Reloads konstant.
*/
const TAU = Math.PI * 2
const PUFF_MOTION = PUFFS.map((p, i) => ({
    ax: 9 + (i % 5) * 4,
    ay: 5 + (i % 3) * 5,
    px: 34000 + (i % 7) * 4300,
    py: 26000 + (i % 4) * 5100,
    ph: (i * 1.37) % (Math.PI * 2),
    breath: 0.05 + (i % 3) * 0.025,
    pb: 19000 + (i % 5) * 3700,
    /*
      QUELLEN UND ZERFALLEN. Bisher stand die Größe jedes Flecks fest, nur die
      Deckkraft atmete — und ein Wolkenfeld, in dem sich nichts aufbaut und
      nichts vergeht, wirkt wie ein Standbild mit Zittern.

      `swell` ist die Amplitude der Größenänderung (8 bis 17 %), `ps` ihre
      Dauer (47 bis 95 s). Beide sind absichtlich viel länger als das seitliche
      Schwimmen: Wolken bauen sich in Minuten auf, nicht in Sekunden. Die
      eigene Phase `phs` sorgt dafür, dass zu jedem Zeitpunkt einige wachsen
      und andere schrumpfen — nie das ganze Feld gleichzeitig, das wäre ein
      Pulsieren.
    */
    swell: 0.08 + (i % 4) * 0.03,
    ps: 47000 + (i % 6) * 9700,
    phs: (i * 2.399) % (Math.PI * 2),
}))

/*
  NACHLAUF DES FLUGZEUGS. Seine Position ist eine reine Funktion des
  Scrollstands — hört das Scrollen auf, steht es im selben Bild still. Statt
  direkt auf den Zielwert zu springen, läuft es ihm exponentiell nach: beim
  Scrollen bleibt es ein Stück zurück, beim Anhalten gleitet es nach.

  Als Zeitkonstante formuliert und nicht als Faktor pro Frame, damit der
  Nachlauf bei 60 und bei 120 Hz gleich lang dauert.
*/
/*
  ══ VORDERGRUND ═══════════════════════════════════════════════════════════
  Schwaden VOR dem Flugzeug statt nur dahinter.

  Warum das die leere Bühne rettet: bisher bewegt sich zwar alles, aber alles
  GLEICH — Decke und Karte liegen hinter dem Flugzeug und ziehen mit rund einer
  Bildhöhe über die ganze Seite. Ohne etwas, das sich schneller bewegt als der
  Rest, gibt es keinen Maßstab, an dem sich Tempo ablesen ließe; das Flugzeug
  steht praktisch fest im Bild. Eine nahe Ebene liefert genau diesen Maßstab:
  sie ist am schnellsten und schneidet das Flugzeug gelegentlich an.

  Deshalb ist die Reiseweite das Vierfache der Decke. Der Rest der Wirkung
  steckt in der Unschärfe — nah heißt außerhalb der Schärfeebene, und erst das
  macht aus einer hellen Fläche einen Vordergrund statt einer zweiten Decke.
  Die Sprites sind dieselben wie in der Decke, nur um ein Vielfaches
  vergrößert: der 400er-Raster wird dabei von selbst weich, die zusätzliche
  Weichzeichnung kann klein bleiben (teuer wäre sie auf dieser Fläche).
*/
const FORE_TRAVEL_VH = 3.4

/*
  x/y in % der Vordergrundfläche, s = Breite in % der Viewportbreite, r =
  Drehung, a = Grunddeckkraft, k = Sprite.

  Hier stand einmal ein `b` — die zusätzliche Unschärfe je Fetzen, die als
  Ebenen-Filter lief. Sie ist in die Sprites gewandert (siehe
  .ku-fore__wisp und SCHWADEN_BLUR im Sprite-Skript); die vier Werte 9/13/10/15
  px sind dort in Sprite-Einheiten umgerechnet dokumentiert.

  Vier Schwaden, bewusst nicht mehr: sie sind riesig, und jede weitere kostet
  Fläche ohne Zugewinn an Tiefe. Sie stehen abwechselnd links und rechts, damit
  keine über der Bildmitte hängen bleibt, wo Wortmarke und Karten liegen.

  Die Grunddeckkraft liegt bei einem Drittel bis der Hälfte der Deckenflecken.
  Vordergrund darf nicht LESBAR sein — er soll die Aufmerksamkeit nicht auf
  sich ziehen, sondern nur beweisen, dass es zwischen Betrachter und Flugzeug
  noch Luft gibt.
*/
const FORE = [
    { x: 16, y: 10, s: 122, r: -6, a: 0.085, k: 0 },
    { x: 88, y: 33, s: 152, r: 12, a: 0.065, k: 1 },
    { x: 30, y: 61, s: 136, r: -15, a: 0.08, k: 1 },
    { x: 78, y: 87, s: 166, r: 7, a: 0.055, k: 0 },
]

/*
  Eigenbewegung wie bei den Deckenflecken, nur groesser und langsamer: eine nahe
  Wolke füllt mehr Bild, dieselbe Winkelgeschwindigkeit bedeutet also mehr
  Pixel. Ohne sie stünde der Vordergrund still, sobald das Scrollen aufhört —
  und gerade dann fällt er am meisten auf.
*/
const FORE_MOTION = FORE.map((p, i) => ({
    ax: 22 + (i % 3) * 9,
    ay: 13 + (i % 4) * 6,
    px: 61000 + (i % 4) * 8300,
    py: 47000 + (i % 3) * 6700,
    ph: (i * 1.91) % (Math.PI * 2),
    swell: 0.1 + (i % 3) * 0.04,
    ps: 73000 + (i % 4) * 11000,
    phs: (i * 2.71) % (Math.PI * 2),
}))

/*
  ZEITFENSTER. Der Vordergrund ist kein Dauerzustand — er gehört in die Strecke,
  in der sonst nichts im Bild ist.

  Vorher (Wortmarke, Copy) wäre er eine Störung: dort ist das Bild voll und der
  Blick hat eine Aufgabe. Nachher, wenn die Karten kommen, muss er zurück, sonst
  legt sich Nebel über Text, den man lesen soll. Er bleibt aber nicht ganz weg —
  die Reise zwischen den Karten ist genauso leer wie die Bühne.
*/
/*
  Ein Grundanteil liegt von Anfang an über der Bühne — auch über dem Kopf.
  Nur ein Hauch (rund ein Viertel), aber er nimmt der Bühne das Trockene: eine
  Wolke, die vor der Schrift durchzieht, stellt die Copy erst in den Himmel
  hinein statt sie davorzukleben. Der volle Wert bleibt der leeren Strecke
  vorbehalten.
*/
const FORE_BASE = 0.26
const FORE_IN = 0.2
const FORE_IN_SPAN = 0.42
// Rest, sobald die Karten übernehmen. Anteil der vollen Deckkraft.
const FORE_REST = 0.34
// Über wie viele Bühnenhöhen NACH dem Pin er auf diesen Rest zurückgeht.
const FORE_OUT_SPAN = 0.9

/*
  ── SCHRUMPFEN DER BÜHNEN-COPY ────────────────────────────────────────────
  Sie geht nicht nur weg, sie geht WEITER weg. Ausblenden allein liest sich
  als Abschalten; zusammen mit einer leichten Verkleinerung liest es sich als
  Entfernung — dieselbe Bewegung, die die Bühne als Ganzes macht.

  Der Vorlauf ist der Punkt: das Schrumpfen beginnt eine knappe Drittel-
  Bühnenhöhe VOR dem Ausblenden und läuft über beides hinweg. Deckungsgleich
  mit der Deckkraft wäre es ein Zucken kurz vor dem Verschwinden. So ist die
  Copy schon unterwegs, wenn sie zu verblassen beginnt.

  7,5 % klingt nach nichts und ist an einer bildschirmbreiten Zeile deutlich
  sichtbar. Mehr sähe nach Zoom aus.
*/
const COPY_SHRINK = 0.14
/*
  Ausblendstrecke der Copy, in Bildhöhen Scrollweg. Das Ausblenden ist das
  kurze, Ereignis-artige; die Verkleinerung läuft daneben über die ganze Zeit
  vom Abflug bis zum Verschwinden (siehe render()).
*/
const COPY_FADE_SPAN = 0.09
/*
  Ob die Copy gegen den Bühnen-Pin festgehalten wird.

  Ohne das Halten fährt sie ab dem Pin-Ende mit der Bühne nach oben, und zwar
  mitten im Ausblenden: gemessen wandert die Headline dabei von 733 auf 627,
  also 106 px, während die Deckkraft von 0,77 auf 0 fällt. Gegen die 8,5 px
  aus der Verkleinerung ist das eine Größenordnung mehr — deshalb an.
*/
const COPY_HOLD = true

/*
  Zeitkonstante, mit der die Copy beim ZURÜCKSCROLLEN wieder auftaucht.

  Hin und zurück sind hier nicht symmetrisch, und das ist Absicht. Nach unten
  muss sie schnell weg sein, bevor die Karte kommt — dort folgt sie weiter
  unmittelbar dem Scrollstand. Nach oben soll sie erst erscheinen, wenn die
  Karte vollständig fort ist; ohne eigene Zeit gäbe es an dieser Freigabe einen
  Sprung, weil der Scrollstand dort längst einen sichtbaren Wert vorgibt.

  Sie fällt also sofort und steigt nachlaufend.
*/
const COPY_RETURN_TAU = 0.3

/*
  ── AUSKLANG DES MORPHS ───────────────────────────────────────────────────
  Der Morph bremst zum Schluss doppelt aus: seine Hauptkurve ist eine
  Smootherstep, und das Fenster der Faltung wird zusätzlich weich eingepasst.
  Zwei ineinandergeschachtelte Bremskurven ergeben am Ende Stillstand.

  Gemessen an der Summe aller Pfadzahlen im Morph-SVG, je 2,5 % Bühnenweg:

    Fortschritt 0.90 -> 0.93    114
    Fortschritt 0.93 -> 0.96     94
    Fortschritt 0.96 -> 0.99      7      <- ab hier steht das Bild
    Fortschritt 0.99 -> 1.00      2

  Das Flugzeug fliegt aber erst bei Fortschritt 1 los. Dazwischen liegt also
  Scrollweg, in dem sich nichts rührt, und danach springt die Anzeige vom
  Morph auf das Flugzeug — genau der Eindruck, dass der Start hakt.

  Den Ausklang zu STRECKEN hilft nicht: was gesättigt ist, wird durch kein
  Umparametrisieren wieder sichtbar; der Stillstand wandert nur an eine andere
  Stelle (ausprobiert und wieder verworfen). Die Strecke muss übersprungen
  werden. Der Fortschritt läuft deshalb nur bis 0,96 und ist DORT am Ende der
  Bühnenstrecke angekommen — die letzten vier Prozent sieht niemand, sie
  kosten aber auch keinen Scroll mehr. Am Umschaltpunkt bewegt sich die Form
  also noch mit voller Geschwindigkeit, und das Flugzeug übernimmt sie im
  Lauf.
*/
/*
  Gemessen ist das an der ERJ-Kontur; Variante A hat einen anderen Morph und
  bleibt deshalb bei 1 — sie ist der Bestandsstand und darf sich durch diese
  Korrektur nicht verändern.
*/
const MORPH_DONE = 0.96

/*
  Abflugpunkt: der Ort, an dem aus der Wortmarke ein Flugzeug wurde.

  Er steht in der Rangordnung der Marken zwischen den beiden vorhandenen — die
  Wegpunkte sind ein 9-px-Punkt, das Ziel ein Ring von 28 bis 44 px mit Kern.
  Der Abflug bekommt denselben Aufbau wie das Ziel, nur kleiner: er ist ein
  ausgezeichneter Ort, aber nicht das, worauf die Reise zuläuft.
*/
/*
  ── MITFAHRT DER BÜHNE ────────────────────────────────────────────────────
  Anteil des Scrollwegs, den die Bühne ab dem Abflug mitgeht. 1 = wie die
  Seite, 0 = sie klebt bis zum Ende ihrer Strecke.

  STEHT AUF NULL. Ausprobiert und wieder abgeschaltet: mitfahren kann nur das
  Sichtbare der Bühne, und das ist zu diesem Zeitpunkt im Wesentlichen der
  Kopf — der Morph ist längst ausgeblendet, das Bühnenbild ist der Himmel
  dahinter. Ein Kopf, der als einziges Element wegwandert, sieht falsch aus.
  Die Dynamik, die hier eigentlich gesucht war, steckt nicht in der Bühne,
  sondern in der Bahn (siehe unten).

  Die Mechanik bleibt stehen, weil sie einen Regler hat und die Begründung
  trägt.

  Das ist keine Willkür, sondern die einzige Lösung, die ohne Umbau geht.
  Nachgerechnet und gemessen:

    Der Bogen beginnt am Logo (Bühne, fährt mit) und endet auf der Flugbahn
    (Seitenfluss, fährt nicht mit). Zwischen Abflug und Übergabe legt die
    Bühne 648 px zurück. Ein Pfad, der starr auf der Bühne liegt, muss also
    genau 648 px LÄNGER werden, sonst verfehlt sein Ende die Übergabe.

  Beide Auswege wurden gebaut und wieder verworfen:

  1. Bogen strecken. Trifft die Übergabe, verzerrt aber die Form — seine
     Sinkstrecke wächst von 450 auf 1098 px bei gleichem Seitenausschlag.
  2. Flugbahn mit nach oben rücken (negativer Außenabstand). Hält die Form,
     verschiebt aber die Zeichen-Front: der Stützpunkt der Übergabe liegt auf
     `-vh * (1 - FRONT_AT)`, und die Front auf `-routeTop + vh * frontAt`.
     Beide fallen nur zusammen, solange die Route zum Zeitpunkt der Übergabe
     genau an der Fensterunterkante steht. Gemessen sprang das Flugzeug dort
     um 365 px.

  Dazu kommt eine Grenze, an der beide Wege scheitern: der Bogen sinkt 450 px,
  die Bühne steigt in derselben Zeit 648 px. Führe die Flugebene mit, stiege
  das Flugzeug am Bildschirm um 198 px — beim Sturzflug.

  Damit ALLES mitfahren kann, müsste der Klebebereich schon am Abflug enden
  und der Bogen im Routen-Container statt in der Bühne liegen. Dann stehen
  beide Enden im selben Koordinatensystem, die Form bleibt und die Übergabe
  entfällt sogar ganz. Das ist ein Umbau der Startphase, kein Regler.
*/
const STAGE_RUN = 0

const ORIGIN_R = 11
const ORIGIN_CORE_R = 3

/*
  ── TEMPO DES STARTBOGENS ─────────────────────────────────────────────────
  Gemessen wurde, wie weit sich das Flugzeug je Scroll-Pixel bewegt:

    Startbogen        1.67 px/px   (gleichmäßig, mit hartem Einsatz)
    Reise danach      0.11 px/px

  Die Reise ist am Bildschirm zwangsläufig langsam: dort hängt das Flugzeug an
  der Zeichen-Front und damit am Fenster, nicht an der Seite — es wandert nur
  noch, soweit die Kurve seitlich ausschlägt. Der Bogen dagegen durchquert die
  Bühne. Die beiden Zahlen messen also Verschiedenes; anschließen muss die
  Bewegung trotzdem, und 1.67 auf 0.11 ist ein Absturz.

  Das Flugzeug schoss also mit dem Vierfachen der Reisegeschwindigkeit los und
  fiel an der Übergabe schlagartig darauf zurück. Kein toter Teil mehr — ein
  Tempobruch. Zwei Schrauben dagegen:

  1. Mehr Scrollweg für den Bogen (morphEnd der Variante). Das senkt den
     Durchschnitt; auf dieselbe Geschwindigkeit wie die Reise käme man nur mit
     einer mehrfach längeren Bühne, die Größenordnung stimmt aber wieder.
  2. Dieses Profil statt gleichmäßigem Durchlauf: sanft anfahren, in der Mitte
     am schnellsten, in die Übergabe hinein lange ausrollen — und zwar NICHT
     bis auf null. Ein Ausrollen auf null wäre wieder ein Stillstand, diesmal
     am unteren Ende. Es endet bei knapp einem Viertel der
     Durchschnittsgeschwindigkeit und trifft damit die Reise dort, wo sie
     tatsächlich anfängt.

  Die Zahlen sind relativ zur Marschfahrt AUF DEM BOGEN, nicht am Bildschirm.
  Seit die Welt mitzieht, ist die Bildschirmbewegung die Differenz aus beidem:
  bewegt sich das Flugzeug mit 2,7 px je Scroll-Pixel über einen Bogen, dessen
  Welt mit 1,0 nach oben zieht, sieht man 1,7 — und das ist der Antritt, mit
  dem es die Bildmitte verlässt.

  FAST GLEICHMÄSSIG. Die Zahlen liegen bewusst dicht beieinander: 0,8 am
  Anfang, 1,0 in der Mitte, 0,8 am Ende — über die ganze Strecke schwankt das
  Tempo damit nur um ein Fünftel, und weiche Enden hat es trotzdem.

  Frühere Fassungen hatten einen Antritt mit Plateau und tiefem Ausrollen
  (0,92 / 1,0 / 0,12). Das las sich als Beschleunigen und Bremsen, obwohl es
  darum gar nicht geht: Der Sinn dieser Phase ist nicht Geschwindigkeit,
  sondern eine LAGEÄNDERUNG — das Flugzeug soll aus der Bildmitte heraus und
  später wieder ein Stück zurückfallen. Das darf über die ganze Strecke laufen.

  Die frühere Sorge, das Ausrollen müsse die Zeichen-Front von selbst treffen,
  ist hinfällig: seit deren Gewicht über die ganze Phase einwächst (siehe
  unten), gibt es keinen Umschaltpunkt mehr, an dem etwas treffen müsste.
*/
/*
  Länge der scrollgetriebenen Phase, in Bildhöhen SCROLLWEG ab dem Abflug.

  Vorher endete sie exakt am Pin. Das war die Ursache der „Verzögerung", die
  sich nach dem Losfliegen einstellte — und zwar nicht im Morph, der läuft
  gleichmäßig durch, sondern an der Umschaltstelle: davor legte das Flugzeug
  rund 1,1 px Bildschirmweg je Scroll-Pixel zurück, danach hängt es an der
  Zeichen-Front und damit am Fenster, also praktisch null. Die Bewegung starb
  mitten im Anflug.

  Jetzt reicht die Phase bis kurz vor den ersten Wegpunkt.

  IN SCROLL-PIXELN, nicht im Bühnenfortschritt: `qx` zählt während des Pins in
  Pin-Anteilen und danach in Bildhöhen — bei dieser Bühne sind das 1800 gegen
  900 px, dieselbe Zahl bedeutet also vor und nach dem Pin das Doppelte. Eine
  Phase, die über die Grenze reicht, liefe damit in ihrer ersten Hälfte halb
  so schnell (ausprobiert und gemessen: 0,03 px/px statt 1,0 — genau die
  Verzögerung, die weg sollte).
*/
const TAKEOFF_VH = 1

const TO_A = 0.2          // Ende der Anfahrt
const TO_B = 0.8          // Beginn des Ausrollens
const TO_V0 = 0.8         // Geschwindigkeit am Anfang, relativ zur Marschfahrt
const TO_V1 = 0.8         // ... am Ende
const TO_SA = (TO_A * (1 + TO_V0)) / 2
const TO_SB = TO_SA + (TO_B - TO_A)
const TO_S1 = TO_SB + ((1 - TO_B) * (1 + TO_V1)) / 2

// Weg über den Bogen, aus dem trapezförmigen Geschwindigkeitsverlauf
// integriert und auf 0..1 normiert.
function takeoffEase(x) {
    let s
    if (x < TO_A) s = TO_V0 * x + ((1 - TO_V0) * x * x) / (2 * TO_A)
    else if (x < TO_B) s = TO_SA + (x - TO_A)
    else {
        const w = (x - TO_B) / (1 - TO_B)
        s = TO_SB + (1 - TO_B) * (w - ((1 - TO_V1) * w * w) / 2)
    }
    return s / TO_S1
}

/*
  ── PARALLAX DER KARTEN ───────────────────────────────────────────────────
  Jede Karte bewegt sich ein wenig schneller als die Seite, und jede ein
  bisschen anders schnell.

  Der Versatz hängt am Abstand der Karte von der Bildmitte und ist dort GENAU
  NULL. Das ist keine Feinheit, sondern die Bedingung, unter der der Effekt
  hier überhaupt zulässig ist: die Karte gehört zu ihrem Knoten auf der
  Flugbahn, und der ist die Stützstelle der Kurve und darf sich nie bewegen.
  Solange man die Karte liest, steht sie also exakt an ihrem Platz; der
  Versatz entsteht nur an den Bildrändern, wo der Knoten ohnehin niemanden
  interessiert.

  Richtung: SCHNELLER, nicht langsamer. Die Karte kommt beim Einlaufen ohnehin
  von unten herauf (70 px, siehe buildReveals) — schneller verlängert diese
  Bewegung, langsamer würde gegen sie arbeiten.

  Die Amplitude war zuerst 22, dann 64 px je Bildhöhe und blieb beide Male
  unsichtbar: verteilt über die gesamte Durchfahrt einer Karte bleibt pro Frame
  nichts übrig. Wahrnehmbar wird ein Parallax erst über seine STEIGUNG in der
  Bildmitte — dort schaut man hin, und dort entscheidet sich, ob eine Karte
  sichtbar anders läuft als ihre Nachbarin.

  Deshalb eine Sinusform statt einer geraden Linie mit Klammer: in der Mitte
  ist sie am steilsten (Faktor π/2 gegenüber der Geraden), zu den Rändern hin
  läuft sie von selbst flach aus und ist beim Anschlag angekommen. Eine harte
  Klammer hätte dort einen Knick erzeugt, die Karte wäre schlagartig stehen
  geblieben.

  Amplitude und Reichweite sind DASSELBE Bild von zwei Seiten: die Steigung in
  der Mitte ist (π/2)·Amplitude/Reichweite, der größte Versatz ist die
  Amplitude. Beide zusammen zu halbieren lässt die sichtbare Bewegung also
  unverändert und halbiert nur, wie weit eine Karte am Bildrand höchstens von
  ihrem Platz abrückt. Genau das war nötig: der volle Versatz drückt die erste
  Karte nach UNTEN, solange sie unter der Bildmitte steht — gemessen 100 der
  171 px, die sie am Ende des Pins noch unter der Fensterkante lag. Der
  Parallax hat ihr Erscheinen also selbst verzögert.

  Bewegt wird über die eigenständige CSS-Eigenschaft `translate`. Der
  `transform` der Karte gehört GSAP (dem Einlaufen), und zwei Stellen dürfen
  sich nicht um dieselbe Kette streiten — dieselbe Trennung wie bei Drehung
  und Größe der Wolkenflecken.
*/
/*
  Auslösepunkte des Karten-Einlaufs, als Anteil der Fensterhöhe, an dem die
  Oberkante der Station steht. Zwei Werte, weil sie zwei verschiedene Fragen
  beantworten — Begründung bei buildReveals.
*/
const REVEAL_ERSTE = 60
const REVEAL_REST = 78

/*
  Strecke, über die das KI-Kennzeichnungs-Badge (EU AI Act Art. 50) von der
  Bild-OBERkante zur UNTERkante wandert — als Anteil der Fensterhöhe, gerechnet
  ab dem Auslösepunkt des Einlaufs.

  Warum an den Einlauf gekoppelt und nicht am Viewport gehaftet (wie in
  ai-label-sticky.js für die übrigen Seiten): die Kartenbilder sind
  Breitformate und damit auf jeder realistischen Fensterhöhe KÜRZER als der
  Viewport. Was einer Viewport-Haftung an Strecke bliebe, ist
  `Bildhöhe - (1 - REVEAL/100) * Fensterhöhe` — gemessen 0 bis 10 px bei 900 px
  Fensterhöhe, weil das Bild erst aufblendet, wenn es fast vollständig im Bild
  steht. Die Haftung wäre also rechnerisch korrekt und trotzdem unsichtbar.

  Hier ist die Strecke stattdessen gesetzt und damit unabhängig von der
  Bildhöhe immer sichtbar. Gescrubbt, nicht zeitgesteuert: das Badge soll
  mitscrollen, nicht von selbst herunterlaufen.
*/
const BADGE_LAUF = 0.42

const CARD_PARALLAX = 55
// Bildhöhen bis zum Anschlag.
const CARD_PARALLAX_RANGE = 0.45
// Nach Index, nicht nach Zufall: benachbarte Karten sollen sich unterscheiden,
// aber über Reloads gleich bleiben. Läuft bei mehr Karten von vorn um.
const CARD_PARALLAX_MIX = [1, 0.55, 1.35, 0.75, 1.2, 0.62]

const FOLLOW_TAU = 0.19

/*
  NACHLAUF DES HIMMELS. Dasselbe für die Parallax-Ebenen: Wolkendecke und Karte
  hängen sonst starr am Scrollstand und stehen im selben Bild still, in dem das
  Scrollen aufhört — während das Flugzeug daneben ausgleitet.

  Länger als beim Flugzeug. Die Decke ist eine große, weiche Masse und darf
  träger wirken als ein Flugzeug; und weil sie den ganzen Bildschirm füllt,
  fällt der Rückstand während des Scrollens nicht als Verzug auf, das Ausgleiten
  danach aber schon.
*/
const SKY_TAU = 0.3

const reduce = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches


const root = ref(null)
const track = ref(null)
const stageEl = ref(null)
const morphSvg = ref(null)
const linesEl = ref(null)
const originRing = ref(null)
const originCore = ref(null)
const copyEl = ref(null)
const hintEl = ref(null)
const goldGrad = ref(null)
const chartEl = ref(null)
// Die Kartenkörper, einmal in build() eingesammelt: render() greift sie in
// jedem Frame ab, eine DOM-Abfrage je Frame wäre reine Verschwendung.
let kartenEls = []
// Nachlaufende Deckkraft der Bühnen-Copy (siehe COPY_RETURN_TAU). -1 heißt
// „noch kein Startwert"; `copyRuht` hält den frühen Ausstieg offen, solange
// sie ihrem Ziel noch nachläuft.
let copyAlpha = -1
let copyRuht = true
const foreEl = ref(null)
const wispEls = ref([])
const deckEl = ref(null)
const hazeEl = ref(null)
const puffEls = ref([])
const journeyEl = ref(null)
const routePath = ref(null)
const trailPath = ref(null)
/*
  Der Schimmer des geflogenen Teils, als zwei breite schwache Striche UNTER der
  Haarlinie. Früher war das ein `drop-shadow` an .ku-trail — unvereinbar damit,
  dass sich der Dashoffset jeden Frame ändert: jede Änderung hat den kompletten
  Filterbereich neu gerechnet, gemessen 344x3444 CSS-px und auf DPR 2 also ein
  Puffer von 688x6888 = 4,7 Megapixel, 60 mal je Sekunde. Zwei zusätzliche
  Strokes kosten dagegen nichts (siehe .ku-trail-glow).
*/
const trailGlowWide = ref(null)
const trailGlowNarrow = ref(null)
// Trägt den Versatz, um den der SVG-Kasten nach oben verlängert ist.
// Siehe .ku-route-wrap__lines und den Block am Ende von build().
const linesGroup = ref(null)
const journeyPlane = ref(null)
const nodeEls = ref([])
const stationEls = ref([])

/*
  Die Stationen kommen als Slot-Inhalt aus dem Twig, es gibt also keine
  :ref-Callbacks mehr, die die beiden Listen fuellen -- sie werden einmal aus
  dem DOM gelesen.

  MUSS vor build() laufen: buildWaypoints() misst .ku-station, und allNodes()
  liest nodeEls. Die Reihenfolge von querySelectorAll ist die Dokument-
  reihenfolge und damit die Reisereihenfolge -- genau das, was die Spline als
  Stuetzstellenfolge braucht.

  Gesucht wird unterhalb von root, nicht im document: die Komponente soll
  nichts messen, was ausserhalb von ihr liegt.
*/
function sammleStationen() {
    const host = root.value
    if (!host) return
    stationEls.value = Array.from(host.querySelectorAll('.ku-station'))
    nodeEls.value = stationEls.value.map((el) => el.querySelector('.ku-node'))
}
const terminusNode = ref(null)
const boardEl = ref(null)
const boardReady = ref(false)

let morph = null
let raf = 0
let ro = null
// Alles, was build() vermisst und render() nur noch liest.
let geo = null
let lastQ = -1
let lastFront = Number.NaN
// Nachlaufende Position des Flugzeugs auf der Flugbahn und der Zeitstempel des
// letzten Bildes. -1 heißt „noch nicht gesetzt".
let lFollow = -1
let lastRenderNow = 0
// y jeder Destination in Routen-Koordinaten, NACH der Deckelung.
let nodeYs = []
let reachedCount = -1
let triggers = []

// Alle Messpunkte in Flugrichtung: die Karten, dann der Endpunkt.
function allNodes() {
    return [...nodeEls.value.filter(Boolean), terminusNode.value].filter(Boolean)
}

/*
  Alle Striche, die den GEFLOGENEN Teil zeichnen: dieselbe Kurve, derselbe
  Dashoffset, nur verschiedene Breiten und Deckkräfte. Sie stehen hier
  zusammen, damit `d`, Dasharray und Dashoffset nur an EINER Stelle gesetzt
  werden — der Schimmer darf der Haarlinie nicht um einen Frame nachlaufen,
  sonst sieht man an der Zeichenfront zwei Enden.
*/
function gezeichnet() {
    return [trailGlowWide.value, trailGlowNarrow.value, trailPath.value].filter(Boolean)
}

/*
  Stützstellen der Bahn aus den Knoten messen.

  Die Knoten sind 0×0 groß und werden nie animiert (alles Sichtbare hängt als
  eigenes Kind daran) — ihre linke obere Ecke IST der Punkt. Gemessen wird
  gegen den Routen-Container, in dessen Koordinaten auch der Pfad lebt.
*/
function buildWaypoints(vh, anchorX) {
    const nodes = allNodes()
    if (nodes.length < 2) return []

    // Korrektur des letzten Durchlaufs zurücknehmen, sonst misst dieser
    // Durchlauf die eigene Verschiebung mit und der Fehler summiert sich auf.
    nodes.forEach((el) => { el.style.translate = '' })

    const rr = journeyEl.value.getBoundingClientRect()
    const pts = nodes.map((el) => {
        const r = el.getBoundingClientRect()
        return { x: r.left - rr.left, y: r.top - rr.top }
    })

    /*
      Die EINFLUGSPUR liegt eine halbe Bühnenhöhe ÜBER dem Container
      (negatives y): genau dort steht die Zeichen-Front im Moment des Handoffs.
      Dadurch fällt der Anschluss an den Anflug von selbst zusammen — es
      braucht keine Kalibrierung zwischen Scrollweg und Bahnlänge.

      Ihre x-Lage bestimmt die BREITE des Anflugbogens. Sie liegt von der
      Logomitte (`anchorX`, in Container-Koordinaten) aus in Richtung des ersten
      Knotens, nur weiter außen.

      Die Seite ist über LANE_SIDE frei wählbar. Legt man die Spur auf die
      GEGENÜBERLIEGENDE Seite des ersten Knotens, schwenkt der Anflug erst zur
      anderen Seite aus und die Bahn holt ihn über ihre ganze Länge zurück —
      aus zwei kurzen Stücken wird ein langer Zug. Die Untergrenze „natürlicher
      Abstand" gilt dann nicht mehr: sie stammt aus dem Fall, dass die Spur
      AUSSERHALB des Knotens liegt.
    */
    const natural = Math.abs(pts[0].x - anchorX)
    const natSeite = Math.sign(pts[0].x - anchorX) || 1
    const outward = LANE_SIDE ?? natSeite
    const room = outward > 0 ? rr.width - anchorX : anchorX
    /*
      Untergrenze: der natürliche Abstand zur Achse der ersten Karte — aber nur,
      solange die Spur auf DEREN Seite liegt. Auf der Gegenseite wäre er sinnlos
      und würde die Spur nach außen treiben.

      Fehler, den das behebt: für eine vorgegebene Seite stand hier eine
      feste Untergrenze von 48 px. Auf schmalen Breiten wandert die Kartenachse
      an den Rand (gemessen 174 px von der Logomitte), der Anteil der
      Containerbreite ergibt aber nur 39 — die Spur wurde auf 48 geklemmt und
      landete damit MITTEN auf der Karte statt in der Randspur. Die Bahn lief
      dadurch hinter den Karten durch. Mit dem natürlichen Abstand als
      Untergrenze sitzt sie auf jeder Breite dort, wo Platz ist.
    */
    const untergrenze = outward === natSeite ? natural : 48
    const width = clamp(rr.width * LANE_RATIO, untergrenze, Math.max(untergrenze, room))
    /*
      VERWORFEN: ein zusätzlicher Stützpunkt unter der Einflugspur.

      Die Idee war, den Umkehrpunkt der Kehre aus dem kurzen Anflug in die lange
      Bahn zu verlegen, damit sich die Drehung auf mehr Strecke verteilt.
      Gemessen (Grad Drehung je 100 px Weg) tut sie das auch — aber sie
      verlagert die Schärfe nur, statt sie abzubauen:

                          Anflug   Bahn
        mit Stützpunkt     19.8    70.5
        ohne               36.8    26.9      (Variante A zum Vergleich: 45 / 27)

      Der zusätzliche Punkt ist ein Knoten, durch den der Spline HINDURCH muss —
      und an einem Knoten mit Richtungswechsel ballt sich die Krümmung. Der
      schlimmste Wert der ganzen Seite steigt dadurch von 37 auf 70. Deshalb
      bleibt es bei der einfachen Kette; der Rest steckt in der Form des
      Anflugs und in der Lage der Spur.
    */
    const maxSlope = Number.parseFloat(
        getComputedStyle(journeyEl.value).getPropertyValue('--max-slope'),
    ) || MAX_SLOPE_FALLBACK
    const chain = limitSlope(
        monotonizeY(
            [{ x: anchorX + outward * width, y: -vh * (1 - LANE_AT) }, ...pts],
            Math.max(80, vh * 0.12),
        ),
        maxSlope,
    )

    // Die Deckelung kann Punkte verschoben haben. Damit die Bahn trotzdem
    // exakt durch jede Destination läuft, wandert der Knoten mit: `translate`
    // als eigenständige Property, damit GSAP `transform` für die Reveals der
    // Kinder behält.
    nodeYs = chain.slice(1).map((p, i) => {
        const dx = p.x - pts[i].x
        const dy = p.y - pts[i].y
        nodes[i].style.translate = Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5
            ? `${dx.toFixed(1)}px ${dy.toFixed(1)}px`
            : ''
        return p.y
    })

    return chain
}

// Zwischenspeicher der Abtast-Tabelle, siehe build().
let lastRouteD = ''
let lastTable = null


// Abflugmarke am Anfang der Bahn — blendet mit dem Abflug auf.
function setOrigin(a) {
    const w = a.toFixed(3)
    if (originRing.value) originRing.value.style.opacity = w
    if (originCore.value) originCore.value.style.opacity = w
}


function build() {
    if (!track.value || !journeyEl.value || !morphSvg.value) return
    geo = null
    spFollow = -1

    /*
      Höhe der Übergabe. Ganz zuerst, weil buildWaypoints() sie für
      die Lage der Einflugspur braucht und render() für die Zeichen-Front —
      beide müssen dieselbe Zahl sehen.

      Das CSS gibt sie nur auf schmalen Breiten vor; ab l steht dort `auto`,
      dann entscheidet die Variante (und damit der Regler in der Leiste).
    */
    {
        const ausCss = Number.parseFloat(
            getComputedStyle(stageEl.value).getPropertyValue('--ku-copy-fade'),
        )
        if (Number.isFinite(ausCss)) COPY_FADE = ausCss
    }
    {
        const stil = getComputedStyle(journeyEl.value)
        const hoehe = Number.parseFloat(stil.getPropertyValue('--ku-front-at'))
        FRONT_AT = Number.isFinite(hoehe) ? hoehe : FRONT_AT_STD
        const seite = Number.parseFloat(stil.getPropertyValue('--ku-lane-side'))
        LANE_SIDE = Number.isFinite(seite) ? Math.sign(seite) : LANE_SIDE_STD
        /*
          Neigung am Stützpunkt. `none` heißt: keine vorgegebene Tangente, der
          Spline rechnet sie aus den Nachbarn. Siehe --ku-handoff-tilt.
        */
        const roh = stil.getPropertyValue('--ku-handoff-tilt').trim()
        const grad = Number.parseFloat(roh)
        HANDOFF_TILT = roh === 'none' ? null
            : (Number.isFinite(grad) ? grad : HANDOFF_TILT_STD)
    }

    /*
      ── ANHEBEN DER ROUTE ─────────────────────────────────────────────────
      Der Routen-Container rückt um GENAU den Scrollweg des Startbogens nach
      oben. Das ist kein gewählter Wert, sondern die Gegenbuchung:

      Seit der Pfad mitscrollt, muss der Bogen zwei Dinge zugleich überbrücken
      — die Sinkstrecke am Bildschirm UND den Weg, den die Welt darunter
      zurücklegt. Die erste Etappe wuchs dadurch um exakt diesen Scrollweg
      (gemessen 822 -> 1074 px, während die folgenden bei 840, 763 und 671
      blieben). Der Versatz nimmt ihn wieder heraus: die Etappe ist danach so
      lang wie vor dem Umbau, und der Bogen behält trotzdem seine Mitfahrt.

      Zwei Dinge kommen gratis dazu: die Karten rücken um dieselbe Strecke
      nach oben, kommen also früher ins Bild — und weil der Wert am Scrollweg
      hängt, korrigiert er sich mit, wenn morphEnd sich ändert.

      ZUERST, weil es als Außenabstand die Lage der Route verschiebt: jede
      Messung danach muss ihn schon enthalten. Aus offsetHeight statt aus
      einem Rect — die Höhen hängen nicht am Scrollstand.
    */
    const bogenScroll = (1 - MORPH_END)
        * (track.value.offsetHeight - stageEl.value.offsetHeight)
    root.value.style.setProperty('--ku-lift', `${bogenScroll.toFixed(1)}px`)

    // Bühnenhöhe aus dem DOM, NICHT window.innerHeight: die Bühne ist 100svh
    // hoch, innerHeight ist auf Mobile die dynamische Höhe. Beide Systeme mit
    // derselben Zahl rechnen zu lassen ist wichtiger, als welche es ist.
    const vh = stageEl.value.offsetHeight
    const trackRect = track.value.getBoundingClientRect()
    const stageRect = stageEl.value.getBoundingClientRect()
    const svgRect = morphSvg.value.getBoundingClientRect()
    // Oberkante des Scroll-Hinweises, gemessen von der Bühnenunterkante: die
    // Untergrenze des Kopf-Bandes (siehe --ku-hint-top). Hier mitlesen, nicht
    // später — nach den Style-Writes unten wäre es ein erzwungener Reflow.
    const hintTop = hintEl.value
        ? stageRect.bottom - hintEl.value.getBoundingClientRect().top
        : null
    /*
      Höhe des Headers, Untergrenze für den Scheitel des Startbogens. Der
      GEMESSENE Wert und der Token, davon der größere:

      - `--header-height` steht bis zum Mount von TheMegaHeader auf seinem
        Default von 108 px. Auf 844 px Breite ist der Header aber 121 hoch —
        allein mit dem Token blieb dem Scheitel dort 3 px Luft.
      - Der gemessene Wert wiederum stimmt nur für den Moment: der Header
        schrumpft beim Scrollen auf 84. Zur Bauzeit der Geometrie stehen wir
        meist oben, also am größeren Wert — aber eben nicht immer.

      Das Maximum ist in beiden Fällen die vorsichtige Wahl, und vorsichtig ist
      hier die richtige Richtung.
    */
    const headerH = Math.max(
        Number.parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        ) || 0,
        document.querySelector('header')?.getBoundingClientRect().height ?? 0,
        84,
    )
    if (!journeyEl.value.clientWidth || !svgRect.width) return

    /*
      Reihenfolge: erst der Handoff-Punkt A, dann die Flugbahn. Die Bahn
      braucht ihn, weil die Lage ihrer Einflugspur davon abhängt, wo das
      Flugzeug den Morph verlässt — von dort aus wird die Breite des
      Startbogens abgetragen. Der Rect des Containers ist dafür schon hier
      nötig; die Knotenkorrektur in buildWaypoints ändert ihn nicht.
    */
    const routeRect = journeyEl.value.getBoundingClientRect()
    /*
      ── Handoff-Punkt A (Bühnen-Koordinaten) ──────────────────────────────
      Wo steht das Flugzeug am Morph-Ende? Direkt messen können wir das nicht,
      beim Vermessen sind wir eventuell noch nicht im Pin. Also aus der viewBox
      rechnen — die Lage des SVG in der Bühne ist scrollunabhängig.

      Die Abbildung wird vollständig aus VIEW_BOX und PLANE_BOX hergeleitet
      (preserveAspectRatio xMidYMid meet: Skalierung ist der kleinere der
      beiden Faktoren, der Rest wird zentriert). Die Vorgängerfassung hatte
      die drei Zahlen der alten Logo-Geometrie eingetragen — beim Wechsel auf
      die Animation v2 wäre das still auseinandergelaufen statt zu brechen.
    */
    const [, , VB_W, VB_H] = VIEW_BOX.split(/\s+/).map(Number)
    const scale = Math.min(svgRect.width / VB_W, svgRect.height / VB_H)
    const A = {
        x: svgRect.left - stageRect.left + (svgRect.width - VB_W * scale) / 2 + PLANE_BOX.cx * scale,
        y: svgRect.top - stageRect.top + (svgRect.height - VB_H * scale) / 2 + PLANE_BOX.cy * scale,
    }
    const handoffSize = PLANE_BOX.width * scale

    const scrollY = window.scrollY
    // scrollY, bei dem der Pin endet (q === 1). Dann liegt die Bühnenoberkante
    // bei Viewport 0, Bühnen- und Viewport-Koordinaten sind deckungsgleich.
    const handoffScroll = trackRect.bottom + scrollY - vh
    const routeTopAtHandoff = routeRect.top + scrollY - handoffScroll
    /*
      ── DER BEZUGSPUNKT IST DER ABFLUG, NICHT DIE ÜBERGABE ────────────────

      Bisher wurde der Startbogen so umgerechnet, dass er im Moment der
      ÜBERGABE auf der Flugbahn liegt — passend dazu, dass die Bühne bis dahin
      klebt und der Bogen mit ihr stillsteht.

      Jetzt fährt die Flugebene ab dem Abflug mit dem Scroll mit (siehe
      render()). Damit steht sie ab diesem Moment im selben Bezugssystem wie
      die Flugbahn, und beide bewegen sich gemeinsam. Also muss der Bogen dort
      passen, wo er ANFÄNGT zu gelten: beim Abflug. Danach stimmt es von
      selbst weiter, weil sich der Abstand nicht mehr ändert.

      Die Strecke dazwischen ist der Scrollweg des Startbogens. Der Bogen wird
      dadurch höher — er überbrückt jetzt beides: die Sinkstrecke am Bildschirm
      UND den Weg, den die Welt darunter zurücklegt. Genau daraus entsteht die
      Dynamik: das Flugzeug bewegt sich relativ zur mitziehenden Welt.
    */
    const routeTopAtDepart = routeTopAtHandoff + bogenScroll

    // ── Flugbahn (Routen-Koordinaten) ─────────────────────────────────────
    const waypoints = buildWaypoints(vh, A.x + stageRect.left - routeRect.left)
    if (waypoints.length < 2) return

    /*
      DAS LOGO LIEGT AUF DER FLUGBAHN.

      Vorher waren Anflug und Bahn zwei getrennt konstruierte Kurven, die im
      Übergabepunkt aneinanderstießen. Ihre Tangenten ließen sich exakt
      angleichen, ihre KRÜMMUNG nicht: gemessen wechselte sie dort das
      Vorzeichen (+8 auf -2,5), und ein Vorzeichenwechsel der Krümmung ist ein
      Wendepunkt — als Schlenker sichtbar, obwohl die Richtung stimmte. Ein
      einzelnes Kurvenstück kann das nicht auflösen, weil sein einziger
      Freiheitsgrad schon für den senkrechten Abflug am Logo draufgeht.

      Jetzt ist das Logo der erste Stützpunkt DERSELBEN Kurve. Der
      Übergabepunkt bleibt, wo er war — er muss, sonst liefe die Zeichen-Front
      aus dem Tritt —, ist aber kein Nahtpunkt mehr, sondern einer von vielen
      Knoten. Was danach als Anflug gezeichnet wird, ist einfach ihr erstes
      Segment.

      Das Logo geht dabei NICHT durch monotonizeY/limitSlope: die beiden
      dürfen es nicht verschieben, sonst passt es nicht mehr zum Morph-Ende.
    */
    const logoInBahn = {
        x: A.x + stageRect.left - routeRect.left,
        y: A.y - routeTopAtDepart,
    }
    const glideDrop = waypoints[0].y - logoInBahn.y
    const head = { len: Math.max(48, glideDrop * GLIDE_LEAD), dir: { x: 0, y: 1 } }
    /*
      Eigene Tangente am Übergabepunkt (siehe splineThrough). `handoffTilt` ist
      der Winkel gegen die Senkrechte in Grad; NEGATIV heißt nach innen, also
      zurück in Richtung der ersten Karte. Damit liegt der Scheitel des Bogens
      vor dem Übergabepunkt, und das Stück darunter baucht sich zur Gegenseite
      statt gerade durchzulaufen. `handoffLen` ist die Grifflänge als Anteil der
      Strecke von der Übergabe zum ersten Knoten.
    */
    const mid = HANDOFF_TILT != null
        ? (() => {
            const lenOut = Math.max(
                48,
                (waypoints[1].y - waypoints[0].y) * HANDOFF_LEN,
            )
            /*
              Die Neigung ist nach oben durch die Geometrie gedeckelt: mehr als
              der Winkel, unter dem die Spur überhaupt neben der Achse der
              ersten Karte liegt, ergibt keinen Bogen mehr — nur noch eine
              Ausbuchtung, die sofort zurückgeholt werden muss.

              Auf schmalen Breiten wandert diese Achse an den Rand und die Spur
              fällt mit ihr ZUSAMMEN (gemessen 0 px Versatz, auf Desktop 21).
              Ohne Deckel schob die feste Neigung von 6 Grad die Kurve dort
              trotzdem hinaus, und der Knoten senkrecht darunter zog sie zurück:
              ein Schlenker mit Vorzeichenwechsel der Krümmung. Der Deckel macht
              daraus von selbst eine gerade Einfahrt.
            */
            const versatz = Math.abs(waypoints[0].x - waypoints[1].x)
            const moeglich = (Math.atan2(versatz, lenOut) * 180) / Math.PI
            const grad = Math.sign(HANDOFF_TILT)
                * Math.min(Math.abs(HANDOFF_TILT), moeglich)
            const r = (grad * Math.PI) / 180
            const seite = LANE_SIDE ?? 1
            return {
                dir: { x: Math.sin(r) * seite, y: Math.cos(r) },
                // Einlaufender Griff an der Fallhöhe des Anflugs, auslaufender
                // an der Strecke zum ersten Knoten — jeder an SEINEM Abschnitt.
                lenIn: Math.max(48, glideDrop * HANDOFF_LEN_IN),
                lenOut,
            }
        })()
        : null
    /*
      EIN PFAD. Bogen und Reise sind EINE Kurve im Routen-Container —
      dieselbe, die splineThrough liefert.

      Vorher wurde sie hinter dem ersten Segment aufgetrennt: der Bogen ging in
      Bühnenkoordinaten, der Rest blieb im Container. Das war nötig, solange
      die Bühne klebte und der Bogen mit ihr stillstand — zwei Bezugssysteme,
      also zwei Zeichnungen. Seit die Flugebene zur Welt gehört, ist das
      hinfällig, und mit ihm der ganze Apparat: das Auftrennen, das Verschieben
      der Koordinaten und die zweite Kopie der Flugbahn für die Zeichen-Front.

      Der Container scrollt im Seitenfluss ohnehin mit. Es braucht deshalb auch
      keine Gegen- oder Mitbewegung mehr — der Pfad liegt einfach da, wo er
      hingehört.
    */
    const d = splineThrough([logoInBahn, ...waypoints], undefined, head, mid)
    routePath.value.setAttribute('d', d)
    for (const el of gezeichnet()) el.setAttribute('d', d)

    /*
      ── DEN SVG-KASTEN UM DIE KURVE LEGEN ─────────────────────────────────

      Die Einflugspur liegt über dem Container, die Kurve hat also negative
      y-Werte. Bisher ragte sie damit aus dem SVG-Kasten heraus und wurde nur
      von `overflow: visible` gehalten (Messwerte und Begründung an
      .ku-route-wrap__lines).

      GEMESSEN, nicht gerechnet: der Überhang kommt aus der Bounding-Box des
      fertigen Pfades. Eine Formel aus vh und LANE_AT wäre eine zweite Stelle,
      die bei jeder Änderung an der Spur mitgepflegt werden müsste; die Box
      stimmt immer. `getBBox()` liefert dabei die eigenen Koordinaten des
      Pfades, unabhängig vom Transform der Gruppe darum.

      Der Zuschlag ist die Flugzeuggröße: am Anfang der Kurve steht das
      Flugzeug MITTIG auf ihr, ragt also um seine halbe Höhe darüber hinaus.
      Ganze Pixel, damit der Versatz nichts unscharf macht.
    */
    const bahnBox = trailPath.value.getBBox()
    const over = Math.max(0, Math.ceil(-bahnBox.y) + Math.ceil(handoffSize))
    root.value.style.setProperty('--ku-lines-over', `${over}px`)
    linesGroup.value?.setAttribute('transform', `translate(0 ${over})`)
    /*
      Die Tabelle Bogenlänge <-> y wird nur neu abgetastet, wenn sich die
      Flugbahn wirklich geändert hat.

      Sie ist mit Abstand der teuerste Teil des Neuaufbaus; alles andere
      zusammen liegt unter 0.5 ms (siehe sampleTable).

      Bei einer Fensteränderung greift der Vergleich nicht — der erste
      Stützpunkt der Bahn liegt eine halbe Bühnenhöhe über dem Container, die
      Bahn hängt also auch an der HÖHE. Er greift bei den Neuaufbauten nach
      Schriften und `load`: dort wird oft gar nichts verschoben, und dann ist
      die alte Tabelle noch gültig.
    */
    if (d !== lastRouteD) {
        lastRouteD = d
        lastTable = sampleTable(trailPath.value)
    }
    const table = lastTable
    for (const el of [...gezeichnet(), routePath.value]) {
        el.style.strokeDasharray = String(table.total)
        el.style.strokeDashoffset = String(table.total)
    }

    /*
      Bogenlänge bis zum Umschaltpunkt — die Marke auf der Gesamtkurve, an der
      der Antrieb wechselt (Scrollanteil davor, Zeichen-Front danach).

      Sie wird aus der FRONT abgeleitet, nicht aus dem Stützpunkt der
      Einflugspur. Beide fielen früher zusammen, weil der Stützpunkt genau
      dafür konstruiert war (`-vh * (1 - FRONT_AT)`). Seit die Route angehoben
      ist, stimmt das nicht mehr — und es muss auch nicht: gebraucht wird
      allein, dass beide Antriebe an der Umschaltstelle dieselbe Bogenlänge
      liefern. Also fragt man die Front, wo sie zu diesem Zeitpunkt steht.

      Dadurch bleibt der Stützpunkt frei und die Kurve unverändert.
    */
    /*
      Der Umschaltpunkt liegt am ENDE der Antriebsphase (TAKEOFF_END), nicht
      mehr am Pin. Dort steht die Route um die seither gescrollten Bühnenhöhen
      höher — die Front also entsprechend weiter auf der Kurve.
    */
    const pinScroll = trackRect.height - vh
    // Scrollweg vom Abflug bis zum Umschalten, und wie weit die Route dann
    // gegenüber ihrer Lage am Pin-Ende gewandert ist.
    const abflugBisUebergabe = TAKEOFF_VH * vh
    const routeTopDann = routeTopAtHandoff
        + (1 - MORPH_END) * pinScroll - abflugBisUebergabe
    let frontAtUebergabe = FRONT_AT
    {
        const past = clamp((vh - routeTopDann) / (vh * FRONT_SETTLE_SPAN), 0, 1)
        frontAtUebergabe += (FRONT_REST - FRONT_AT) * (past * past * (3 - 2 * past))
    }
    const takeoffLen = lengthAtY(lastTable, -routeTopDann + vh * frontAtUebergabe)

    // Der Abflugpunkt sitzt am Anfang der Kurve — dem Ort des Logos im Moment
    // des Abflugs, siehe logoInBahn.
    if (originRing.value && originCore.value) {
        const p0 = trailPath.value.getPointAtLength(0)
        for (const [el, r] of [[originRing.value, ORIGIN_R], [originCore.value, ORIGIN_CORE_R]]) {
            el.setAttribute('cx', p0.x.toFixed(1))
            el.setAttribute('cy', p0.y.toFixed(1))
            el.setAttribute('r', String(r))
        }
    }

    // ── Parallax-Ebenen ───────────────────────────────────────────────────
    // Höhe = Viewport + zurückzulegender Weg. Nur so groß wie nötig, damit die
    // Turbulenz-Rasterung klein bleibt.
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - vh)
    /*
      Weg der Kartenebene. Aus dem CSS, weil er an der Breite hängt: die Karte
      wird mit `cover` eingepasst, und je höher ihr Kasten (Viewport + Weg),
      desto stärker skaliert `cover` über die Höhe und desto weniger BREITE
      bleibt sichtbar. Auf schmalen Fenstern waren das nur 31 % der Karte —
      man sah fast nichts von ihr.

      Flacher heißt also mehr Karte. Der Preis ist weniger Parallax auf genau
      dieser Ebene; die Wolkendecke behält ihren Weg, sie trägt die Bewegung.
    */
    const wegCss = Number.parseFloat(
        getComputedStyle(root.value).getPropertyValue('--ku-chart-travel'),
    )
    const chartTravel = vh * (Number.isFinite(wegCss) ? wegCss : CHART_TRAVEL_VH)
    const cloudTravel = vh * CLOUD_TRAVEL_VH
    if (chartEl.value) chartEl.value.style.height = `${Math.round(vh + chartTravel)}px`
    if (deckEl.value) deckEl.value.style.height = `${Math.round(vh + cloudTravel)}px`
    kartenEls = stationEls.value.map((el) => el?.querySelector('[data-card]') ?? null)
    /*
      Weg des Vordergrunds, mobil gekürzt — wie beim Kartenweg über eine
      CSS-Variable, damit der Wert bei der Breakpoint-Weiche steht und nicht
      hier. Die Höhe der Ebene IST dieser Weg (plus ein Bildschirm): auf einem
      Telefon-Viewport waren das 393 x 3749, die größte promovierte Ebene der
      Seite nach dem Dokument selbst.
    */
    const foreCss = Number.parseFloat(
        getComputedStyle(root.value).getPropertyValue('--ku-fore-travel'),
    )
    const foreTravel = vh * (Number.isFinite(foreCss) ? foreCss : FORE_TRAVEL_VH)
    if (foreEl.value) foreEl.value.style.height = `${Math.round(vh + foreTravel)}px`

    /*
      Scrollweg des Auflösens, in Pixeln ab Oberkante der Bühnenstrecke, damit
      er über deren Ende hinausreichen kann.
    */
    const trackTopPage = trackRect.top + scrollY
    const openStart = (trackRect.height - vh) * OPEN_FROM
    let openEnd = trackRect.height - vh
    const firstCard = stationEls.value[0]?.querySelector('.ku-card')
    if (firstCard) {
        const cr = firstCard.getBoundingClientRect()
        // Mitte der ersten Karte auf Höhe der Sichtfeldmitte, plus eine gute
        // halbe Bildhöhe Zugabe: die Randflecken sollen noch aufgehen, während
        // man die Karte schon liest.
        openEnd = cr.top + cr.height / 2 + scrollY - trackTopPage - vh / 2 + vh * 0.6
    }

    geo = {
        vh,
        table,
        openStart,
        openEnd: Math.max(openStart + vh * 0.5, openEnd),
        takeoffLen,
        handoffSize,
        maxScroll,
        chartTravel,
        cloudTravel,
        foreTravel,
        open: 0,
    }
    lastQ = -1
    lastFront = Number.NaN
    reachedCount = -1

    // Ganz zum Schluss: die Property verschiebt den Kopf und macht damit das
    // Layout schmutzig. Vor den Messungen oben gesetzt, würde jede davon einen
    // Reflow erzwingen.
    if (hintTop !== null) {
        stageEl.value.style.setProperty('--ku-hint-top', `${hintTop.toFixed(1)}px`)
    }
}

/*
  Nullrichtung der Kontur.

  Die ERJ-Kontur liegt mit der NASE NACH UNTEN; die Flugbahn rechnet dagegen in
  „0 = Nase oben" (flight-path.js: Tangente + 90). Eine halbe Drehung Versatz
  bringt beides in Deckung.

  Am Abflug entsteht dadurch KEIN Sprung: die erste Tangente der Bahn zeigt
  senkrecht nach unten (siehe `head` in build()), mit dem Versatz ergibt das
  genau 0 Grad — die Lage, in der der Morph endet. Eine Drehkorrektur über den
  Anflug, wie sie eine steigende Bahn gebraucht hätte, ist damit hinfällig.
*/
function place(el, pt, size) {
    el.setAttribute(
        'transform',
        planeTransform({ x: pt.x, y: pt.y, size, angle: pt.angle + ANGLE_OFFSET }),
    )
}

/*
  Den Schein über den Morph wegblenden.

  Nicht durch Umschalten des fill: von url(#ku-gold) auf einen Vollton zu
  wechseln wäre ein harter Schnitt mitten in der Animation. Stattdessen laufen
  die Farbstopps des Verlaufs selbst auf den Vollton zu; bei FADE_SHEEN_BY
  tragen alle dieselbe Farbe, der Verlauf ist dann rechnerisch noch da, aber
  nicht mehr zu sehen. Der wandernde Glanzpeak wird damit automatisch
  wirkungslos, ohne dass man ihn abstellen müsste.

  Geschrieben wird nur bei echter Änderung — sieben Attribute pro Frame über die
  halbe Bühnenstrecke wären sonst umsonst.
*/
const goldStops = []
let sheenBase = null
let sheenLast = -1

function readRgb(hex) {
    const h = hex.trim().replace('#', '')
    const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
    return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)]
}

function initSheen() {
    if (!goldGrad.value) return
    goldStops.push(...goldGrad.value.querySelectorAll('stop'))
    sheenBase = goldStops.map((s) => readRgb(s.getAttribute('stop-color')))
}

/*
  Glanz weg: der Bronze-Verlauf mit wanderndem Peak läuft auf einen Vollton zu
  (flatColor), fertig bei fadeSheenBy. Nicht umgeschaltet, sondern die Stopps
  selbst laufen dorthin — ein Wechsel des fill wäre ein harter Schnitt, und
  danach ist der wandernde Peak wirkungslos, weil alle Stopps dieselbe Farbe
  tragen.

  VERWORFEN: von hier aus weiter in einen Weißton zu laufen, damit das
  Flugzeug dem Foto ähnlicher wird. Ausprobiert (ab dem Zusammenstoß der
  Balken, Ziel #F2EBE1) und wieder entfernt — es passt nicht zur Seite.
*/
function fadeSheen(t) {
    if (!sheenBase) return
    const s = clamp(t / FADE_SHEEN_BY, 0, 1)
    const e = s * s * (3 - 2 * s)
    // Auf drei Stellen gerundet vergleichen: darunter ändert sich keine Farbe
    // mehr um einen ganzen Wert.
    if (Math.abs(e - sheenLast) < 0.002 && (e === 1) === (sheenLast === 1)) return
    sheenLast = e
    const ziel = readRgb(ACCENT)
    goldStops.forEach((stop, i) => {
        const von = sheenBase[i]
        const c = ziel.map((z, k) => Math.round(von[k] + (z - von[k]) * e))
        stop.setAttribute('stop-color', `rgb(${c[0]} ${c[1]} ${c[2]})`)
    })
}

// Destinations schalten, wenn die Zeichen-Front sie passiert hat. Nur bei
// Änderung ins DOM schreiben — die Klasse wechselt über die ganze Seite
// sechsmal, der Vergleich läuft pro Frame.
function setReached(n) {
    if (n === reachedCount) return
    reachedCount = n
    allNodes().forEach((el, i) => el.classList.toggle('is-reached', i < n))
}

function reachedAt(front) {
    let n = 0
    while (n < nodeYs.length && front >= nodeYs[n]) n++
    return n
}

function render(now) {
    if (!geo) return
    // Lesen gebündelt, danach nur noch schreiben — kein Layout-Thrashing.
    const trackRect = track.value.getBoundingClientRect()
    const routeRect = journeyEl.value.getBoundingClientRect()
    const { vh, table, takeoffLen, handoffSize } = geo

    const q = clamp(-trackRect.top / Math.max(1, trackRect.height - vh), 0, 1)
    /*
      Die Zeichen-Front steigt nach der Übergabe wieder ins Bild.

      Die Front bestimmt zwei Dinge, die bisher unauflöslich aneinanderhingen:
      die Höhe des Übergabepunkts UND die Flughöhe auf der ganzen Reise. Der
      Gleitflug braucht Sinkstrecke — je mehr, desto weiter und ruhiger sein
      Bogen —, aber eine tiefe Übergabe hieß bislang, dass das Flugzeug die
      ganze Reise am unteren Bildrand entlangfliegt.

      Gebraucht wird die Tiefe aber nur im Moment der Übergabe. Danach darf die
      Front zurück in die Bildmitte wandern: das Flugzeug fängt sich nach dem
      Sinkflug wieder — physikalisch stimmig und optisch das, was man erwartet.

      Der Anstieg ist langsamer als der Scroll (0,52 - 0,90 = 0,38 Bühnenhöhe
      über 1,6 Bühnenhöhen Weg), deshalb bleibt `front` monoton wachsend; sonst
      liefe die Zeichnung beim Weiterscrollen rückwärts.
    */
    let frontAt = FRONT_AT
    {
        const past = clamp((vh - routeRect.top) / (vh * FRONT_SETTLE_SPAN), 0, 1)
        frontAt += (FRONT_REST - FRONT_AT) * (past * past * (3 - 2 * past))
    }
    const front = -routeRect.top + vh * frontAt
    // Seiten-Fortschritt für den Parallax hier mitnehmen: trackRect liegt
    // bereits vor, und renderSky() darf keinen eigenen Layout-Read nach den
    // Style-Writes unten machen (Read-after-write = erzwungenes Reflow).
    geo.sp = clamp(-trackRect.top / geo.maxScroll, 0, 1)
    /*
      Frühzeitig aussteigen nur, wenn sich weder Scrollstand NOCH die
      nachlaufende Position noch ändern. Ohne die zweite Bedingung bliebe das
      Flugzeug beim Loslassen genau dort stehen, wo es war — der Nachlauf käme
      nie zum Zug.
    */
    const dt = lastRenderNow ? Math.min(0.05, (now - lastRenderNow) / 1000) : 0
    lastRenderNow = now
    const settled = lFollow < 0 || Math.abs(lengthAtY(table, front) - lFollow) < 0.4
    if (q === lastQ && front === lastFront && settled && copyRuht) return
    lastQ = q
    lastFront = front

    /*
      ── Bühne scrollt ab dem Losfliegen mit ─────────────────────────────
      Steht auf null (siehe STAGE_RUN) und ist nur noch der Regler dafür.

      Die FLUGEBENE braucht hier nichts mehr: sie zeichnet beim Gleitflug gar
      nichts, alles liegt im Routen-Container und scrollt im Seitenfluss mit.
      Genau das war das Ziel des Umbaus.
    */
    if (STAGE_RUN > 0 && stageEl.value) {
        const pinEnde = trackRect.height - vh
        const vorlauf = Math.max(0, -trackRect.top - MORPH_END * pinEnde)
        const voll = STAGE_RUN * (1 - MORPH_END) * pinEnde
        stageEl.value.style.translate =
            `0 ${(-Math.min(STAGE_RUN * vorlauf, voll)).toFixed(1)}px`
    }

    /*
      ── Parallax der Karten (siehe CARD_PARALLAX) ────────────────────────
      Gemessen wird die STATION, verschoben wird die Karte darin. Andersherum
      läse sich der Versatz in der nächsten Messung mit und schaukelte sich
      auf: getBoundingClientRect liefert die sichtbare Lage, `translate`
      inklusive. Die Station bleibt unberührt, ihr Kasten kommt aus dem
      Layout — und der Knoten hängt an ihr, nicht an der Karte.

      Erst alle Messungen, dann alle Schreibvorgänge, und beides noch vor dem
      Morph: ein Layout-Read nach einem Style-Write erzwingt einen Reflow.
    */
    if (kartenEls.length) {
        const lagen = stationEls.value.map((el) => (el ? el.getBoundingClientRect() : null))
        for (let i = 0; i < kartenEls.length; i++) {
            const el = kartenEls[i]
            const r = lagen[i]
            if (!el || !r) continue
            // +1 unter dem Bild, 0 in der Mitte, -1 darüber.
            //
            // Das Vorzeichen ist die ganze Aussage: unten liegt die Karte
            // TIEFER als ihr Platz, oben höher — sie legt also mehr Weg zurück
            // als die Seite und wirkt dadurch näher.
            //
            // Die Gegenrichtung wurde ausprobiert und wieder verworfen, und
            // zwar nicht aus Geschmack: sie zog die Karte im Moment des
            // Einlaufs nach OBEN, genau entgegen der Bewegung, die der Einlauf
            // macht. Gemessen blieben von seinen 70 px Weg noch +12 und +7 px
            // übrig — bei Karte 1 und 3 sogar -12 und -25, die Karten sanken
            // also an ihren Platz, statt heraufzukommen. In dieser Richtung
            // addiert sich beides zu 98 bis 107 px.
            const rel = clamp(
                ((r.top + r.height / 2) - vh / 2) / vh / CARD_PARALLAX_RANGE, -1, 1,
            )
            const f = CARD_PARALLAX_MIX[i % CARD_PARALLAX_MIX.length]
            const off = Math.sin((rel * Math.PI) / 2) * CARD_PARALLAX * f
            /*
              Gerastert wie der Himmel (siehe SCHREIBEN MIT RASTER), und hier
              aus einem eigenen Grund: die Karte ist `overflow: hidden` und
              trägt zusätzlich den Transform der Reveal-Timeline. Ein
              klippendes, transformiertes Element wird vom Browser zu einer
              eigenen Ebene mit eigenem Zuschnitt — sie in jedem Frame zu
              verschieben heißt, diesen Zuschnitt in jedem Frame neu
              anzuwenden.

              Ein Viertelpixel ist die Stufe und nicht ein halber: der Versatz
              geht bis CARD_PARALLAX, läuft über die Bildhöhe und ist eine
              Bewegung, die man DIREKT neben ruhendem Text sieht. Gröber wäre
              hier als Ruckeln zu erkennen — anders als bei den Wolken, die
              weich und ohne Kante sind.
            */
            schreibe(el, 'translate', `0 ${raster(off, 0.25).toFixed(2)}px`)
        }
    }

    // ── A: Morph ──────────────────────────────────────────────────────────
    const t = clamp(q / MORPH_END, 0, 1)
    // Nur bis zum sichtbaren Ende fahren (siehe MORPH_DONE). `t` selbst bleibt
    // der Anteil der Bühnenstrecke und steuert weiterhin das Umschalten.
    morph.progress = t * MORPH_DONE

    // Glanzlicht über das Motiv wandern lassen (Idee wie sectionCtaShine.js).
    if (goldGrad.value) {
        goldGrad.value.setAttribute('gradientTransform', `translate(${(q * 1.1 - 0.55).toFixed(4)} 0)`)
    }
    // Schein über den Morph wegblenden — siehe fadeSheen().
    fadeSheen(t)
    /*
      KEINE Abdunklung mehr. Sie schwoll mit dem Morph an (bis 0.85) und danach
      wieder ab — dramaturgisch gedacht, praktisch aber ein Wackler: die Wolken
      wurden erst deutlich dunkler und dann wieder heller, ohne dass etwas
      passiert wäre. Ihre Aufgabe, dem Logo Kontrast zu geben, erledigt der
      Scrim des Bühnenbilds, und der geht nur in eine Richtung.

      Abschwellen musste sie ohnehin: sie sitzt IN der Bühne, ein stehen
      gebliebener Wert wäre beim Wegscrollen als waagerechte Kante über den
      Inhalt gelaufen — dasselbe Problem wie beim Kino-Rahmen.
    */
    // Fortschritt des Auflösens. Wird hier gesetzt und in renderSky()
    // angewendet: dort liegen die Hintergrundebenen zusammen.
    /*
      LINEAR, nicht mit weicher Kurve. Eine Smootherstep über diesen Weg
      erledigt zwei Drittel des Auflösens in der Mitte der Strecke — der Weg
      war dann zwar lang, die Bewegung darin aber immer noch kurz. Die weiche
      Staffelung liefert ohnehin schon der Versatz der Flecken untereinander
      (PUFF_STAGGER), jeder einzelne von ihnen läuft weiterhin mit Kurve.
    */
    geo.open = clamp(
        (-trackRect.top - geo.openStart) / Math.max(1, geo.openEnd - geo.openStart),
        0, 1,
    )
    /*
      Die Copy steht über den ganzen Morph (endet bei 0.72) und geht erst
      während des Steigflugs weg — das Flugzeug klettert nach oben, sie sitzt
      unten, die beiden kommen sich nicht in die Quere. Bis zum Handoff ist sie
      weg. Eine frühere Fassung ließ sie schon bei 40 % der Bühnenstrecke
      verschwinden; danach stand eine halbe Scrollstrecke lang eine leere Bühne
      mit einem Logo darin.
    */
    /*
      Wann sie geht, hängt an der Breite (--ku-copy-fade, siehe dort).

      Der Fortschritt läuft dafür ÜBER das Pin-Ende hinaus: `q` steht bei 1
      still, sobald die Bühne den Pin verlässt — solange sie aber gepinnt ist,
      füllt sie den Viewport und von der ersten Karte ist nichts zu sehen. Soll
      die Copy stehen bleiben, bis die Karte anschneidet, muss sie also nach dem
      Pin weiterblenden. Dahinter zählt der Weg in Bühnenhöhen weiter, 1.2 heißt
      demnach „ein Fünftel Bühnenhöhe nach dem Pin".

      Die Copy scrollt in dieser Phase ohnehin mit der Bühne nach oben, während
      die Karte von unten hereinkommt — beide sind gleichzeitig im Bild.
    */
    const ueberPin = Math.max(0, (-trackRect.top - (trackRect.height - vh)) / vh)
    // Derselbe erweiterte Fortschritt steuert den Vordergrund (siehe FORE_IN).
    // Hier abgelegt und nicht dort gerechnet: renderSky() hat kein trackRect.
    geo.qx = q + ueberPin
    /*
      Choreografie des Übergangs. Alles hängt am Scrollweg seit dem Abflug,
      weil sich daran auch das Flugzeug bewegt — nur so lassen sich die drei
      Ereignisse gegeneinander stellen:

        die Copy geht, kurz BEVOR das Flugzeug ihre Höhe erreicht,
        die Karte kommt kurz DANACH.

      Die Verkleinerung endet mit dem Ausblenden und beginnt entsprechend
      früher; ihre Strecke ist fest, ihre Lage folgt also dem Ausblenden.
    */
    const pinWeg = trackRect.height - vh
    const seitAbflug = (-trackRect.top - MORPH_END * pinWeg) / vh
    const kopfWeg = COPY_FADE + COPY_FADE_SPAN
    /*
      Die Copy weicht der ersten Karte — in BEIDE Richtungen.

      Ihr Ausblenden hängt am Scrollstand und folgt ihm sofort; der Einlauf der
      Karte ist dagegen eine Zeit-Tween von rund einer Sekunde. Nach unten geht
      das gut aus (die Copy ist weg, bevor die Karte kommt), nach oben nicht:
      dort ist die Copy sofort wieder da, während die Karte noch ausblendet.

      Statt an Auslösepunkten zu schrauben, wird die Regel direkt hingeschrieben
      — die Copy kann höchstens so sichtbar sein, wie die Karte es NICHT ist.
      Beim Hochscrollen wird daraus von selbst eine Kreuzblende im Tempo der
      Karte; nach unten ändert sich nichts, weil die Copy dort ohnehin schon
      auf null steht.

      Gelesen wird der Inline-Wert, den GSAP schreibt, nicht der berechnete:
      das ist ein einfacher Attributzugriff und erzwingt keine Stilberechnung.
    */
    const ersteKarte = kartenEls[0]
    const kartenAlpha = ersteKarte ? (Number.parseFloat(ersteKarte.style.opacity) || 0) : 0
    // Solange die Karte auch nur schwach steht, bleibt die Copy ganz weg —
    // kein Übereinander und keine Kreuzblende.
    const zielAlpha = kartenAlpha > 0.02
        ? 0
        : 1 - clamp((seitAbflug - COPY_FADE) / COPY_FADE_SPAN, 0, 1)
    if (copyAlpha < 0) copyAlpha = zielAlpha
    copyAlpha = zielAlpha <= copyAlpha
        ? zielAlpha
        : copyAlpha + (zielAlpha - copyAlpha)
            * (dt > 0 ? 1 - Math.exp(-dt / COPY_RETURN_TAU) : 1)
    copyRuht = Math.abs(zielAlpha - copyAlpha) < 0.002
    copyEl.value.style.opacity = copyAlpha.toFixed(3)
    /*
      SIE STEHT STILL, BIS SIE WEG IST.

      Die Copy sitzt in der Bühne, und die klebt nur bis zum Ende ihrer
      Strecke — danach scrollt sie weg und nähme die Copy mit. Da ihr
      Ausblenden inzwischen am Flugzeug hängt und nicht mehr am Pin, fällt es
      zum Teil hinter dessen Ende: sie würde also mitten im Verschwinden nach
      oben wandern.

      Deshalb bekommt sie den Weg, den die Bühne nach dem Pin zurücklegt, als
      Gegenbewegung — aber nur so lange, bis sie unsichtbar ist. Danach friert
      der Wert ein und sie zieht mit der Bühne ab; sichtbar ist davon nichts
      mehr, und ohne den Deckel bliebe ein unsichtbarer Textblock dauerhaft im
      Bild stehen.

      Eigenständige Eigenschaft neben `scale`, damit sich beide nicht um
      dieselbe Kette streiten.
    */
    if (COPY_HOLD) {
        const nachPin = Math.max(0, -trackRect.top - pinWeg)
        const halten = Math.max(0, kopfWeg * vh - (1 - MORPH_END) * pinWeg)
        copyEl.value.style.translate = `0 ${Math.min(nachPin, halten).toFixed(1)}px`
    } else if (copyEl.value.style.translate) {
        copyEl.value.style.translate = ''
    }
    /*
      TEST: Die Verkleinerung beginnt mit dem ABFLUG und endet mit dem
      Ausblenden — sie läuft also über die ganze Zeit, in der die Copy nach dem
      Morph noch steht. Vorher hatte sie eine eigene, kürzere Strecke kurz vor
      dem Verschwinden.

      Eigenständige Eigenschaft, nicht `transform`: die Copy liegt im
      Bühnenraster und soll dort bleiben.
    */
    const weg = ease(clamp(seitAbflug / Math.max(0.05, kopfWeg), 0, 1))
    copyEl.value.style.scale = (1 - COPY_SHRINK * weg).toFixed(4)
    hintEl.value.style.opacity = String(1 - clamp(q / 0.1, 0, 1))
    morphSvg.value.style.opacity = t < 1 ? '1' : '0'

    if (t < 1) {
        // Vor dem Handoff zeichnet das Morph-SVG die Form. Weder Route noch
        // Streifen sind sichtbar — es gibt noch keinen Flug.
        journeyPlane.value.style.opacity = '0'
        setOrigin(0)
        for (const el of gezeichnet()) el.style.strokeDashoffset = String(table.total)
        routePath.value.style.strokeDashoffset = String(table.total)
        setReached(0)
        return
    }

    /*
      ── DER FLUG ─────────────────────────────────────────────────────────

      Alles ist EINE Kurve, und es wechselt nur der ANTRIEB: bis zur Übergabe
      zieht der Scrollanteil das Flugzeug über den Bogen (mit eigenem
      Tempoverlauf, siehe takeoffEase), danach hängt es an der Zeichen-Front
      und läuft ihr nach. Beide liefern an der Übergabe dieselbe Bogenlänge —
      deshalb gibt es dort nichts anzugleichen.
    */
    // Fortschritt der Antriebsphase, in Scroll-Pixeln ab dem Abflug
    // (siehe TAKEOFF_VH) — NICHT in Bühnenfortschritt.
    const abflug = -trackRect.top - MORPH_END * (trackRect.height - vh)
    const xRoh = abflug / Math.max(1, TAKEOFF_VH * vh)
    /*
      ZWEI ANTRIEBE, ÜBER DIE GANZE PHASE INEINANDER.

      Der Scrollanteil bringt das Flugzeug vom Logo weg, die Zeichen-Front
      trägt es über die Reise. Sie hart umzuschalten war als Bremsruck zu
      sehen — die eine endet bei rund 0,4 px Bildschirmweg je Scroll-Pixel,
      die andere läuft mit etwa null, und weil der Wechsel in die Nähe des
      ersten Wegpunkts fiel, wirkte er wie ein Fehler an der Station.

      Erst nach dem Ende zu überblenden half nicht: dort liegen die beiden
      Rechnungen schon weit auseinander, das Flugzeug schwang auf -0,57
      zurück. Der Grund ist, dass der in build() geschätzte Umschaltpunkt
      die Front nie exakt trifft.

      Deshalb wächst das Gewicht der Front über die GANZE Antriebsphase von
      null auf eins. Am Abflug zählt allein der Scrollanteil (das Flugzeug
      steht auf dem Logo), am Ende allein die Front — der Anschluss stimmt
      damit per Konstruktion und nicht per Schätzung. Das Verlangsamen hängt
      dadurch am zurückgelegten Weg statt an einem Punkt.
    */
    const ausScroll = takeoffEase(clamp(xRoh, 0, 1)) * takeoffLen
    const lTarget = lengthAtY(table, front)
    if (lFollow < 0) lFollow = lTarget
    lFollow += (lTarget - lFollow) * (dt > 0 ? 1 - Math.exp(-dt / FOLLOW_TAU) : 1)
    const gewicht = ease(clamp(xRoh, 0, 1))
    const l = ausScroll + (lFollow - ausScroll) * gewicht

    /*
      KEINE Größenänderung mehr. Das Flugzeug behält die Größe, mit der es
      aus der Wortmarke hervorgeht — gemessen 50,4 px auf breiten Fenstern
      gegen 46 der früheren Reiseflug-Größe, schmal 21,5 gegen 22. Der
      Unterschied war klein, lief aber genau über den Anfang des Fluges und
      war dort als zusätzliches Schrumpfen zu sehen, wo ohnehin schon der
      Morph endet.
    */
    const punkt = pointAt(trailPath.value, l, table.total)
    place(journeyPlane.value, punkt, handoffSize)

    // Der Vorlauf der leisen Linie wächst über den Bogen von null auf sein
    // Maß und bleibt danach konstant — eine Formel für beide Phasen.
    const lead = vh * LEAD_VH * clamp(l / Math.max(1, takeoffLen), 0, 1)
    for (const el of gezeichnet()) el.style.strokeDashoffset = String(table.total - l)
    routePath.value.style.strokeDashoffset =
        String(table.total - Math.min(table.total, l + lead))
    journeyPlane.value.style.opacity = String(
        1 - clamp((l / Math.max(1, table.total) - (1 - FADE_TAIL)) / FADE_TAIL, 0, 1),
    )
    setOrigin(clamp(l / Math.max(1, takeoffLen) / 0.1, 0, 1))
    /*
      Destinationen schalten an der Lage des FLUGZEUGS, nicht an der der
      Zeichen-Front und nicht an der Antriebsphase.

      Vorher stand hier `xRoh < 1 ? 0 : reachedAt(front)` — solange der
      Scrollanteil führte, blieb alles dunkel. Das ging auf, als diese
      Phase vor dem ersten Wegpunkt endete; seit sie eine volle Bildhöhe
      lang ist, reicht sie über ihn hinaus, und der erste Knoten leuchtete
      erst auf, wenn das Flugzeug längst daran vorbei war. Die übrigen
      waren nie betroffen, weil sie hinter der Phase liegen.

      Die Front taugt als Bezug ebenfalls nicht: sie eilt dem Flugzeug
      voraus, in der Antriebsphase um ein Vielfaches. Der Punkt, an dem
      das Flugzeug gerade steht, ist die einzige Größe, die in beiden
      Phasen dasselbe bedeutet — in der Reise fallen beide ohnehin
      zusammen, dort ändert sich nichts.
    */
    setReached(reachedAt(punkt.y))
}

/*
  ══ SCHREIBEN MIT RASTER ══════════════════════════════════════════════════

  Die Eigenbewegung des Himmels läuft in MINUTEN, gezeichnet wird in
  Sechzigstelsekunden. Genau daraus entstand der teuerste Unsinn der Seite:
  renderSky() schrieb jedem Fleck und jedem Fetzen pro Frame ein neues `scale`,
  obwohl sich der Wert zwischen zwei Frames um rund 0,0003 änderte — bei einer
  Quellperiode von 47 bis 106 Sekunden.

  Eine Änderung an `scale` ist aber KEINE reine Kompositionsänderung: der
  Browser muss das Hintergrundbild in der neuen Größe neu rastern. Genau das
  stand als Warnung schon an PUFF_MOTION („gemessen 39 statt 67 fps"), und das
  später ergänzte Quellen und Zerfallen (`swell`) hat es wieder eingebaut.

  Statt das Quellen aufzugeben, wird es GERASTERT: der Wert rutscht auf ein
  Vielfaches der Stufe, und geschrieben wird nur, wenn sich daran etwas ändert.
  Bei 0,003 auf einem 1637 px großen Fleck ist eine Stufe knapp 5 px an einer
  Fläche mit 10 bis 20 % Deckkraft — nicht zu sehen, aber statt 60 Rasterungen
  je Sekunde bleiben rund 8.

  Was NICHT gerastert wird: alles, was am Scrollstand hängt (Parallax-Weg,
  Drift, die Deckkraft des Vordergrunds). Das soll dem Finger folgen, und es
  sind Verschiebungen und Deckkräfte — reine Komposition, also billig. Gerastert
  wird nur, was eine Neurasterung auslöst (`scale`) oder was ohnehin in
  Zeitlupe läuft.
*/
const letzterStil = new WeakMap()
function schreibe(el, prop, text) {
    let merk = letzterStil.get(el)
    if (!merk) { merk = {}; letzterStil.set(el, merk) }
    if (merk[prop] === text) return
    merk[prop] = text
    el.style[prop] = text
}
// Rastert einen Wert auf ein Vielfaches von `stufe`.
const raster = (wert, stufe) => Math.round(wert / stufe) * stufe

/*
  Stufen, je Eigenschaft und Ebene begründet:

  PUFF_SCALE / FORE_SCALE lösen eine Neurasterung des Sprites aus, sie sind die
  eigentliche Ersparnis. Die Fetzen dürfen viel gröber laufen als die Flecken:
  sie sind größer (bis 2510 px), weicher und laufen bei 5,5 bis 8,5 % statt bei
  10 bis 20 % Deckkraft.

  ZOOM gilt für Karte und Wolkendecke. Beide skalieren über die Seite (Faktor
  1 -> 1,1 bzw. 1 -> 1,22) und beide sind riesig; die Karte trägt zusätzlich
  eine Weichzeichnung, die bei jeder Größenänderung neu gerechnet wird. 0,002
  sind über die ganze Seite 50 bzw. 110 Stufen statt eine je Frame.
*/
const PUFF_SCALE_STUFE = 0.003
const PUFF_ALPHA_STUFE = 0.002
const FORE_SCALE_STUFE = 0.008
const ZOOM_STUFE = 0.002

// Parallax + Wolkendrift. Läuft JEDEN Frame, auch wenn render() bei
// unverändertem Scrollstand früh zurückkehrt — sonst stünde der Drift still.
let driftX = 0
// Nachlaufender Seiten-Fortschritt der Hintergrundebenen (siehe SKY_TAU).
// -1 heißt „noch kein Startwert": der erste Frame nach einem Neuaufbau setzt
// ihn hart, sonst gliche die Decke aus ihrer alten Lage in die neue.
let spFollow = -1
let lastNow = 0
let lastOpen = -1

/*
  Auflösen des Wolkenfeldes. Läuft nur, wenn sich der Fortschritt geändert hat —
  zwanzig Elemente pro Frame anzufassen wäre sonst der teuerste Teil der Seite,
  obwohl sich meistens nichts ändert.

  Was sich ändert, ist AUSSCHLIESSLICH die Deckkraft — und das ist eine
  Messung, keine Vorliebe. Zuerst wuchsen die Flecken beim Auflösen zusätzlich
  (eine Wolke, die dünner wird, zerfließt). Der Hintergrund eines Flecks ist
  aber eine SVG mit Turbulenzfilter: ändert sich seine Größe, rastert der
  Browser sie neu. Gemessen 39 statt 67 fps beim Scrollen durch die Bühne.
  Quantisieren half kaum (jede neue Stufe ist ein neuer Filterlauf, und jeder
  Fleck hat seine eigenen Stufen).

  Das Auflösen trägt ohnehin die Staffelung: die Flecken gehen nicht gemeinsam,
  sondern von der Mitte nach außen nacheinander. Die Größenstreuung liefert
  stattdessen der statische Aufbau (siehe PUFFS) — sie kostet dort nichts.
*/
/* ── Fallblattanzeige ─────────────────────────────────────────────────── */
let flapCells = []
let flipping = false
let boardIo = null

const drumIdx = (ch) => DRUM_IDX.get(ch) ?? 0

// Zentriert auf COLS polstern: kürzere Angaben stehen mittig im Raster.
function padCenter(text) {
    const t = [...text].slice(0, COLS)
    const left = Math.floor((COLS - t.length) / 2)
    return ' '.repeat(left) + t.join('') + ' '.repeat(COLS - t.length - left)
}

function setBoard(instant = false) {
    if (!flapCells.length) return
    const lines = boardRows.map((r) => [...padCenter(r)])

    if (instant) {
        lines.forEach((line, r) => {
            for (let c = 0; c < COLS; c++) {
                const cell = flapCells[r * COLS + c]
                cell.shown = drumIdx(line[c])
                cell.steps = 0
                cell.leaf.textContent = DRUM_CHARS[cell.shown]
            }
        })
        boardReady.value = true
        return
    }

    const now = performance.now()
    lines.forEach((line, r) => {
        for (let c = 0; c < COLS; c++) {
            const cell = flapCells[r * COLS + c]
            const target = drumIdx(line[c])
            let dist = (target - cell.shown + N_DRUM) % N_DRUM
            if (dist === 0) { cell.steps = 0; continue }
            let from = cell.shown
            if (dist > MAX_STEPS) { from = (target - MAX_STEPS + N_DRUM) % N_DRUM; dist = MAX_STEPS }
            cell.from = from
            cell.steps = dist
            cell.done = -1
            cell.start = now + c * COL_STAGGER + r * ROW_STAGGER
        }
    })
    flipping = true
}

// Läuft in derselben rAF-Schleife wie alles andere. Die Zeichen werden
// imperativ per textContent gesetzt: bei ~90 Zellen wäre Vues Patch-Zyklus
// reiner Overhead, und die Zellenstruktur ändert sich nie.
function stepFlip(now) {
    if (!flipping) return
    let busy = false
    for (const cell of flapCells) {
        if (!cell.steps) continue
        const t = now - cell.start
        if (t < 0) { busy = true; continue }
        const step = Math.min(cell.steps, Math.floor(t / STEP_MS))
        if (step !== cell.done) {
            cell.done = step
            cell.shown = (cell.from + step) % N_DRUM
            cell.leaf.textContent = DRUM_CHARS[cell.shown]
        }
        if (step >= cell.steps) {
            cell.steps = 0
            if (cell.spinning) { cell.spinning = false; cell.flap.classList.remove('is-spinning') }
        } else {
            busy = true
            if (!cell.spinning) { cell.spinning = true; cell.flap.classList.add('is-spinning') }
        }
    }
    if (!busy) { flipping = false; boardReady.value = true }
}

/*
  Zurück auf leer. Erst dadurch kann die Tafel ein zweites Mal blättern: das
  Umspringen ist der Weg von einem Zeichen zum nächsten, und wenn schon das
  Ziel dasteht, ist dieser Weg null (siehe setBoard, `dist === 0`).
*/
function clearBoard() {
    flipping = false
    for (const cell of flapCells) {
        cell.shown = 0
        cell.steps = 0
        cell.done = -1
        cell.leaf.textContent = DRUM_CHARS[0]
        if (cell.spinning) { cell.spinning = false; cell.flap.classList.remove('is-spinning') }
    }
    boardReady.value = false
}

function initBoard() {
    const flaps = boardEl.value?.querySelectorAll('.ku-flap') ?? []
    flapCells = [...flaps].map((flap) => ({
        flap,
        leaf: flap.firstElementChild,
        shown: 0, from: 0, steps: 0, start: 0, done: -1, spinning: false,
    }))
    if (!flapCells.length) return

    if (reduce) { setBoard(true); return }

    /*
      Erst blättern, wenn die Tafel im Blick ist — sie soll nicht schon fertig
      dastehen, wenn man unten ankommt, sondern sichtbar umspringen.

      Und JEDES Mal, nicht nur beim ersten Vorbeikommen: der Beobachter bleibt
      bestehen, und sobald die Tafel das Bild ganz verlassen hat, wird sie
      geleert. Beim nächsten Herunterscrollen springt sie damit wieder um.
      Vorher trennte er sich nach dem ersten Treffer selbst ab — die Tafel war
      ein einmaliger Effekt, wie der Einlauf der Karten.

      Zwei Schwellen: umspringen ab 35 % Sichtbarkeit, leeren erst bei 0.
      Zwischen beiden liegt genug Abstand, dass ein Wackeln am Bildrand nicht
      zwischen den Zuständen hin- und herschaltet.
    */
    let gezeigt = false
    boardIo = new IntersectionObserver((entries) => {
        for (const e of entries) {
            if (e.isIntersecting && e.intersectionRatio >= 0.35) {
                if (!gezeigt) { gezeigt = true; setBoard() }
            } else if (!e.isIntersecting && gezeigt) {
                gezeigt = false
                clearBoard()
            }
        }
    }, { threshold: [0, 0.35] })
    boardIo.observe(boardEl.value)
}

/*
  Deckkraft, die auf der REISE stehen bleibt — Mitte praktisch weg, Rand als
  Kranz. Absolute Werte, unabhängig von der Grunddeckkraft in der Bühne: die
  beiden Zustände sind getrennt einstellbar (siehe applyDissolve).
*/
const REST_MID = 0.011
const REST_EDGE = 0.275
// Bezugsgröße für die Abstufung der einzelnen Flecken. Gerechnet statt notiert,
// damit sie beim nächsten Nachjustieren von `a` nicht stehenbleibt.
const A_MEAN = PUFFS.reduce((s, p) => s + p.a, 0) / PUFFS.length

const puffAlpha = PUFFS.map((p) => p.a)

function applyDissolve(open) {
    for (let i = 0; i < PUFFS.length; i++) {
        const p = PUFFS[i]
        // 0 in der Mitte, 1 am Rand — steuert Reihenfolge UND Restdeckkraft.
        const edge = Math.abs(p.x - 50) / 50
        const u = ease(clamp((open - PUFF_STAGGER * edge) / PUFF_SPAN, 0, 1))
        /*
          Restdeckkraft, QUADRATISCH mit dem Randabstand. Linear war zu wenig:
          die Flecken sind größer als ihr Abstand, also reicht schon ein
          halbwegs mittiger Fleck mit 30 % Rest, um die Mitte wieder zuzuziehen.
          Quadratisch bleibt praktisch nur der äußere Kranz stehen — und genau
          der soll bleiben.

          Sie ist jetzt ein ABSOLUTER Wert, kein Anteil von `a` mehr. Vorher
          hing das Ende an der Bühne: jedes Zurücknehmen der Grunddeckkraft
          nahm die Reise mit, und beide Male musste hier gegengerechnet werden.
          Der Bezug auf A_MEAN erhält dabei die Abstufung der einzelnen Flecken
          (die unteren Reihen sind dünner), ohne die Kopplung an ihre Höhe.
        */
        /*
          Ohne Deckel auf die Bühnendeckkraft — mit Absicht. Die Bühne ist
          inzwischen so dunkel und die Reise so kräftig, dass sich beide am
          RAND kreuzen: die äußeren Flecken (edge ab ~0,73, sechs von zwanzig)
          enden heller, als sie angefangen haben. Ein Deckel hätte genau die
          sechs eingefroren, also den Kranz, auf den es ankommt. So liest es
          sich stattdessen als Licht, das die stehengebliebenen Wolken fängt,
          während die Mitte aufreißt.
        */
        const restA = (REST_MID + (REST_EDGE - REST_MID) * edge * edge) * (p.a / A_MEAN)
        // Nur der Zielwert; geschrieben wird er in renderSky() zusammen mit
        // dem Atmen — sonst stritten sich zwei Stellen um dieselbe Deckkraft.
        puffAlpha[i] = p.a + (restA - p.a) * u
    }
}

// Parallax, Drift und Auflösen. Läuft JEDEN Frame, auch wenn render() bei
// unverändertem Scrollstand früh zurückkehrt — sonst stünde der Drift still.
function renderSky(now) {
    if (!geo || reduce) return
    const open = geo.open ?? 0

    // Aufaddieren statt ausrechnen (siehe DRIFT_SPEED). dt gedeckelt, damit ein
    // Tab-Wechsel keinen Sprung erzeugt.
    const dt = lastNow ? Math.min(0.05, (now - lastNow) / 1000) : 0
    lastNow = now
    driftX = clamp(driftX + DRIFT_SPEED * dt * (1 - open), -DRIFT_MAX, DRIFT_MAX)

    /*
      Der Seiten-Fortschritt, dem die Ebenen folgen — nicht der, an dem sie
      hängen. Wie beim Flugzeug: bildratenunabhängig über die Zeitkonstante,
      damit der Nachlauf bei 60 und 120 Hz gleich lang dauert.

      Er speist auch die Deckkraft der Karte weiter unten: Lage und Helligkeit
      beschreiben denselben Weg, sie dürfen nicht auseinanderlaufen. Das
      Auflösen der Wolken (`open`) bleibt dagegen ungebremst — es hängt am
      Aufdecken, nicht an der Bewegung, und würde sonst zwanzig Flecken pro
      Frame neu setzen, nur um nachzulaufen.
    */
    const spTarget = geo.sp ?? 0
    if (spFollow < 0) spFollow = spTarget
    spFollow += (spTarget - spFollow) * (dt > 0 ? 1 - Math.exp(-dt / SKY_TAU) : 1)
    const sp = spFollow

    if (chartEl.value) {
        const sway = CHART_SWAY.reduce((a, d) => a + d.amp * Math.sin((now / d.period) * Math.PI * 2), 0) * 100
        /*
          Der Weg bleibt fein, die SKALIERUNG wird gerastert (siehe
          ZOOM_STUFE). Der Unterschied ist entscheidend: eine Verschiebung
          schiebt die fertige Textur, eine Skalierung rastert sie neu — und
          diese Ebene ist 1812x1543 CSS-px groß und trägt ab l eine
          Weichzeichnung. Sie pro Frame neu zu blurren war gemessen der zweite
          große Posten der Seite (p95 16,7 gegen 9,0 ms ohne den Blur).
        */
        schreibe(chartEl.value, 'transform',
            `translate3d(${sway.toFixed(2)}%, ${(-sp * geo.chartTravel).toFixed(1)}px, 0)`
            + ` scale(${raster(1 + sp * CHART_ZOOM, ZOOM_STUFE).toFixed(4)})`)
        // Die Karte wird nicht nur freigelegt, sie kommt auch herauf: ohne den
        // Anstieg sähe man sie in der ersten Lücke schon in voller Stärke.
        /*
          Zwei Anteile. Der erste ist das FREILEGEN: er hängt am Auflösen der
          Wolken und läuft etwas schneller als dieses — sonst entsteht in der
          Mitte ein Loch, in dem der Himmel schon weg und der Boden noch nicht
          da ist.

          Der zweite ist das SINKEN über die Seite: die Karte kommt im Verlauf
          näher. Sie war der eigentliche Deckel, nicht die Abdunklung — die
          trägt nur einen flächigen Anteil von rund einem Drittel, an ihr zu
          drehen war deshalb kaum zu sehen.

          Beide Anteile sind heraufgesetzt, und das Sinken beginnt bei einem
          Achtel der Seite statt bei einem Drittel: die Karte war zu lange zu
          dunkel, um als Boden gelesen zu werden. Die Summe ist jetzt genau 1 —
          mehr geht an dieser Schraube nicht. Der zweite Teil der Aufhellung
          steckt deshalb in chart.svg selbst, dessen Gruppen-Deckkraft um die
          Hälfte angehoben ist; über allem liegt weiterhin der Scrim (46 %
          Schwarz plus Vignette). Die Zahlen hier sind also nicht das, was
          ankommt.
        */
        const reveal = clamp(open / 0.68, 0, 1)
        const descent = ease(clamp((sp - 0.12) / 0.88, 0, 1))
        schreibe(chartEl.value, 'opacity',
            (0.1 + 0.45 * reveal + 0.45 * descent).toFixed(3))
    }

    // Eine Transformation für das ganze Feld: Drift und Parallax hängen an der
    // Deckfläche, die Flecken tragen nur ihr eigenes Auflösen.
    if (deckEl.value) {
        // Skalierung gerastert wie bei der Karte: die Deckfläche ist 1881x1786
        // CSS-px groß und trägt das ganze Fleckenfeld samt Grundton.
        schreibe(deckEl.value, 'transform',
            `translate3d(${driftX.toFixed(2)}%, ${(-sp * geo.cloudTravel).toFixed(1)}px, 0)`
            + ` scale(${raster(1 + sp * CLOUD_ZOOM, ZOOM_STUFE).toFixed(4)})`)
    }

    /*
      Eigenbewegung der Flecken. Läuft jeden Frame, auch bei stehendem Scroll —
      in der Bühne ist das die einzige Bewegung im Bild.
    */
    const puffs = puffEls.value
    for (let i = 0; i < PUFFS.length; i++) {
        const el = puffs[i]
        if (!el) continue
        const m = PUFF_MOTION[i]
        const dx = m.ax * Math.sin((now / m.px) * TAU + m.ph)
        const dy = m.ay * Math.sin((now / m.py) * TAU + m.ph * 1.7)
        // Halbe Pixel: das Schwimmen legt bei 25 px Amplitude und 34 s Periode
        // rund 0,08 px je Frame zurück — ohne Raster ein Schreibvorgang für ein
        // Zwölftel Pixel.
        schreibe(el, 'transform',
            `translate3d(${raster(dx, 0.5).toFixed(1)}px, ${raster(dy, 0.5).toFixed(1)}px, 0)`)

        /*
          Quellen und Zerfallen (siehe swell in PUFF_MOTION). Über die
          EIGENSTÄNDIGE CSS-Eigenschaft `scale`, nicht über die transform-Kette:
          dort sitzt schon das Schwimmen, und zwei Stellen um dieselbe Kette
          streiten zu lassen war der Grund, warum Drehung und Größe von Anfang
          an getrennt liegen. Der Fleck skaliert dabei um seine eigene Mitte,
          das zentrierende `translate: -50% -50%` bleibt unberührt.
        */
        const grow = m.swell * Math.sin((now / m.ps) * TAU + m.phs)
        schreibe(el, 'scale', raster(1 + grow, PUFF_SCALE_STUFE).toFixed(4))

        /*
          Wachsen heißt auch DICHTER. Eine Wolke, die aufquillt, bekommt mehr
          Wasser und streut mehr Licht; eine, die zerfällt, zieht sich
          auseinander und wird fahl. Deshalb ist die Deckkraft mit demselben
          Vorzeichen gekoppelt (0,9) und nicht gegenläufig — gegenläufig hätte
          sich beides fast aufgehoben und man hätte gar nichts gesehen.
          Das kurze Atmen bleibt als eigener, schnellerer Anteil daneben.
        */
        const breath = m.breath * Math.sin((now / m.pb) * TAU + m.ph)
        schreibe(el, 'opacity',
            raster(puffAlpha[i] * (1 + breath + grow * 0.9), PUFF_ALPHA_STUFE).toFixed(3))
    }

    /*
      ── Vordergrund ──────────────────────────────────────────────────────
      Dieselbe Mechanik wie die Decke, nur mit dem vierfachen Weg und einem
      Zeitfenster davor. Die Drift ist ebenfalls schneller: nah heißt bei
      Seitwärtsbewegung genauso nah wie bei senkrechter.
    */
    if (foreEl.value) {
        const qx = geo.qx ?? 0
        const rise = FORE_BASE
            + (1 - FORE_BASE) * ease(clamp((qx - FORE_IN) / FORE_IN_SPAN, 0, 1))
        const settle = ease(clamp((qx - 1) / FORE_OUT_SPAN, 0, 1))
        const foreA = rise * (1 - (1 - FORE_REST) * settle)

        // Die Ebene selbst trägt nur eine Verschiebung, kein Zoom: hier ist
        // nichts zu rastern, und der Parallax soll dem Finger folgen.
        schreibe(foreEl.value, 'transform',
            `translate3d(${(driftX * 1.7).toFixed(2)}%, ${(-sp * geo.foreTravel).toFixed(1)}px, 0)`)

        const wisps = wispEls.value
        for (let i = 0; i < FORE.length; i++) {
            const el = wisps[i]
            if (!el) continue
            const m = FORE_MOTION[i]
            const dx = m.ax * Math.sin((now / m.px) * TAU + m.ph)
            const dy = m.ay * Math.sin((now / m.py) * TAU + m.ph * 1.4)
            /*
              Gröber als bei den Flecken, und zwar deutlich: diese Fetzen sind
              bis 2510 px groß, weichgezeichnet und laufen bei 5,5 bis 8,5 %
              Deckkraft. Ein ganzer Pixel Weg und 0,8 % Größe sind dort
              unsichtbar — die Größenstufe alleine senkt die Neurasterungen von
              60 je Sekunde auf etwa zwei.
            */
            schreibe(el, 'transform',
                `translate3d(${raster(dx, 1).toFixed(0)}px, ${raster(dy, 1).toFixed(0)}px, 0)`)
            const grow = m.swell * Math.sin((now / m.ps) * TAU + m.phs)
            schreibe(el, 'scale', raster(1 + grow, FORE_SCALE_STUFE).toFixed(4))
            // Deckkraft NICHT gerastert: `foreA` hängt am Scrollstand (siehe
            // FORE_IN), das Auf- und Abblenden soll dem Finger folgen. Eine
            // Deckkraftänderung rastert ohnehin nichts neu.
            schreibe(el, 'opacity', (FORE[i].a * foreA * (1 + grow * 0.8)).toFixed(4))
        }
    }

    /*
      Die Abdunklung bleibt KONSTANT. Sie über den Scrollverlauf zurückzunehmen
      war der zweite Weg, auf dem die Wolken wieder heller wurden — und sie war
      dafür ohnehin das falsche Werkzeug: gemessen trägt sie nur rund ein
      Drittel flächigen Anteil, spürbar war die Änderung erst, als die Karte
      selbst heller wurde. Genau die macht es jetzt allein (siehe oben).
    */
    /*
      Der warme Grundton geht DEUTLICH früher als die Wolken selbst: er ist die
      Farbe des Himmels, und durch eine Lücke sieht man nicht Himmel, sondern
      Boden. Linear mit dem Auflösen gekoppelt lag er als flächiger Schleier
      über der Karte, solange noch Wolken da waren — das war der Grund, warum
      die Karte im Spalt nicht auftauchte, obwohl die Decke schon offen war.
    */
    if (hazeEl.value) {
        schreibe(hazeEl.value, 'opacity',
            (1 - ease(clamp(open / 0.72, 0, 1))).toFixed(3))
        /*
          Der Grundton bekommt den Seitwärtsdrift wieder abgezogen.

          Er hängt als Kind an der Deckfläche, weil er nur so deren Höhe und
          senkrechten Parallax erbt — er ist aber die EINZIGE Ebene dort mit
          einer harten Kante: sein Verlauf ist deckend, die Fläche endet
          senkrecht. Die Flecken dürfen driften, sie sind radial ausmaskiert
          und haben keinen Rand.

          Der Überhang der Deckfläche (8 % je Seite) deckt den Drift nicht:
          14 % der EIGENEN Breite sind 16,2 % der Bühne, die Kante stand
          danach bei +8 % im Bild — als senkrechte Linie links, sichtbar erst
          nach gut einer Minute Standzeit. Genau der Fall, den DRIFT_MAX
          verhindern sollte.

          Das Aufheben ist dort exakt, wo es zählt: In der Bühne ist die
          Skalierung der Deckfläche noch ~1, der Drift läuft ohnehin nur
          solange sie steht. Später bleibt ein Rest von d·(1−scale), und der
          zieht die Kante nach AUSSEN.
        */
        schreibe(hazeEl.value, 'transform', `translate3d(${(-driftX).toFixed(2)}%, 0, 0)`)
    }

    if (open !== lastOpen) {
        lastOpen = open
        applyDissolve(open)
    }
}

// Statischer Zustand für prefers-reduced-motion: Logo, fertig gezeichnete
// Bahn, alle Destinations erreicht, kein Flugzeug. Keine rAF-Schleife.
function renderStatic() {
    morph.progress = 0
    goldGrad.value?.setAttribute('gradientTransform', 'translate(0 0)')
    // Ohne Scroll gibt es keinen Vorhang: Decke dünn, Karte da.
    if (chartEl.value) chartEl.value.style.opacity = '0.55'
    if (hazeEl.value) hazeEl.value.style.opacity = '0.2'
    applyDissolve(1)
    puffEls.value.forEach((el, i) => { if (el) el.style.opacity = puffAlpha[i].toFixed(3) })
    morphSvg.value.style.opacity = '1'
    journeyPlane.value.style.opacity = '0'
    copyEl.value.style.opacity = '1'
    copyAlpha = 1
    copyRuht = true
    copyEl.value.style.translate = ''
    copyEl.value.style.scale = '1'
    if (stageEl.value) stageEl.value.style.translate = ''
    if (linesEl.value) linesEl.value.style.translate = ''
    hintEl.value.style.opacity = '1'
    if (geo) {
        for (const el of gezeichnet()) el.style.strokeDashoffset = '0'
        routePath.value.style.strokeDashoffset = '0'
    }
    setReached(nodeYs.length)
}

function loop(now) {
    if (!document.hidden) {
        render(now)
        renderSky(now)
        stepFlip(now)
    }
    raf = requestAnimationFrame(loop)
}

/*
  Wann neu vermessen wird.

  BREITE des Routen-Containers: immer. Die Stützstellen der Bahn sind
  Kartenpositionen, sie hängen daran.

  HÖHE der Bühne: ebenfalls, und zwar schon ab wenigen Pixeln. Vorher stand
  hier eine Schwelle von 80 px, damit der Neuaufbau nicht mit dem eigenen
  Layout schwingt — die Sorge trifft aber die Höhe des INHALTS (Reveals,
  nachladende Bilder), nicht die der Bühne. Die ist 100svh: sie ändert sich
  weder durch Inhalt noch durch die ein- und ausfahrende Adressleiste, sondern
  nur, wenn jemand das Fenster anfasst oder das Gerät dreht. Genau dann soll
  neu gerechnet werden.

  Ohne das blieb der Startbogen bei einer reinen Höhenänderung stehen, während
  die Wortmarke darüber wanderte — bei 900 -> 500 px lag sein Anfang 205 px
  unter dem Flugzeug.

  Dafür muss der Observer auch die Bühne beobachten: der Routen-Container ist
  so breit wie der Inhalt und so hoch wie seine Karten, eine Fensterhöhe merkt
  er gar nicht.
*/
let lastW = 0
let lastVh = 0
let rebuildRaf = 0
let rebuildTimer = 0
/*
  Kurze nachlaufende Sperre statt nur eines rAF. Ein Neuaufbau kostet rund 9 ms
  (fast alles davon die Abtast-Tabelle, siehe sampleTable), und der
  ResizeObserver feuert beim Ziehen am Fensterrand in jedem Bild. Ohne die
  Sperre ginge dabei über die Hälfte jedes Bildes für Geometrie drauf.

  60 ms bündeln das auf höchstens etwa 16 Neuaufbauten je Sekunde — die Bahn
  folgt dem Ziehen weiterhin sichtbar, kostet dabei aber ein Viertel. Länger
  wäre sparsamer, würde aber als Nachhinken auffallen.
*/
const REBUILD_SETTLE = 60
function scheduleRebuild() {
    clearTimeout(rebuildTimer)
    cancelAnimationFrame(rebuildRaf)
    rebuildTimer = setTimeout(() => {
        rebuildRaf = requestAnimationFrame(() => {
            build()
            if (reduce) renderStatic()
            else render(performance.now())
            ScrollTrigger.refresh()
        })
    }, REBUILD_SETTLE)
}

/*
  Reveals der Stationen — pro Station eine eigene Timeline, damit die Elemente
  gestaffelt kommen und nicht die ganze Seite im selben Takt atmet. Erst die
  Karte, dann Kopf → Text → Eckdaten → Bild, der Streckenpunkt setzt sich
  mittendrin.

  EINE RICHTUNG. Aus Variante C übernommen war ein seitlicher Anteil, dessen
  Vorzeichen mit der Kartenseite wechselte — die Teile liefen also diagonal
  ein, während die Karte selbst senkrecht hochkommt. Bei einer Textzeile fällt
  das kaum auf, bei einer Bildfläche über die volle Kartenbreite sehr wohl: sie
  schob sich sichtbar aus der Ecke. Jetzt steigt alles senkrecht, wie die Karte.

  Und das Bild steigt gar nicht, es blendet nur auf. Es ist die größte Fläche
  in der Karte; wenn es zusätzlich zu den 70 px der Karte noch einmal 26 px in
  seinem eigenen Rahmen wandert, bewegen sich zwei Ebenen unterschiedlich
  schnell übereinander. Text verträgt das, eine Bildfläche nicht.

  WEG UND DECKKRAFT DER KARTE SIND GETRENNT. Zusammen in einem Tween teilen sie
  sich zwangsläufig die Kurve, und `power3.out` ist für einen Weg gedacht: er
  bringt schnell voran und läuft lange aus. Auf die Deckkraft angewandt stand
  die Fläche nach 300 ms bereits bei 66 % — sie war einfach da, und danach
  schob sie sich nur noch. Als eigener Tween mit flacher Kurve über die
  gesamte Dauer blendet sie tatsächlich auf.

  Wichtig für die Geometrie: animiert wird die KARTE und der Punkt IM Knoten,
  nie der Knoten selbst — der ist der Messpunkt der Kurve.
*/
function buildReveals() {
    if (reduce) return
    stationEls.value.filter(Boolean).forEach((el, i) => {
        const card = el.querySelector('[data-card]')
        // Bild getrennt von den Textteilen: es bekommt keinen Weg, nur Deckkraft.
        const rises = gsap.utils.toArray(el.querySelectorAll('[data-rise]:not([data-rise-media])'))
        const media = el.querySelector('[data-rise-media]')
        const dot = el.querySelector('[data-dot]')

        /*
          ANFANGSZUSTAND VORAB, nicht per fromTo.

          fromTo() legt seinen Anfangszustand bei einem Stagger nur auf das
          ERSTE Ziel; die übrigen bleiben unangetastet, bis ihr eigener Versatz
          an der Reihe ist. Solange die Karte selbst noch auf
          `visibility: hidden` steht, fällt das nicht auf — sobald ihr Tween
          startet, wird sie sichtbar und ihre Kinder mit ihr, und zwar an ihrer
          ENDposition mit voller Deckkraft. Wenn dann 0.2 s später der
          Stagger-Tween beginnt, springen sie auf den Anfangszustand zurück und
          laufen erneut ein. Einmal pro Element, gestaffelt — das sah aus wie
          ein Ruckeln.

          Mit einem gsap.set() davor steht alles von Anfang an richtig, und die
          Tweens sind reine .to(). Nebeneffekt: bei einem
          ScrollTrigger-Refresh mitten in der Bewegung nehmen .to()-Tweens den
          aktuellen Stand als Startwert, statt an den Anfang zurückzuspringen.
        */
        if (card) gsap.set(card, { autoAlpha: 0, y: 70 })
        if (dot) gsap.set(dot, { autoAlpha: 0, scale: 0.2 })
        if (rises.length) gsap.set(rises, { autoAlpha: 0, y: 26 })
        if (media) gsap.set(media, { autoAlpha: 0 })

        /*
          Auslöser: Oberkante der Station auf % der Fensterhöhe — für die
          ERSTE Karte ein anderer Wert als für die übrigen.

          Nur die erste hat einen Nachbarn, auf den sie Rücksicht nehmen muss:
          den Bühnenkopf. Gemessen steht ihre Oberkante bei +330 px nach dem
          Abflug schon über der Headline, die dort noch 0,15 Deckkraft hat —
          jeder frühere Auslöser lässt sie den Kopf zudecken. Der Kopf gibt den
          Platz erst bei +345 frei, und dahinter sitzt ihr Wert.

          Die übrigen Karten haben diese Fessel nicht und dürfen deutlich
          früher aufblenden. Der Wert ist im Lauf mehrfach gewandert
          (78 -> 70 -> 55 -> 63 -> 85 -> 55 -> 60); dass er sich nie festlegen
          ließ, lag daran, dass EIN Wert zwei Aufgaben erfüllen sollte.
                */
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: el,
                start: `top ${i === 0 ? REVEAL_ERSTE : REVEAL_REST}%`,
                end: 'bottom top',
                toggleActions: 'play reverse play reverse',
                invalidateOnRefresh: true,
            },
        })
        if (card) {
            tl.to(card, { y: 0, duration: 1, ease: 'power3.out' }, 0)
            tl.to(card, { autoAlpha: 1, duration: 1.2, ease: 'power1.out' }, 0)
        }
        if (dot) {
            tl.to(dot, { autoAlpha: 1, scale: 1, duration: 0.7, ease: 'back.out(2.2)' }, 0.12)
        }
        if (rises.length) {
            tl.to(rises,
                { autoAlpha: 1, y: 0, duration: 0.85, ease: 'power3.out', stagger: 0.09 }, 0.2)
        }
        // Das Bild kommt zuerst — es ist der Anlass der Karte, nicht ihr
        // Nachtrag. Länger und weicher als der Text, damit das Aufblenden einer
        // großen Fläche nicht als Schalten wirkt.
        if (media) {
            tl.to(media, { autoAlpha: 1, duration: 1.1, ease: 'power2.out' }, 0.1)
        }
        if (tl.scrollTrigger) triggers.push(tl.scrollTrigger)

        /*
          KI-Kennzeichnungs-Badge (EU AI Act Art. 50) von der Bild-Oberkante zur
          Unterkante wandern lassen — siehe BADGE_LAUF.

          Ruhelage ist unten rechts im Bild (die Basis-Styles von
          `.media__ai-label` setzen inset-block-end: 0). Der Weg geht deshalb
          von `-strecke` nach 0, nicht umgekehrt: bei y = 0 sitzt das Badge dort,
          wo es ohne jede Animation auch sitzen würde. Fällt die Animation aus
          (reduzierte Bewegung, JS-Fehler), ist die Kennzeichnung also trotzdem
          an ihrem Platz — bei einer Pflicht-Kennzeichnung ist das die
          Anforderung an den Fehlerfall.

          Die Strecke ist eine Funktion, nicht ein Wert: `invalidateOnRefresh`
          lässt sie ScrollTrigger bei jedem Refresh neu ausrechnen. Nötig, weil
          die Bildhöhe am Layout hängt — im Format `media-side` füllt das Bild
          ab l die ganze Kartenhöhe, darunter läuft es im eigenen Verhältnis.

          `ease: 'none'`, weil gescrubbt: jede andere Kurve entkoppelt das Badge
          von der Scrollbewegung und liest sich als Nachlaufen.
        */
        const badge = el.querySelector('.ku-card__ai-label')
        const frame = badge && badge.offsetParent
        if (badge && frame) {
            /*
              Am oberen Ende der Strecke soll dieselbe Luft zur Bildkante stehen
              wie unten in der Ruhelage — sonst klebt das Badge oben am Rand.

              Unten kommt der Abstand aus `padding-block-end` der Basis-Styles
              (das Badge sitzt bündig an der Unterkante, das SVG darin um das
              Padding eingerückt). Oben gibt es kein Gegenstück, also wird die
              Strecke um genau diesen Wert gekürzt. Gelesen statt hingeschrieben:
              ändert sich der Abstand in `_media.scss`, wandert er hier mit,
              statt auseinanderzulaufen.
            */
            const luft = () => parseFloat(getComputedStyle(badge).paddingBlockEnd) || 0
            const strecke = () => Math.max(0, frame.offsetHeight - badge.offsetHeight - luft())
            const lauf = gsap.fromTo(badge,
                { y: () => -strecke() },
                {
                    y: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: el,
                        start: `top ${i === 0 ? REVEAL_ERSTE : REVEAL_REST}%`,
                        end: `top ${Math.max(2, (i === 0 ? REVEAL_ERSTE : REVEAL_REST) - BADGE_LAUF * 100)}%`,
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                })
            if (lauf.scrollTrigger) triggers.push(lauf.scrollTrigger)
        }
    })
}

onMounted(() => {
    // Gradient statt Volltonfarbe: createMorph setzt `color` als fill-Attribut,
    // ein url(#…) ist dort genauso gültig — damit bekommt das Motiv das
    // Glanzlicht der .cta--big-Optik.
    morph = createMorph(morphSvg.value, { color: 'url(#ku-gold)', phases: NOSPIN_PHASES })
    initSheen()
    journeyPlane.value.setAttribute('d', PLANE_D)

    nextTick(() => {
        sammleStationen()
        build()
        lastW = journeyEl.value?.clientWidth || 0
        lastVh = stageEl.value?.offsetHeight || 0
        buildReveals()
        initBoard()
        if (reduce) {
            renderStatic()
        } else {
            render(performance.now())
            raf = requestAnimationFrame(loop)
        }

        /*
          Formie nach-initialisieren. Formies eigenes Script bindet Formulare
          bei DOMContentLoaded — diese Komponente ist aber ein async geladener
          Chunk, ihr Slot-Inhalt (das Formular) steht zu dem Zeitpunkt noch
          nicht im DOM. initForms() ist re-entrant (Guard über formHashId),
          ein doppelter Aufruf ist also harmlos.
        */
        if (window.Formie) {
            window.Formie.initForms()
        } else {
            document.addEventListener('DOMContentLoaded', () => window.Formie?.initForms(), { once: true })
        }
    })

    // Nach Fonts und Bildern nachmessen — Stationshöhen und damit die
    // Stützstellen verschieben sich dabei noch.
    if (document.fonts?.ready) document.fonts.ready.then(scheduleRebuild)
    window.addEventListener('load', scheduleRebuild)
    window.addEventListener('orientationchange', scheduleRebuild)

    ro = new ResizeObserver(() => {
        const w = journeyEl.value?.clientWidth || 0
        const h = stageEl.value?.offsetHeight || 0
        if (w !== lastW || Math.abs(h - lastVh) > 4) {
            lastW = w
            lastVh = h
            scheduleRebuild()
        }
    })
    if (journeyEl.value) ro.observe(journeyEl.value)
    if (stageEl.value) ro.observe(stageEl.value)
})

onBeforeUnmount(() => {
    cancelAnimationFrame(raf)
    cancelAnimationFrame(rebuildRaf)
    clearTimeout(rebuildTimer)
    window.removeEventListener('load', scheduleRebuild)
    window.removeEventListener('orientationchange', scheduleRebuild)
    ro?.disconnect()
    boardIo?.disconnect()
    triggers.forEach((t) => t.kill())
    triggers = []
    morph?.destroy()
})
</script>

<template>
    <div ref="root" class="ku">
        <!-- ── Parallax-Hintergrund (Draufsicht) ────────────────────────── -->
        <!-- Liegt AUSSERHALB von .ku-content: dessen overflow-x: clip würde
             den Klebebereich je nach Engine beschneiden. -->
        <div class="ku-sky" aria-hidden="true">
            <!-- Die Karte. Liegt ganz unten und ist zu Beginn vom Vorhang
                 verdeckt; Deckkraft und Weg setzt renderSky(). -->
            <div ref="chartEl" class="ku-sky__chart" :style="{ backgroundImage: `url(${chartUrl})` }"></div>

            <!-- Die Wolkendecke: ein Feld aus einzelnen Flecken, die sich
                 auflösen. Der warme Grundton (haze) liegt darunter und macht
                 aus den Flecken einen Himmel statt heller Flusen im Schwarz;
                 er geht mit der Decke weg. -->
            <div ref="deckEl" class="ku-sky__deck">
                <div ref="hazeEl" class="ku-sky__haze"></div>
                <span
                    v-for="(p, i) in PUFFS"
                    :key="i"
                    :ref="el => puffEls[i] = el"
                    class="ku-sky__puff"
                    :class="`ku-sky__puff--${p.k}`"
                    :style="{
                        left: `${p.x}%`, top: `${p.y}%`, rotate: `${p.r}deg`,
                        width: `calc(${p.s}% * var(--ku-puff-boost, 1))`,
                    }"
                ></span>
            </div>

            <!-- Licht von UNTERHALB der Decke. Liegt über den Flecken und
                 blendet sich mit ihnen; klebt am Viewport, während die Wolken
                 hindurchziehen. Siehe .ku-sky__glow. -->
            <div class="ku-sky__glow"></div>
        </div>

        <!-- Abdunklung über dem Hintergrund und UNTER dem Inhalt. Die Bühne
             hatte diese Ebenen bisher allein; an ihrer Unterkante entstand
             deshalb eine sichtbare waagerechte Kante, weil darunter dieselben
             Wolken plötzlich ungedämpft weiterliefen. Jetzt gilt dieselbe
             Dämpfung für die ganze Seite — und sie nimmt die Karte gleich mit
             zurück. -->
        <div class="ku-scrim" aria-hidden="true"></div>

        <!-- Vordergrund: die einzige Ebene VOR dem Flugzeug. Liegt deshalb
             über .ku-content und nicht in .ku-sky. Siehe FORE. -->
        <div class="ku-fore" aria-hidden="true">
            <div ref="foreEl" class="ku-fore__field">
                <span
                    v-for="(w, i) in FORE"
                    :key="i"
                    :ref="el => wispEls[i] = el"
                    class="ku-fore__wisp"
                    :class="`ku-fore__wisp--${w.k}`"
                    :style="{
                        left: `${w.x}%`, top: `${w.y}%`, rotate: `${w.r}deg`,
                        width: `calc(${w.s}% * var(--ku-puff-boost, 1))`,
                    }"
                ></span>
            </div>
        </div>

        <!-- Korn und weiche Vignette über der GANZEN Seite, nicht nur der
             Bühne: das hält den Film-Charakter auch in der Strecke. -->
        <div class="ku-film" aria-hidden="true">
            <div class="ku-film__vignette"></div>
            <div class="ku-film__grain"></div>
        </div>

        <div class="ku-content">
            <!-- ── Bühne (Variante 1) ───────────────────────────────────── -->
            <div ref="track" class="ku-track">
                <section
                    ref="stageEl"
                    class="section foreground-light background-expansion-viewport ku-stage"
                >
                    <!-- Kein eigenes Bild mehr: der Himmel der Bühne IST die
                         Wolkendecke der Himmelsebene dahinter. Der Container
                         bleibt als Träger des Scrims (::after), dessen Deckkraft
                         render() während des Morphs zurückfährt. -->
                    <div class="section__bg"></div>

                    <!-- Trägt nur noch den Verlauf: das Morph-SVG referenziert
                         ihn über url(#ku-gold). Gezeichnet wird hier nichts —
                         Bahn und Flugzeug liegen im Routen-Container. -->
                    <svg ref="linesEl" class="ku-stage__lines" aria-hidden="true">
                        <defs>
                            <!-- Bronze-Gradient mit wanderndem Glanzpeak.
                                 Farbstopps 1:1 aus .cta--big (_cta.scss). -->
                            <linearGradient
                                id="ku-gold"
                                ref="goldGrad"
                                x1="0" y1="0" x2="1" y2="0.85"
                                spreadMethod="repeat"
                            >
                                <stop offset="0" stop-color="#B98A58" />
                                <stop offset="0.30" stop-color="#E0BB90" />
                                <stop offset="0.44" stop-color="#F2D9BC" />
                                <stop offset="0.50" stop-color="#FFFBF4" />
                                <stop offset="0.56" stop-color="#F2D9BC" />
                                <stop offset="0.72" stop-color="#D9B389" />
                                <stop offset="1" stop-color="#B98A58" />
                            </linearGradient>
                        </defs>
                    </svg>

                    <!-- createMorph() übernimmt dieses <svg> und hängt seine
                         Gruppe hinein; das leere Element ist Absicht.

                         NICHT aria-hidden: die Wortmarke ist das einzige, was
                         den Eventnamen auf der Seite ausspricht — die h1
                         darunter sagt „Der erste Beauty-Airliner der Welt. by
                         DR. RICK & DR. NICK", nie „Beauty Airways". Versteckt
                         hörte ein Screenreader den Namen hier nirgends.

                         Das Label ist der wörtliche Text der Grafik und
                         deshalb hartkodiert statt aus dem CMS: die Wortmarke
                         zeichnet diese Komponente selbst (logo-plane-morph),
                         sie ist in jeder Sprache dieselbe und keine Redaktion
                         kann sie ändern.

                         role="img" trägt auch, solange das Element noch leer
                         ist. Dass es später zum Flugzeug morpht, lassen wir
                         unbeschriftet durchlaufen — der Morph ist Übergang,
                         der Gegenstand bleibt die Wortmarke. -->
                    <svg
                        ref="morphSvg"
                        class="ku-stage__morph"
                        role="img"
                        aria-label="Beauty Airways by Aesthetify"
                    ></svg>

                    <!-- Kino-Ebenen, 1:1 aus der Scroll-Stage der Startseite. -->
                    <div class="ku-stage__edge-frame"></div>

                    <!-- Name und Absender stehen in der Wortmarke darüber;
                         hier nur, was sie nicht sagt. -->
                    <div ref="copyEl" class="ku-stage__copy">
                        <!-- Headline und Byline kommen als Slot-Inhalt aus dem
                             Twig, damit die h1 im ausgelieferten HTML steht.
                             Der animierte Ref bleibt am Wrapper darum. -->
                        <slot name="stage"></slot>
                    </div>

                    <!-- Der Scroll-Hinweis der Startseite, unverändert
                         übernommen (sections/_scroll-stage.scss): Text plus
                         herabtropfende Haarlinie. Die Klassen sind global,
                         nur die Farbvariable hängt an .section-scroll-stage —
                         die setzt .ku-stage deshalb selbst. -->
                    <div ref="hintEl" class="section-scroll-stage__hint">
                        {{ stage.hint }}
                        <div class="section-scroll-stage__hint-line"></div>
                    </div>
                </section>
            </div>

            <!-- ── Strecke (Variante C) an der Flugbahn (Variante 1) ────── -->
            <!-- Alle Stationen liegen in EINEM Positionierungskontext, damit
                 die Bahn sie durchgehend verbinden kann. Eine Sektion pro
                 Station würde die Kurve an jeder Sektionsgrenze abschneiden. -->
            <section class="section foreground-light ku-journey">
                <div ref="journeyEl" class="ku-route-wrap">
                    <!-- Die Bahn ragt oben um eine halbe Bühnenhöhe aus dem
                         Container heraus (Handoff-Punkt) -> overflow: visible,
                         kein viewBox: Nutzereinheiten sind CSS-px. -->
                    <svg class="ku-route-wrap__lines" aria-hidden="true">
                        <!-- Schiebt alles um den Überhang nach unten, um den
                             der Kasten nach oben verlängert ist. Dadurch
                             bleiben die Koordinaten im Skript
                             Container-Koordinaten. Siehe __lines. -->
                        <g ref="linesGroup">
                            <path ref="routePath" class="ku-route" />
                            <!-- Der Schimmer: zwei breite schwache Striche
                                 unter der Haarlinie, statt eines drop-shadow
                                 an ihr. Siehe trailGlowWide. -->
                            <path ref="trailGlowWide" class="ku-trail-glow ku-trail-glow--wide" />
                            <path ref="trailGlowNarrow" class="ku-trail-glow ku-trail-glow--narrow" />
                            <path ref="trailPath" class="ku-trail" />
                            <!-- Abflugpunkt. Liegt auf derselben Kurve wie
                                 alles andere und scrollt deshalb einfach mit.
                                 Vor dem Flugzeug, damit dieses beim Start
                                 darüberzieht. -->
                            <circle ref="originRing" class="ku-origin__ring" />
                            <circle ref="originCore" class="ku-origin__core" />
                            <path ref="journeyPlane" class="ku-plane" />
                        </g>
                    </svg>

                    <!-- Die Stationen kommen als Slot-Inhalt aus dem Twig
                         (_/pages/beauty-airways.twig): damit stehen sie im
                         ausgelieferten HTML statt nur im page-Attribut.

                         Diese Komponente rendert sie nicht mehr, sie MISST sie:
                         .ku-station liefert die Stuetzstellen der Spline,
                         [data-card]/[data-rise]/[data-dot] die Ziele der
                         Reveal-Timelines. Die Klassennamen sind der Vertrag mit
                         dem Template — siehe den Kommentar dort. -->
                    <slot name="stations"></slot>

                    <!-- Endpunkt. Gehört bewusst noch in den Routen-Container:
                         die Bahn muss auf dem Ring auslaufen, nicht an einer
                         Sektionskante abbrechen. -->
                    <div class="ku-terminus">
                        <i ref="terminusNode" class="ku-node ku-node--end" aria-hidden="true">
                            <b class="ku-node__ring"></b>
                        </i>
                        <div class="ku-terminus__copy">
                            <!-- Ueberschrift aus dem Twig; die Fallblattanzeige
                                 darunter bleibt Mechanik dieser Komponente. -->
                            <slot name="terminus"></slot>
                        </div>

                        <!-- Eckdaten als Fallblattanzeige, Bauart aus
                             Variante A (dort ist sie die Bühne, hier eine
                             kleine Tafel am Ende). Das Zeichenraster ist
                             aria-hidden, der lesbare Text steht daneben. -->
                        <!-- Ohne gepflegte Zeilen bliebe sonst ein leerer,
                             gerahmter Kasten stehen: initBoard() steigt bei
                             null Zellen aus, bevor is-ready gesetzt wird. -->
                        <div
                            v-if="boardRows.length"
                            ref="boardEl"
                            class="ku-board"
                            :class="{ 'is-ready': boardReady }"
                            :style="{ '--cols': COLS }"
                        >
                            <div class="ku-board__frame">
                                <div class="ku-board__head">
                                    <span>{{ labels.flightData }}</span>
                                    <span>Beauty Airways</span>
                                </div>

                                <div class="ku-board__rows" aria-hidden="true">
                                    <div v-for="r in boardRows.length" :key="r" class="ku-row">
                                        <span v-for="c in COLS" :key="c" class="ku-flap">
                                            <span class="ku-flap__leaf">&nbsp;</span>
                                        </span>
                                    </div>
                                </div>

                                <p class="ku-board__sr">{{ terminus.rows.join('. ') }}.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ku-contact ist der Angriffspunkt für den Schleier weiter unten:
                 auf `.kc` zu zielen hieße, jede Klasse dieses Namens zu treffen —
                 auch eine aus einem CKEditor-Text. -->
            <BoardingPass class="ku-contact">
                <!-- Botschaft, Strecke und Hinweis kommen wie das Formular aus
                     dem Twig und werden hier nur durchgereicht. Die Bordkarte
                     kennt keinen dieser Texte mehr. -->
                <template v-if="$slots.contact" #aside><slot name="contact" /></template>
                <template v-if="$slots.form" #form><slot name="form" /></template>
                <template v-if="$slots['contact-note']" #note><slot name="contact-note" /></template>
            </BoardingPass>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use "@/scss/framework" as *;

/* ── Seitengerüst ────────────────────────────────────────────────────────
   Himmel, Filmkorn und Inhalt liegen in DERSELBEN Grid-Zelle übereinander.
   Damit ist der Klebebereich der beiden Ebenen exakt die Höhe des Inhalts —
   sie hören an der Unterkante des Inhalts von selbst auf und decken den
   Footer nicht zu. */
.ku {
    // #050505 wie --scroll-stage-bg der Startseiten-Bühne, NICHT das hellere
    // --color-dark-marble-1 der normalen Sektionen.
    --ku-black: #050505;

    /*
      MASSSTAB DER WOLKEN auf schmalen Breiten.

      Die Flecken sind in Prozent ihrer Ebene bemessen, ihr Sprite ist aber ein
      festes 400-px-Raster mit fester Turbulenzfrequenz. Auf breiten Fenstern
      wird er dadurch auf rund 969 px AUFgeblasen und wirkt weich; auf schmalen
      landet er bei 356 px, wird also verkleinert — und dann zeigt sich die
      feine Turbulenz als Gekrissel statt als Wolke.

      1,8 war der erste Versuch und der falsche Hebel: die Kanten wurden weich,
      aber die Bewegung ging verloren — eine große weiche Fläche zeigt beim
      Vorbeiziehen kaum noch etwas, das Bild wirkte wie eine Fotografie. Den
      Maßstab holt jetzt die gröbere Turbulenz der schmalen Sprites (siehe
      $wolken-sprite-0-grob). Hier bleibt nur ein kleiner Zuschlag, damit die
      Flecken nicht kleiner sind als die Strukturen darin.

      Die Unschärfe der Vordergrundschwaden wird mitskaliert.
    */
    --ku-puff-boost: 1.2;

    /*
      Weg der Kartenebene in Bildhöhen (siehe build()). Schmal flach, damit von
      der Karte überhaupt etwas zu sehen ist: 0,12 bringt den sichtbaren Anteil
      ihrer Breite von 31 auf rund 45 %. Breit bleibt es beim vollen Weg.
    */
    --ku-chart-travel: 0.12;

    /*
      Weg des Vordergrunds in Bildhöhen (siehe build()). Mobil knapp die
      Hälfte: die Höhe der Ebene ist dieser Weg, und sie war mit 3749 px die
      größte promovierte Ebene nach dem Dokument. Der Preis ist weniger
      Parallax auf den vier Fetzen, die dort ohnehin ohne Weichzeichnung und
      mit 5 bis 8 % Deckkraft laufen.
    */
    --ku-fore-travel: 1.6;
    --ku-hair: color-mix(in srgb, var(--color-white-sand-1) 15%, transparent);
    --ku-hair-gold: color-mix(in srgb, var(--color-cashmere-1) 32%, transparent);

    position: relative;
    display: grid;
    grid-template-columns: 100%;
    background: var(--ku-black);

    > * { grid-area: 1 / 1; }

    @include mq-from(l) {
        --ku-puff-boost: 1;
        --ku-chart-travel: 0.66;
        --ku-fore-travel: 3.4;
    }
}

.ku-film {
    position: sticky;
    top: 0;
    align-self: start;
    height: 100vh;
    height: 100svh;
    z-index: 3;
    pointer-events: none;

    &__vignette,
    &__grain {
        position: absolute;
        inset: 0;
    }

    &__vignette {
        background: radial-gradient(ellipse at center, transparent 52%, rgb(0 0 0 / 45%) 100%);
    }

    // Korn wie _scroll-stage.scss: fractalNoise, entsättigt, 300px-Kachel,
    // overlay-Blend bei 4,5 % — sichtbar nur als Textur, nie als Schleier.
    &__grain {
        opacity: 0.045;
        mix-blend-mode: overlay;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        background-size: 300px 300px;
    }
}

/* Abdunklung über dem Hintergrund, unter dem Inhalt. Bisher lagen Vignette und
   Kino-Rahmen NUR in der Bühne; an deren Unterkante brach die Dämpfung deshalb
   schlagartig ab, während dieselben Wolken darunter ungedämpft weiterliefen —
   das war die harte waagerechte Kante. Als eigene, klebende Ebene gilt sie für
   die ganze Seite, und sie nimmt die Karte gleich mit zurück. */
.ku-scrim {
    position: sticky;
    top: 0;
    align-self: start;
    height: 100vh;
    height: 100svh;
    z-index: 1;
    pointer-events: none;
    background:
        radial-gradient(ellipse 130% 95% at 50% 42%, transparent 20%, rgb(0 0 0 / 66%) 100%),
        linear-gradient(rgb(0 0 0 / 46%), rgb(0 0 0 / 46%));
}

.ku-content {
    position: relative;
    z-index: 2;
    /*
      HIER KEIN overflow. An dieser Stelle stand ein `overflow-x: clip`, und
      das war die Ursache eines sichtbaren Zuckens auf iOS.

      Ein clippender Vorfahr macht sich zum Scrollport des klebenden
      .ku-stage darin, und WebKit führt `position: sticky` dann auf dem MAIN
      THREAD nach statt im Compositor. Weil iOS die Scrollposition nur auf
      rund einem Drittel der Frames und nur in ganzen Pixeln meldet (gemessen:
      68 % der Frames ohne Änderung, Schübe bis 24 px), zog die ganze Bühne
      dabei um einen Pixel hin und her. Sichtbar war das an Logo und
      Bühnen-Copy; am Himmel fällt es nicht auf, weil er keine Kanten hat.

      Am Gerät nachgemessen: mit clip zuckt es, ohne clip nicht — und die
      Bühne bleibt in beiden Fällen sticky. Aus JS ist davon nichts zu sehen,
      die Positionen von Logo und Copy stehen über 656 Frames auf zwei
      Dezimalstellen still; das Zucken entsteht erst beim Rastern.

      Die Zeile fehlt hier auch ohne Ersatz nicht: der waagerechte Überlauf
      bleibt null (scrollWidth == clientWidth bei 393 und bei 1440 px über die
      ganze Seite), weil die breiten Ebenen .ku-sky und .ku-fore ausserhalb
      von .ku-content liegen und selbst kappen. Die Kappung für den Inhalt
      sitzt jetzt an .ku-journey — dort liegt der breite Inhalt, und es ist
      KEIN Vorfahr der Bühne.
    */
}

/* Die beiden Wolken-Sprites. Als Variablen, weil sie an zwei Stellen gebraucht
   werden: in der Decke hinter dem Flugzeug und im Vordergrund davor. Dieselbe
   Turbulenz in beiden Ebenen ist Absicht — es ist derselbe Himmel, nur in
   verschiedener Entfernung. Die ausführliche Beschreibung der Filterkette steht
   bei .ku-sky__puff.

   VORGERENDERTE BITMAPS, keine SVG-Data-URIs mehr.

   Die Filterkette stand früher direkt hier und wurde vom Browser bei jeder
   Rasterung neu gerechnet. Chromium legt das Ergebnis in den Bild-Cache,
   WebKit nicht — auf einem iPhone war die Seite dadurch unbedienbar. Gemessen
   am angeschlossenen Gerät über das Web-Inspector-Protokoll, beim Scrollen:

       mit SVG-Filter              4,0 FPS   p95 267 ms   100 % Frames > 32 ms
       mit vorgerendertem Bitmap  59,3 FPS   p95  17 ms     0 %
       ohne Hintergrund           59,5 FPS   p95  17 ms     0 %

   Die letzten zwei Zeilen sind der Beweis: ein Bitmap kostet so viel wie GAR
   KEIN Hintergrund. Es lag also nicht an Fläche, Anzahl oder Deckkraft der
   Flecken, sondern ausschliesslich an der Filterkette.

   Die Bilder entstehen aus genau diesen Filtern —
   app/scripts/make-beauty-airways-clouds.mjs rechnet sie einmal aus und legt
   sie hier daneben. Optik unverändert, nur der Zeitpunkt der Berechnung.
   Filterwerte ändern heisst: im Skript ändern, Skript laufen lassen, erzeugte
   Dateien mitcommitten.

   Pro Viewport werden nur ZWEI der vier Dateien geladen: `grob` unter l,
   `fein` darüber. Ein background-image in einer nicht passenden Media Query
   holt der Browser nicht.

   Das Film-Korn weiter oben bleibt bewusst ein SVG-Filter: gemessen kostet es
   nichts (ohne .ku-film 0 % Gewinn). Es liegt statisch über der Seite, wird
   nicht transformiert und daher einmal gerastert. */
$wolken-sprite-0: url("./wolke-fein-0.webp");
$wolken-sprite-1: url("./wolke-fein-1.webp");

/* Dieselben Sprites mit GRÖBERER Turbulenz, für schmale Breiten.

   Die Frequenz im Filter ist fest, die Fläche eines Flecks aber nicht: breit
   wird der 400-px-Raster auf rund 970 px aufgeblasen, schmal landet er bei 356.
   Dieselbe Turbulenz ergibt dort viermal so viele Strukturen je Fläche — aus
   Wolken wird Gekrissel.

   Die Flecken deshalb größer zu ziehen war der falsche Weg (ausprobiert): es
   nimmt die Kanten, kostet aber die Bewegung, weil eine große weiche Fläche
   beim Vorbeiziehen kaum noch etwas zeigt. Stattdessen wird die FREQUENZ
   gesenkt — Faktor 1,9, also genau der Maßstabsunterschied. Größe, Anzahl und
   Parallax bleiben damit unangetastet. */
$wolken-sprite-0-grob: url("./wolke-grob-0.webp");
$wolken-sprite-1-grob: url("./wolke-grob-1.webp");

/* Die Vordergrund-Schwaden: dieselben feinen Motive, aber mit der Unschärfe
   SCHON DRIN. Sie hatten sie bis hierher als Ebenen-Filter, und das war der
   teuerste Posten der Seite (Begründung und Umrechnung der Werte stehen an
   SCHWADEN_BLUR in app/scripts/make-beauty-airways-clouds.mjs). */
$wolken-schwade-0: url("./wolke-schwade-0.webp");
$wolken-schwade-1: url("./wolke-schwade-1.webp");

/* ── Parallax-Hintergrund ──────────────────────────────────────────────── */
.ku-sky {
    // sticky, NICHT fixed: ein fixed-Element hängt am Viewport und malt auch
    // über alles, was NACH dem Inhalt kommt (der Footer verschwände
    // dahinter). Als Grid-Geschwister von .ku-content ist der Klebebereich
    // genau dessen Höhe.
    position: sticky;
    top: 0;
    align-self: start;
    height: 100vh;
    height: 100svh;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
    background: var(--ku-black);
    // Der Schein (__glow) blendet sich mit dem, was unter ihm liegt. Diese
    // Ebene ist als klebendes Element mit z-index bereits ein eigener
    // Stapelkontext, der ihn einfängt — hier trotzdem ausgeschrieben, damit
    // eine spätere Änderung an position/z-index das nicht versehentlich löst.
    isolation: isolate;

    /* Die Karte. Waagerechter Überhang, damit der Drift (bis ~1 % Amplitude)
       nie eine Kante der Ebene in den Viewport schiebt; senkrecht läuft sie
       über eine Maske aus, damit die Ebenenkante beim Parallax nie sichtbar
       wird. Deckkraft kommt aus renderSky() — im Ruhezustand ist sie fast
       unsichtbar, sie kommt mit dem Vorhang herauf. */
    &__chart {
        position: absolute;
        left: -8%;
        top: 0;
        width: 116%;
        transform-origin: 50% 30%;
        will-change: transform, opacity;
        background-position: 50% 35%;
        background-size: cover;
        background-repeat: no-repeat;
        /*
          KEIN FILTER MEHR. Eine Spur Unschärfe ist hier das Entscheidende
          gegen den Eindruck einer Konstruktionszeichnung — aus scharfen
          Strichen werden weiche Spuren, und die Karte liest sich als Gelände
          aus großer Höhe statt als Grafik. Sie sorgt außerdem dafür, dass die
          einzige wirklich scharfe Linie auf der Seite die Flugbahn bleibt.

          Sie steckt jetzt IM BILD (chart.webp, siehe chartUrl und
          make-beauty-airways-chart-bitmap.mjs) statt als `filter: blur(1.5px)`
          auf der Ebene. Der Grund ist derselbe wie bei den Wolkensprites und
          den Vordergrund-Schwaden, hier nur besonders scharf:

          Diese Ebene ist Viewport + Parallaxweg groß. Gemessen wächst ihr
          Filterpuffer bei DPR 2 mit der Fensterbreite — 10,5 Mpx auf 1512
          CSS-px, 16,2 auf 1840, 21 auf 2560. WebKit deckelt Filterpuffer bei
          rund 4096x4096 = 16,7 Mpx, und was es nicht anlegen kann, zeichnet es
          gar nicht. Ab etwa 1900 px Fensterbreite lag die Karte also auf der
          Kante — genau die Größenordnung, in der die Bühne in Safari
          verschwand.

          Nebenbei fällt das Rastern von 584 Vektorpfaden weg. Der frühere
          Deckel `@include mq-from(l)` ist damit gegenstandslos: mobil war er
          nötig, weil die Weichzeichnung dort 30 % der Frame-Zeit kostete —
          jetzt kostet sie überall nichts.
        */
        opacity: 0.04;
        mask-image: linear-gradient(to bottom, transparent, #000 7%, #000 93%, transparent);
    }

    /* ── Wolkendecke ─────────────────────────────────────────────────────
       Die Deckfläche trägt Drift und Parallax für das ganze Feld: EINE
       Transformation pro Frame statt zwanzig. */
    &__deck {
        position: absolute;
        left: -8%;
        top: 0;
        width: 116%;
        transform-origin: 50% 30%;
        will-change: transform;
    }

    /* Warmer Grundton unter den Flecken. Ohne ihn wären es helle Flusen im
       Schwarz; mit ihm ist es ein Himmel. Der Verlauf ist unten wärmer —
       das ist die Richtung, aus der das Licht kommt. */
    &__haze {
        position: absolute;
        inset: 0;

        /*
          KEIN `will-change`. Der Gedanke stand hier schon richtig — der Haze
          liegt deckungsgleich als einziges Kind im Deck, die Angabe
          promovierte ihn also zu einer ZWEITEN Ebene in Deck-Größe —, die
          Angabe blieb aber ab l trotzdem stehen. Auf Desktop ist genau das
          teuer: gemessen bei 1512x900 und DPR 2 sind es 1881x1786 CSS-px, also
          51 MB Backing-Store für eine Ebene, die nur einen Verlauf trägt.
          Ohne sie lag die Frame-Zeit p95 bei 9,0 statt 16,7 ms.

          Seine Deckkraft wird weiterhin animiert (renderSky); dafür promoviert
          der Browser von selbst, dauerhaft stehen muss die Ebene nicht.
        */
        /* Deutlich zurückgenommen. Der Grundton liegt UNTER den Flecken und
           liest sich als heller Himmel zwischen ihnen — er hellt das Bild
           damit genauso auf wie die Flecken selbst, nur flächig. An den
           Flecken allein zu drehen half deshalb nur zur Hälfte. */
        background:
            radial-gradient(110% 70% at 46% 82%, color-mix(in srgb, #ffb066 16%, transparent), transparent 58%),
            radial-gradient(90% 60% at 78% 26%, color-mix(in srgb, var(--color-cashmere-1) 7%, transparent), transparent 64%),
            linear-gradient(to bottom, #160d05 0%, #100a05 44%, #080604 76%, var(--ku-black) 100%);
    }

    /* Ein Wolkenfleck. Turbulenz mit radial auslaufender Maske: die Form hat
       keine Kante und keine Blickrichtung — genau deshalb liest sie sich als
       Draufsicht. Der Sprite wird je Größe EINMAL gerastert, danach werden nur
       noch `opacity` und `scale` verändert.

       WOLKE STATT RAUCH. Die erste Fassung las sich als Schleier: durchgehend
       halbdurchlässig, feinfransig, ohne Lücken. Drei Stellschrauben in den
       Sprites, alle in dieselbe Richtung:

       - `numOctaves` 5 -> 3. Die beiden feinsten Oktaven sind die Fransen; sie
         tragen kaum Fläche, machen aber den Rauchcharakter aus. Übrig bleiben
         die großen Lappen.
       - Die Alphaflanke in der feColorMatrix ist fast doppelt so steil (6,4 ->
         10,5 bzw. 5,6 -> 9,2), und ihr Mittelpunkt liegt etwas höher. Steiler
         heißt: Wolke oder Lücke statt eines langen Übergangs; höher heißt
         weniger Deckung, also mehr dunkler Himmel dazwischen. Der Versatz ist
         jeweils mitgezogen (k · Mittelpunkt − 0,5), sonst verschöbe sich die
         Deckung mit der Steilheit.
       - Weniger Weichzeichnung (2 -> 1,3 bzw. 3 -> 2) und eine straffere
         Maske: der Fleck hat bis weiter außen volle Deckung und läuft erst im
         letzten Drittel aus.

       VOLUMEN. Das reichte nicht: die Kontur stimmte, aber jeder Fleck war
       weiterhin eine flache Alphamaske EINER Farbe. Die Turbulenz sagte nur,
       WO Wolke ist, nicht wie hell sie dort ist — was fehlte, war Licht und
       Schatten im Inneren. Deshalb rechnet die Filterkette jetzt zweimal mit
       derselben Turbulenz:

       1. Als HÖHENFELD (Rotkanal -> Alpha, den feDiffuseLighting liest). Es
          wird vorher weichgezeichnet, und das ist der entscheidende Schritt:
          auf dem scharfen Feld saßen die Lichtkanten auf den Rändern der
          Masse, das Ergebnis sah aus wie geäderter Marmor. Weichgezeichnet
          modelliert das Licht die großen Ballungen.
       2. Als MASSE (die steile Alphaflanke von oben), mit der das beleuchtete
          Ergebnis anschließend ausgestanzt wird.

       Der Lichteinfall ist flach (30 bzw. 34 Grad) und kommt aus derselben
       Richtung wie der Schein unter der Decke (Azimut um 90 Grad, also von
       unten) — flach, weil erst ein streifender Winkel die Kuppen modelliert.

       FARBTRENNUNG. Ein warmes Licht allein ergab einen monochrom braunen
       Brei. Über feFlood kommt deshalb ein KALTER Umgebungsanteil dazu, der
       additiv auf das diffuse Licht gerechnet wird: beschienene Kuppen warm,
       abgewandte Seiten kühl blaugrau. Das ist das, was einen Himmel schön
       macht, nicht die Helligkeit.

       `rotate` und `scale` sind eigenständige CSS-Eigenschaften und keine
       transform-Funktionen: so kann das Template die Drehung setzen und das
       Skript die Größe, ohne sich gegenseitig zu überschreiben. */
    &__puff {
        position: absolute;
        translate: -50% -50%;
        aspect-ratio: 1;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        pointer-events: none;
    }

    /*
      LICHT VON UNTEN, auf einen Bereich des Viewports begrenzt.

      Der Trick ist die Blendart. `overlay` rechnet bei dunklem Grund 2·b·s:
      wo nichts liegt (b = 0, der schwarze Himmel), bleibt es schwarz; wo eine
      Wolke mit b = 0,15 steht, kommt gut das Doppelte heraus. Der Schein
      trifft also die Wolken und nur sie — `screen` oder `plus-lighter` hätten
      die ganze Fläche aufgehellt und einen sichtbaren Fleck im Schwarz
      hinterlassen.

      Weil die Ebene am Viewport klebt und der Schein `inset: 0` hat, steht er
      im Bild still, während die Wolken durch ihn hindurchziehen — eine
      Lichtquelle unter der Decke, an der die Decke vorbeizieht. Er braucht
      dafür keine eigene Animation und keine Zeile JavaScript.

      Er erlischt auch von selbst: löst sich die Decke auf, ist nichts mehr da,
      worauf `overlay` wirken könnte.

      Die Farbe muss über 0,5 liegen, sonst dunkelt `overlay` ab statt
      aufzuhellen — daher der helle Bernstein. Blau liegt knapp darüber und
      bleibt fast unverändert, Rot verdoppelt: das Licht ist warm.
    */
    &__glow {
        position: absolute;
        inset: 0;
        mix-blend-mode: overlay;
        /*
          Mitte auf der Unterkante: sichtbar ist nur die obere Hälfte der
          Ellipse, das Licht kommt also von unterhalb des Bildes. Mehr als eine
          Verdoppelung kann `overlay` nicht (2·b·s bei s = 1), die Stärke
          steckt deshalb in der Fläche, nicht im Kern.
        */
        background: radial-gradient(
            72% 48% at 50% 100%,
            rgb(255 202 146),
            rgb(255 178 110 / 44%) 42%,
            transparent 76%
        );
    }

    &__puff--0 { background-image: $wolken-sprite-0-grob; }

    &__puff--1 { background-image: $wolken-sprite-1-grob; }

    @include mq-from(l) {
        .ku-sky__puff--0 { background-image: $wolken-sprite-0; }
        .ku-sky__puff--1 { background-image: $wolken-sprite-1; }
    }
}

/* Abflugpunkt: dieselbe Formsprache wie der Zielring (.ku-node__ring), nur
   kleiner. Die Maße stehen im Skript, weil das Bühnen-SVG keine viewBox hat
   und in Pixeln rechnet. */
.ku-origin {
    &__ring {
        fill: none;
        stroke: var(--ku-hair-gold);
        stroke-width: 1;
    }

    &__core {
        fill: var(--color-cashmere-1);
    }
}

/* ── Vordergrund ────────────────────────────────────────────────────────
   Dieselbe Bauart wie .ku-sky, nur auf der anderen Seite des Flugzeugs.

   z-index 3 ist derselbe Wert wie beim Film — und das ist gewollt: Korn und
   Vignette gehören über ALLES, auch über den Vordergrund. Bei gleichem
   z-index entscheidet die Reihenfolge im DOM, und der Film steht danach. */
.ku-fore {
    position: sticky;
    top: 0;
    align-self: start;
    height: 100vh;
    height: 100svh;
    z-index: 3;
    overflow: hidden;
    pointer-events: none;

    /* Trägt den Parallaxweg für alle Schwaden: EINE Transformation pro Frame.
       Die Höhe (Viewport + Weg) setzt build(). */
    &__field {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;

        /*
          `will-change` erst ab l. Die Ebene ist so hoch wie ihr Scrollweg
          (--ku-fore-travel), auf einem Telefon-Viewport gemessen 393 x 3749 —
          sie dauerhaft zu promovieren kostet dort mehr, als das Vermeiden
          eines Promotion-Rucklers bringt. Ohne die Angabe promoviert der
          Browser beim Transform von selbst.
        */
        @include mq-from(l) {
            will-change: transform;
        }
    }

    /* Deckkraft kommt ausschließlich aus renderSky() — im Ruhezustand und
       außerhalb des Zeitfensters ist die Ebene unsichtbar. Der Startwert 0
       verhindert, dass sie vor dem ersten Frame kurz aufblitzt. */
    /*
      KEIN `will-change` UND KEIN FILTER MEHR — die beiden zusammen waren der
      teuerste Posten der ganzen Seite, auf Telefonen wie auf dem Desktop.

      Vorher stand hier `will-change: transform, opacity` plus ab l ein
      `filter: blur(var(--ku-wisp-blur))` von 9 bis 15 px. Gemessen bei
      1512x900 und DPR 2 hielten die vier Fetzen damit 838 MB Backing-Store,
      mit Filterpuffern von 18 bis 40 Megapixel je Fetzen. WebKit deckelt
      Filterpuffer bei rund 4096x4096 = 16,7 Mpx — alle vier lagen darüber, und
      was WebKit nicht anlegen kann, zeichnet es NICHT. Genau daran verschwand
      die Bühne in Safari.

      In Chromium gemessen (rAF-getriebener Scroll durch die Reise, p95 der
      Frame-Zeit): 22,8 ms wie ausgeliefert, 9,3 ms sobald einer der beiden
      Posten wegfiel. Der Rest der Seite lag ohne sie bei 9,2 ms — die Fetzen
      allein trugen also den ganzen Überhang.

      Die Unschärfe ist deshalb IN DIE SPRITES GEWANDERT (siehe
      $wolken-schwade-0). Optisch ist es derselbe Gauss, nur einmal gerechnet
      statt pro Frame; die Fetzen laufen ohnehin mit 5,5 bis 8,5 % Deckkraft.

      Ohne `will-change` promoviert der Browser bei Bedarf selbst — und weil
      hier keine eigene Textur mehr nötig ist, malen die Fetzen jetzt in die
      Kacheln von .ku-fore__field, die am Viewport beschnitten sind. Deshalb
      kostet ihre GRÖSSE auch nichts mehr: sie durfte unangetastet bleiben.
    */
    &__wisp {
        position: absolute;
        translate: -50% -50%;
        aspect-ratio: 1;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        opacity: 0;
    }

    /* Unter dem Umbruch liefen die Fetzen schon vorher ohne Ebenen-Blur; dort
       bleiben deshalb die groben Sprites, wie sie sind. */
    &__wisp--0 { background-image: $wolken-sprite-0-grob; }
    &__wisp--1 { background-image: $wolken-sprite-1-grob; }

    @include mq-from(l) {
        .ku-fore__wisp--0 { background-image: $wolken-schwade-0; }
        .ku-fore__wisp--1 { background-image: $wolken-schwade-1; }
    }
}

/* ── Bahn und Flugzeug: EINE Formsprache in beiden Koordinatensystemen ─── */
.ku-route {
    fill: none;
    stroke: var(--color-cashmere-1);
    stroke-width: 1;
    opacity: 0.15;
}

.ku-trail {
    fill: none;
    stroke: var(--color-cashmere-1);
    stroke-width: 1;
    stroke-linecap: round;
}

/*
  DER SCHIMMER, OHNE FILTER.

  Der geflogene Teil braucht auf dem Parallax-Himmel mehr Stand als auf
  Schwarz. Das war ein `drop-shadow(0 0 6px …/45%)` an .ku-trail — und damit
  der teuerste Strich der Seite: der Dashoffset dieses Pfades ändert sich in
  JEDEM Frame, und jede Änderung rechnet den kompletten Filterbereich neu.
  Gemessen ist seine Bounding-Box 344x3444 CSS-px; auf einem Retina-Schirm
  also ein Puffer von 688x6888 = 4,7 Megapixel, sechzigmal je Sekunde. In
  Chromium lag die Frame-Zeit p95 dadurch bei 16,7 statt 9,0 ms.

  Zwei zusätzliche Strokes bilden denselben Abfall nach: 5 px bei 9 % und
  2,5 px bei 20 %, beide unter der vollen Haarlinie. Ein Stroke ist reine
  Malarbeit ohne Zwischenpuffer — die Kosten sind nicht messbar. Sie hängen
  am selben Dashoffset wie die Haarlinie (siehe gezeichnet()), damit an der
  Zeichenfront nicht zwei Enden auseinanderlaufen.
*/
.ku-trail-glow {
    fill: none;
    stroke-linecap: round;

    &--wide {
        stroke: color-mix(in srgb, var(--color-cashmere-1) 9%, transparent);
        stroke-width: 5;
    }

    &--narrow {
        stroke: color-mix(in srgb, var(--color-cashmere-1) 20%, transparent);
        stroke-width: 2.5;
    }
}

.ku-plane {
    fill: var(--color-cashmere-1);
    filter: drop-shadow(0 0 12px color-mix(in srgb, var(--color-dark-marble-1) 70%, transparent));
    will-change: transform, opacity;
}


.ku-hair {
    display: block;
    height: 1px;
    background: linear-gradient(to right, var(--ku-hair-gold), var(--ku-hair) 42%, transparent);
}

/* ── Bühne (Variante 1) ─────────────────────────────────────────────────── */
.ku-track {
    position: relative;
    /*
      Scrollstrecke der Bühne: Morph + Startbogen. MORPH_END teilt sie.

      Diese Höhe ist die EINZIGE Schraube dafür, wann die erste Karte ins Bild
      kommt. Die Bühne klebt (sticky) und füllt dabei das Fenster — solange
      steht davor nichts anderes zu sehen, ganz gleich, was in ihr passiert.
      Die Karte darunter im Fluss zieht nicht nach oben, wenn die Bühne sich
      bewegt; sie beginnt zu erscheinen, sobald der Klebebereich endet, und das
      ist genau hier festgelegt.

      320 -> 300 vh holt die Karte rund eine Fünftel Bildhöhe früher herein.
      Der Preis ist gleichmäßig verteilt: alles in der Bühne läuft ein Zehntel
      schneller, Morph wie Startbogen.
    */
    height: 300vh;
}

.ku-stage {
    /*
      Ab wann die Copy ausblendet. 1.0 ist das Ende des Pins; darüber hinaus
      zählt der Weg in Bühnenhöhen weiter (siehe render()). Die Ausblendstrecke
      beträgt immer 0.14.

      Der Wert zählt in BILDHÖHEN SCROLLWEG SEIT DEM ABFLUG (siehe
      COPY_FADE). Gemessen erreicht das Flugzeug die Höhe des Kopfes rund 340
      px nach dem Abflug; die Copy soll kurz davor verschwunden sein, also
      endet ihr Ausblenden bei 0,38 Bildhöhen und beginnt 0,09 früher.
    */
    --ku-copy-fade: 0.29;

    @include mq-from(l) {
        --ku-copy-fade: 0.29;
    }

    /*
      Maße der Wortmarke als Token, weil nicht nur sie selbst sie braucht: der
      Kopf darunter beginnt an ihrer Unterkante, und die ist ohne diese Zahlen
      nicht auszurechnen. Die Höhe folgt aus dem Seitenverhältnis der viewBox
      (627.49 × 105.58) — das SVG hat `height: auto`, seine Kastenhöhe ist also
      exakt dieser Quotient.

      Die dritte Grenze, 110svh, deckelt die Wortmarke über die HÖHE: sie hängt
      sonst allein an der Breite, während der Platz für den Kopf darunter aus
      der Höhe kommt. Auf einem flachen Fenster (1440 × 500) blieb dem Kopf
      dadurch ein Band von 73.9 px bei 74.0 px Inhalt — er lag exakt auf der
      Kante und darunter im Logo. Die Grenze hält die Wortmarke bei rund 18 %
      der Bühnenhöhe; sie greift ab etwa 800 px Fensterhöhe und kostet auf einem
      768er Laptop vier Prozent Breite.
    */
    --ku-logo-w: min(88vw, 880px, 110svh);
    --ku-logo-h: calc(var(--ku-logo-w) * 105.58 / 627.49);
    /*
      Abstand von der Bühnenunterkante zur OBERKANTE des Scroll-Hinweises —
      die Untergrenze des Kopf-Bandes. Der Hinweis ist eine globale Komponente
      mit fester Größe (10 px Text, 12 px Abstand, 34 px Haarlinie, 34 px über
      der Kante); den Wert hier trotzdem nicht ausrechnen, sondern in build()
      messen und als Property setzen. Sonst bräche der Kopf still, wenn sich
      die Komponente ändert. Der Vorgabewert trägt nur den ersten Frame.
    */
    --ku-hint-top: 92px;
    /*
      Mindestplatz für den Kopf unter der Wortmarke. Reicht die Bühnenhöhe
      nicht, rückt die WORTMARKE nach oben, statt den Kopf zusammenzudrücken.
    */
    --ku-copy-reserve: 150px;
    /*
      Untergrenze für die Oberkante der Wortmarke. Bewusst eine feste Zahl und
      nicht `var(--header-height)`: der Header schrumpft beim Scrollen von 108
      auf 84, die Wortmarke würde dann mitwandern. 132 deckt auch den höheren
      Header schmaler Viewports (121 px) ab. Der Startbogen bekommt seine
      eigene Grenze im Skript, weil er noch weiter hinaufreicht.
    */
    --ku-top-safe: 132px;
    /*
      Lage der Wortmarke: die Bildmitte, solange darunter der Vorrat für den
      Kopf bleibt. Sonst so weit hinauf, wie es dafür braucht — begrenzt durch
      die Untergrenze. Der Übergang ist stetig; er setzt bei rund 650 px
      Fensterhöhe ein.
    */
    --ku-logo-top: max(
        calc(var(--ku-top-safe) + var(--ku-logo-h) / 2),
        min(
            50%,
            calc(100% - var(--ku-hint-top) - var(--ku-copy-reserve) - var(--ku-logo-h) / 2)
        )
    );

    position: sticky;
    top: 0;
    // svh statt vh/dvh: konstant, während die Mobile-Adressleiste ein- und
    // ausfährt — dvh würde die gepinnte Bühne bei jedem Scroll umlayouten.
    height: 100vh;
    height: 100svh;
    align-content: end;


    /*
      Scrim der Bühne — NUR noch die obere Bande, und die bleibt stehen.

      Vorher lagen hier zusätzlich eine untere Bande und eine flächige
      Abdunklung, und der ganze Scrim wurde während des Morphs ausgeblendet.
      Das war die Ursache für das Aufhellen zu Scrollbeginn: die Wolken sind
      beim Laden gedämpft, und mit dem Wegblenden wurden sie heller statt
      dunkler. Die flächige Grundverdunklung macht jetzt .ku-scrim für die
      ganze Seite und bleibt dort konstant.

      Unten transparent, damit nichts stehen bleibt, was beim Wegscrollen der
      Bühne als waagerechte Kante über den Inhalt liefe.
    */
    .section__bg::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 1;
        background: linear-gradient(
            to bottom,
            color-mix(in srgb, var(--color-dark-marble-1) 62%, transparent),
            transparent 48%
        );
    }

    // Die SVGs müssen absolut liegen; .section erzwingt für direkte Kinder
    // `position: relative`, dieser (ungelayerte) Block schlägt das.
    &__lines,
    &__morph {
        position: absolute;
    }

    &__lines {
        inset: 0;
        width: 100%;
        height: 100%;
        overflow: visible;
        pointer-events: none;
        /*
          ÜBER Vignette, Kino-Rahmen und Abdunklung (4/5/6): die Ebenen sollen
          den Hintergrund wegdrücken, nicht das Motiv.

          Und über dem Kopf (8): das Flugzeug fliegt VOR der Copy, nicht
          dahinter. Es ist das Motiv der Bühne — hinter dem Satzspiegel zu
          verschwinden macht aus einem Flug eine Hintergrundgrafik.
        */
        z-index: 9;
    }

    &__morph {
        z-index: 7;
        left: 50%;
        // Mittig im Bild — außer es wird zu flach, siehe --ku-logo-top. Vorher
        // fest 42 %: der Ausgleich dafür, dass der Kopf am unteren Rand klebte
        // und die obere Hälfte leer ließ. Seit er das Band zwischen Wortmarke
        // und Hinweis füllt, braucht es den Ausgleich nicht mehr.
        top: var(--ku-logo-top);
        transform: translate(-50%, -50%);
        // Die Animation v2 ist eine liegende Wortmarke (viewBox 627 × 106,
        // Seitenverhältnis rund 5.9:1) statt des hochformatigen A der ersten
        // Fassung — sie braucht deutlich mehr Breite, um lesbar zu sein.
        width: var(--ku-logo-w);
        height: auto;
        // Die Nase darf über die viewBox hinausragen.
        overflow: visible;
        color: var(--color-cashmere-1);
        will-change: opacity;
    }

    /*
      Der Kopf füllt das BAND zwischen Wortmarke und Scroll-Hinweis und sitzt
      darin mittig — er hängt an keiner der beiden Kanten.

      Vorher lag er im Fluss der Sektion, unten ausgerichtet, mit einem festen
      Abstand nach unten, der den Hinweis freihalten sollte. Damit war seine
      Lage eine Funktion der Bühnenhöhe statt der beiden Dinge, zwischen denen
      er steht: auf flachen Fenstern rückte er der Wortmarke auf die Pelle, auf
      hohen stand er verloren am unteren Rand.

      Oben die Unterkante der Wortmarke (Mitte plus halbe Höhe), unten die
      Oberkante des Hinweises, dazwischen `align-content: center`. Eine Spalte
      statt max-width + auto-Rändern, weil das Band ein eigener
      Positionierungskontext ist und nicht mehr im Sektionsraster liegt.
    */
    &__copy {
        /*
          TEST: Verkleinerung von der Unterkante aus. Der Block sitzt ohnehin
          unten in seinem Band; damit wandert die Schrift beim Schrumpfen nach
          unten statt zur Blockmitte hinauf.
        */
        transform-origin: 50% 100%;

        position: absolute;
        z-index: 8;
        top: calc(var(--ku-logo-top) + var(--ku-logo-h) / 2);
        bottom: var(--ku-hint-top);
        left: 0;
        right: 0;
        display: grid;
        grid-template-columns: min(100%, 900px);
        justify-content: center;
        /*
          UNTEN im Band, nicht mittig. Vorbild ist die Bühne der Rick-und-Nick-
          Seite: dort sitzt die große Zeile auf 80,5 % der Fensterhöhe, bei uns
          stand sie mittig auf 71,5 %. Der Abstand nach unten hält sie von der
          Scroll-Aufforderung frei — gemessen landet die Headline damit auf
          rund 80 %, also auf der Höhe der Vorlage.

          `safe` ist die eigentliche Zusicherung: passt der Kopf einmal nicht
          ins Band, fällt die Ausrichtung auf `start` zurück, statt ihn nach
          beiden Seiten überlaufen zu lassen. Er kann dann nach unten in den
          Scroll-Hinweis ragen, aber nie nach oben in die Wortmarke — und das
          ist die Richtung, auf die es ankommt. Die Deckelungen oben und beim
          Schriftgrad sorgen dafür, dass der Fall in der Praxis nicht eintritt;
          das hier fängt den Rest ab.
        */
        align-content: safe end;
        padding-bottom: #{clamped(14px, 24px)};
        padding-inline: var(--page-indent);
        text-align: center;
        text-wrap: balance;
        will-change: opacity;
    }

    /* Was die Wortmarke nicht sagt: Anspruch und Urheber. */
    &__edge-frame {
        position: absolute;
        inset: 0;
        pointer-events: none;
    }

    /* Vom Kino-Rahmen ist nur die OBERE Bande übrig, und die sitzt unter dem
       Header. Jede Bande endet an der Bühnenkante, während der Himmel darunter
       ungedämpft weiterläuft — genau das war die sichtbare waagerechte Kante
       (unten) bzw. die beiden senkrechten Absätze (seitlich). Die flächige
       Abdunklung macht jetzt .ku-scrim für die ganze Seite. Nach unten verlässt
       das Flugzeug die Bühne: dort darf ohnehin kein Balken stehen. */
    &__edge-frame {
        z-index: 5;
        background: linear-gradient(to bottom, rgb(0 0 0 / 72%), transparent 15%);
    }

    /* Der Haus-Scrollhinweis wird global gestylt (sections/_scroll-stage.scss);
       seine Farbvariable hängt dort aber an .section-scroll-stage, die es hier
       nicht gibt. Nur deshalb steht sie hier — sonst bliebe die Haarlinie
       unsichtbar. z-index, damit er über den Kino-Ebenen liegt. */
    --scroll-stage-gold-soft: color-mix(in srgb, var(--color-cashmere-1) 75%, transparent);

    .section-scroll-stage__hint {
        z-index: 8;
        will-change: opacity;
    }
}

/* ── Strecke ────────────────────────────────────────────────────────────── */
.ku-journey {
    --padding-behavior-top: clamped(56px, 120px);
    --padding-behavior-bottom: clamped(80px, 160px);

    // KEIN eigener Hintergrund: hier scheint der Parallax-Himmel durch. Das
    // ist der Unterschied zu Variante C, wo die Strecke auf Kino-Schwarz liegt.
    position: relative;

    /*
      ── DIE WAAGERECHTE KAPPUNG MUSS HIER WEG ────────────────────────────

      Sie kommt NICHT von hier, sondern aus der projektweiten Regel
      `.section { overflow-x: clip }` (scss/sections/_section.scss). Diese
      Sektion ist eine `.section`, erbt sie also. Eine gleichlautende
      Deklaration stand zusätzlich an dieser Stelle — reine Dopplung, ihr
      Entfernen allein bewirkte deshalb nichts.

      In Safari ist dieser Clip die Ursache dafür, dass Flugbahn und Flugzeug
      im oberen Bildteil verschwinden. Am ECHTEN Safari 26.5 bisektiert
      (safaridriver, 1800x1348, Scrollstand 2830; Helligkeit des Flugzeugs im
      Screenshot, 33 = fehlt, 189 = da):

        unverändert                          33
        Bühne position: static              189
        Bühne position: relative            189
        .ku-journey overflow-x: visible     189
        .ku-content z-index: auto           190
        alles andere (explizite z-index,
        Ebenen-Promotion, negativer Rand,
        Himmel, Film, Scrim, Bühnen-SVGs) 27-46

      Der Fehler braucht DREI Bedingungen gleichzeitig: eine klebende Bühne im
      Geschwister davor, diese waagerechte Kappung, und den Stapelkontext auf
      .ku-content. Eine davon wegnehmen genügt — und nur diese hier ist
      verzichtbar: die Bühne muss kleben, und .ku-content braucht seinen
      z-index für die Schichtung zwischen Scrim und Vordergrund.

      Isoliert ist `overflow-x: clip` unschuldig: ein Testfall mit einem Kind,
      das oben aus einem clip-Kasten herausragt, wird in WebKit wie in
      Chromium vollständig gezeichnet. Es ist die Konstellation, nicht die
      Eigenschaft — deshalb war das nur am Gerät zu finden, nicht in headless
      WebKit (das klebende Elemente gar nicht erst zu Ebenen befördert).

      Die globale Regel bleibt unangetastet, sie gilt für jede Sektion jeder
      Seite. Hier wird sie gezielt für DIESE eine aufgehoben, und aufgegeben
      wird dabei nichts Gemessenes: der waagerechte Überlauf der Seite ist
      ohne die Kappung bei 430, 1024, 1512 und 1800 px Breite null
      (scrollWidth == clientWidth), und kein Element in .ku-journey ragt
      heraus. Sollte hier je ein wirklich breiter Baustein entstehen — der
      Kandidat ist die Fallblattanzeige, sie wächst mit `--cols` —, dann
      gehört die Kappung AN IHN und nicht an einen Vorfahren der Flugbahn.
    */
    overflow-x: visible;
}

.ku-route-wrap {
    /*
      Anheben um den Scrollweg des Startbogens (siehe build()). Als
      Außenabstand und nicht als Verschiebung: die Seite soll dadurch wirklich
      kürzer werden, und die Auslöser der Karten sollen an der neuen Lage
      hängen, nicht an der alten.
    */
    margin-top: calc(-1 * var(--ku-lift, 0px));

    /*
      Obergrenze für die Steilheit der Bahn (dx/dy), von buildWaypoints
      ausgelesen. Sie steht hier und nicht im Skript, weil sie am Umbruch hängt:
      ab l pendelt die Achse in einer Gasse innerhalb des Inhalts und 0.82 ist
      die Notbremse gegen Zickzack; darunter geht der Wechsel über die volle
      Breite und ist Absicht — dort wäre dieselbe Notbremse eine Fessel.

      Gemessen, größte tatsächliche Steilheit bei ausgehebelter Deckelung:

        390 px -> 0.62    834 px -> 1.06    1023 px -> 1.09
        600 px -> 0.93    900 px -> 1.07

      Sie wächst zum Umbruch hin, weil der Weg mit der Breite länger wird, die
      Karten dabei aber KÜRZER (weniger Zeilenumbrüche). 1.45 lässt das alles
      durch und bleibt eine Notbremse für den Fall, dass eine Station einmal
      sehr wenig Inhalt hat.
    */
    --max-slope: 1.45;

    /*
      Höhe der Übergabe auf schmalen Breiten. Steht hier und nicht im Skript,
      weil es an DIESEM Breakpoint hängt: ab l pendelt die Achse in einer Gasse
      innerhalb des Inhalts, darunter springt sie zwischen den Bildrändern.

      Genau dieser Sprung ist der Grund. Unter l liegt die Achse der zweiten
      Karte am gegenüberliegenden Rand, der Griff des ersten Knotens ragt
      dadurch 52 px zur Seite und beult die Bahn um 23 px aus. Bei einer tiefen
      Übergabe bleiben davor nur 339 px, auf denen sich das als Schlenker zeigt;
      liegt sie in der Bildmitte, sind es 758 px und dieselbe Ausbuchtung
      verläuft sich.

      `auto` heißt: FRONT_AT_STD im Skript gilt. Nur schmal wird der Wert hier
      festgelegt.
    */
    --ku-front-at: 0.80;

    /*
      Seite der Einflugspur. Sie bleibt auf ALLEN Breiten die natürliche, also
      die der ersten Karte.

      Ein Bogen nach links wäre naheliegend: der Anflug muss auf schmalen
      Breiten 174 px zur Seite und hätte über die volle Breite mehr Weg dafür.
      Es geht aber nicht — er muss senkrecht starten, sonst klappt das Flugzeug
      an der Übergabe vom Morph um. Senkrecht starten und sofort nach links
      wollen widerspricht sich: die Kurve beult zuerst nach RECHTS aus (weg vom
      Ziel) und schwenkt dann zurück. Gemessen wird sie dadurch schärfer statt
      ruhiger — 48 gegen 45 Grad je 100 px, und der Ausschlag liegt kurz vor der
      Übergabe, also an der auffälligsten Stelle.
    */
    --ku-lane-side: auto;

    /*
      Neigung der vorgegebenen Tangente am Stützpunkt der Einflugspur. `none`
      heißt: keine Vorgabe, der Spline rechnet die Tangente aus den Nachbarn.

      Das ist die einzige Stelle der Kurve, an der die Krümmung springen darf —
      eine vorgegebene Tangente sichert nur die RICHTUNG zu (G1), nicht die
      Krümmung (G2). Ob der Sprung stört, hängt an der Breite, und zwar
      gegensätzlich (gemessen als Grad je 100 px Bahnlänge über die erste
      Etappe):

                        mit Vorgabe          ohne Vorgabe
        breit           Spitze 10.2,         Spitze 17.7,
                        kein Wechsel         harter Abfall 16.2 -> -0.5
        schmal          Spitze 25.1,         Spitze 28.4,
                        WECHSEL 20.3 -> -8.8 kein Wechsel

      Schmal liegen Spur und erster Knoten fast übereinander; die vorgegebene
      Richtung passt dann nicht mehr zu dem, was die Kurve von selbst täte, und
      es entsteht ein Vorzeichenwechsel — der Schlenker. Breit ist es
      umgekehrt: dort hält die Vorgabe die Kurve zusammen.

      Der höhere Spitzenwert ohne Vorgabe ist der bessere Handel. Eine hohe,
      aber gleichsinnige Krümmung liest sich als Kurve; ein Vorzeichenwechsel
      liest sich als Knick, auch wenn er kleiner ist. Die Griffe anzugleichen
      (0.45/0.25 -> 0.4/0.4) half hier nicht — es lag an der Richtung, nicht an
      der Länge.
    */
    --ku-handoff-tilt: none;

    grid-column: grid;
    position: relative;

    @include mq-from(l) {
        --max-slope: 0.82;
        --ku-front-at: auto;
        --ku-lane-side: auto;
        --ku-handoff-tilt: 6;
    }

    /* Schichtung — die eigentliche Entscheidung dieser Variante:
       Bahn (2) liegt HINTER den Karten (3), aber ÜBER der Feature-Station (1).
       Das Flugzeug taucht also hinter jeder Karte ab und kommt in der Lücke
       wieder hervor, läuft aber bei Station 03 sichtbar über das Wolkenband. */
    /*
      DER KASTEN ENTHÄLT DIE KURVE — er verlässt sich nicht auf `overflow`.

      Die Einflugspur liegt eine gute halbe Bühnenhöhe ÜBER dem Container
      (siehe buildWaypoints). Bisher stand hier `inset: 0` plus
      `overflow: visible`, der gesamte Anflug lag also AUSSERHALB des
      SVG-Kastens und wurde allein von der Overflow-Regel gehalten.

      Auf einem äußeren `<svg>` ist das eine wackelige Konstruktion, und wie
      wackelig, zeigt die Größenordnung: gemessen bei 1800x1348 und Scrollstand
      2806 lagen Kurvenanfang (Viewport-Y 144), Zeichenfront (826) und das
      ganze Flugzeug (796 bis 856) über der Kastenoberkante (861) — 717 px
      weit. Der Überhang wächst mit der Fensterhöhe (rund 0,53 × Höhe).

      NICHT die Ursache des verschwundenen Pfades in Safari — die war eine
      andere (siehe .ku-journey). Das hier ist Vorsorge: der Anflug hing an
      einer Eigenschaft, die er nicht braucht, sobald der Kasten ihn umfasst.

      Jetzt wird der Kasten nach oben verlängert und der Inhalt um denselben
      Betrag nach unten geschoben (die Gruppe `linesGroup`, Transform aus
      build()). Alle Koordinaten bleiben dadurch Container-Koordinaten — das
      Skript rechnet unverändert weiter, nur der Kasten passt jetzt darum.

      `overflow: visible` bleibt als Gürtel neben dem Hosenträger stehen: der
      Überhang wird auf ganze Pixel gerundet, und ein halber Pixel Rundung darf
      keine Kante erzeugen.
    */
    &__lines {
        position: absolute;
        left: 0;
        width: 100%;
        top: calc(-1 * var(--ku-lines-over, 0px));
        height: calc(100% + var(--ku-lines-over, 0px));
        overflow: visible;
        pointer-events: none;
        z-index: 2;
    }
}

/* Überschrift in der Karte. Nur die erste hat eine — die Abschnitte der Reise
   tragen den gelieferten Text ohne erfundene Titel. */
:slotted(.ku-station) .ku-card__headline {
    margin: 0 0 clamped(16px, 26px);
    font-family: var(--font-family-newsreader);
    font-weight: 400;
    font-size: clamped(26px, 44px);
    line-height: 1.12;
    color: var(--color-white-sand-1);
    text-wrap: balance;
}

:slotted(.ku-station) .ku-card__body + .ku-card__body {
    margin-top: clamped(12px, 18px);
}

/* Eckdaten. Jede Angabe steht unter einer Haarlinie — dieselbe Sprache wie die
   Trenner der Seite, und sie ersetzt die Aufzählungspunkte, die hier zu laut
   wären. Zweispaltig, sobald Platz ist: vier Angaben untereinander lesen sich
   als Liste, nebeneinander als Ausstattung. */
:slotted(.ku-station) .ku-facts {
    display: grid;
    gap: clamped(12px, 18px) clamped(24px, 48px);
    margin: clamped(20px, 30px) 0 0;
    padding: 0;
    list-style: none;

    @include mq-from(s) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    li {
        padding-top: clamped(8px, 12px);
        border-top: 1px solid var(--ku-hair);
        color: var(--color-white-sand-1);
        font-family: var(--font-family-newsreader);
        font-size: clamped(16px, 21px);
        line-height: 1.3;
    }

    /*
      Mit Icon wird die Zeile zweispaltig. Nur DIESE Zeilen — eine Liste, in der
      manche Angaben ein Icon haben und andere nicht, soll nicht auf ganzer
      Breite einrücken, nur damit alles gleich ausgerichtet ist. Die Haarlinie
      bleibt der gemeinsame Bezug, nicht der Textanfang.
    */
    &__item--icon {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: start;
        column-gap: clamped(8px, 11px);
    }

    /*
      GRÖSSE UND LAGE HÄNGEN AM MOTIV, NICHT AN DER DATEI.

      Die Icons sind Material Symbols: feste 24er-Box, das Zeichen darin
      zentriert und mit konstruktivem Rand. Gemessen füllen die Motive nur 54
      bis 71 % der Boxhöhe (Croissant 13, Ticket und Film 14, Geschenk 17 von
      24) und sitzen 12 bis 23 % von oben. Setzt man die BOX auf Texthöhe,
      bleibt vom Icon also gut ein Drittel weniger übrig — davor stand hier
      0.82em, und die Motive kamen mit 8,6 px gegen 12,1 px Versalhöhe an.

      Deshalb ist die Box auf die Versalhöhe HOCHGERECHNET: 0.676em Versalhöhe
      geteilt durch die 0.60 Boxfüllung des Regelfalls ergibt 1.13em, gerundet
      1.15em. Damit misst das Motiv der drei flachen Icons 12,0 px gegen 12,1
      px Versalhöhe. Das Geschenk ragt mit 14,6 px darüber hinaus — das ist
      Absicht der Vorlage (ein hohes schmales Zeichen muss höher sein, um
      gleich groß zu WIRKEN), und genau dafür gibt es die gemeinsame Box.

      Der Versatz richtet das MOTIV oben an der Versalhöhe aus, nicht die Box:
      die Box-Oberkante liegt an der Zeilenoberkante, die Luft darin (19 % von
      1.15em = 0.22em) trägt das Motiv fast genau auf den Versal-Anfang bei
      0.197em. Die verbleibenden 0.8 px nimmt der negative Rand heraus.
    */
    &__icon {
        display: block;
        width: 1.15em;
        margin-top: -0.04em;

        /*
          KEIN :deep mehr noetig, seit das Markup als Slot-Inhalt aus dem Twig
          kommt: Nachfahren von :slotted(...) tragen ueberhaupt keine Scope-ID,
          ein :deep() dort greift ins Leere. Vorher kam das SVG per v-html
          herein und brauchte es — ohne blieb es bei seiner Standardgroesse
          (gemessen 267 px) und schwarz. Genau das war es auch wieder, als der
          Umzug diese Zeile zunaechst unveraendert liess: die Eckdatenliste
          sprengte die Karte auf 959 statt 466 px.

          Ein hochgeladenes SVG bringt meist eine viewBox und KEINE Maße mit —
          dann füllt es seinen Kasten. Bringt es doch welche mit, überschreibt
          width/height sie; height: auto hält dabei das Seitenverhältnis.
        */
        svg {
            display: block;
            width: 100%;
            height: auto;
            // Einfarbige Dateien nehmen die Textfarbe an. Dateien mit fest
            // eingebauten Farben behalten ihre — dann greift fill nicht durch.
            fill: currentcolor;
        }
    }
}

:slotted(.ku-station) {
    /*
      Die Achse pendelt auf JEDER Breite — nur zwischen anderen Punkten.

      Ab l zwischen zwei Prozentwerten im Inhaltsbereich, die Karte daneben
      (siehe AXES im Skript). Darunter ist für eine Karte NEBEN einer
      wandernden Achse keine Breite; dort pendelt die Achse deshalb zwischen den
      beiden RÄNDERN, und die Karte nimmt die ganze Restbreite auf der anderen
      Seite. Dieselbe Abwechslung, nur bis an die Ränder gezogen.

      Vorher lagen mobil alle Knoten auf derselben Achse links. Aus der Spline
      wurde damit eine Senkrechte, und das Flugzeug fiel die ganze Seite lang
      im Sturzflug nach unten — es hatte nichts zu fliegen.

      `--gutter` ist die Randspur, auf der die Achse sitzt; sie ist von `--axis`
      getrennt, weil die Achse mobil am RECHTEN Rand liegen kann und dann keine
      Spaltenbreite mehr ist.
    */
    --gutter: 8px;
    --axis: var(--gutter);

    position: relative;
    z-index: 3;
    display: grid;
    grid-template-columns: var(--gutter) 1fr;

    /*
      `& + .ku-station` statt `& + &`: das zweite `&` expandierte zu einem
      ZWEITEN :slotted(...) im selben Selektor, und Vue transformiert nur das
      erste — das uebrige blieb als unbekanntes Pseudo-Element stehen, woraufhin
      der Browser die GANZE Regel verwarf. Der Abstand zwischen den Karten fiel
      damit auf 0 (statt 200 px bei 1200 px Breite) und die Strecke schrumpfte
      um 600 px. Dieselbe Falle gilt fuer jedes `&`, das nicht am Anfang steht.

      Grosszuegig, weil die Bahn hier ihren ganzen Seitenwechsel unterbringen
      muss — enger wird aus der Kurve ein Knick.
    */
    & + .ku-station {
        margin-top: clamped(110px, 340px);
    }

    @include mq-from(l) {
        --axis: var(--axis-x, 50%);
        grid-template-columns: var(--axis) 1fr;
    }
}

/*
  Die gespiegelte Anordnung: Achse rechts, Karte links. Ab l macht das die
  Achsposition aus den Daten, darunter der Rand — der Fall muss deshalb zweimal
  stehen, einmal je Seite der Bruchstelle.
*/
:slotted(.ku-station--left) {
    @include mq-to(l) {
        --axis: calc(100% - var(--gutter));

        grid-template-columns: 1fr var(--gutter);
    }
}

/* Destination: 0×0 und niemals animiert — sie ist die Stützstelle der Kurve.
   Alles Sichtbare hängt als eigenes Kind daran, sonst würden Reveal-Transforms
   die gemessene Geometrie verschieben. `translate` gehört dem JS (Korrektur
   aus der Steilheits-Deckelung), `transform` bleibt für GSAP frei. */
/*
  ZWEI SELEKTOREN, weil es .ku-node an zwei Herkuenften gibt: die Destinationen
  der Stationen kommen als Slot-Inhalt aus dem Twig und tragen nur die
  Slot-Scope-ID (-s), der Endpunkt (.ku-node--end im Template dieser Komponente)
  traegt die normale. Eine einzelne Regel kann beide nicht treffen.
*/
:slotted(.ku-station) .ku-node,
.ku-node {
    position: absolute;
    left: var(--axis);
    top: var(--node-y, 50%);
    width: 0;
    height: 0;
    z-index: 4;

    &__dot,
    &__ring {
        position: absolute;
        left: 0;
        top: 0;
        translate: -50% -50%;
        border-radius: 50%;
    }

    &__dot {
        width: 9px;
        height: 9px;
        background: color-mix(in srgb, var(--color-white-sand-1) 28%, transparent);
        transition: background-color 0.45s ease, box-shadow 0.45s ease;
    }

    &.is-reached .ku-node__dot {
        background: var(--color-cashmere-1);
        box-shadow: 0 0 0 5px color-mix(in srgb, var(--color-cashmere-1) 12%, transparent),
                    0 0 18px color-mix(in srgb, var(--color-cashmere-1) 55%, transparent);
    }

    &__ring {
        width: clamped(28px, 44px);
        height: clamped(28px, 44px);
        border: 1px solid var(--ku-hair-gold);
        transition: border-color 0.5s ease, box-shadow 0.5s ease;

        &::after {
            content: "";
            position: absolute;
            inset: 0;
            margin: auto;
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: var(--color-cashmere-1);
        }
    }

    &.is-reached .ku-node__ring {
        border-color: var(--color-cashmere-1);
        box-shadow: 0 0 26px color-mix(in srgb, var(--color-cashmere-1) 32%, transparent);
    }

}

/* ── Karte ──────────────────────────────────────────────────────────────── */
/* Karte. Die Rahmenlosigkeit ist das Rezept der Finder-Karte aus dem Projekt
   (sections/_finder.scss); der Verlauf weicht davon ab (siehe unten).
   Die Breite steht nicht hier: sie kommt aus der Rasterspalte der Station
   (`--axis-x`, siehe AXES im Skript). */
:slotted(.ku-station) .ku-card {
    /*
      Innenabstand als Variable, weil die Bilder ihn wieder herausrechnen
      müssen: sie sollen randlos an der Kartenkante sitzen (siehe unten).
    */
    --ku-card-pad: #{clamped(20px, 52px)};

    grid-column: 2;
    margin-left: clamped(26px, 36px);
    padding: var(--ku-card-pad);
    /*
      Der Verlauf läuft nach unten ins Dunkle statt aufzuhellen: von Dark
      Marble 1 (#121318) auf #0E0F11 mit 85 % Deckung. Hinter der Karte steht
      der Seitengrund `.ku` (#050505) opak, die Kartenunterkante landet also
      bei rund #0D0E0F — die Transparenz vertieft hier, sie lässt nichts
      durchscheinen. Ein Verlauf für alle Viewports, keine Sonderregel für
      schmal.
    */
    background: linear-gradient(to bottom, var(--color-dark-marble-1), #0e0f11d9);
    // Der Schatten ist die einzige Zutat, die die Finder-Karte nicht hat: die
    // steht auf einer Sektionsfläche, diese schwebt über Wolken und Karte und
    // bräuchte sonst eine Kante, um sich abzusetzen.
    box-shadow: 0 40px 90px rgb(0 0 0 / 45%);
    // Bilder laufen bis an die Kante — nichts darf darüber hinausragen.
    overflow: hidden;

    @include mq-from(l) {
        margin-left: clamped(48px, 96px, $min-bp: 1024px);
    }
}

:slotted(.ku-station--left) .ku-card {
    @include mq-to(l) {
        grid-column: 1;
        margin-left: 0;
        margin-right: clamped(26px, 36px);
    }

    @include mq-from(l) {
        grid-column: 1;
        margin-left: 0;
        margin-right: clamped(48px, 96px, $min-bp: 1024px);
    }
}

:slotted(.ku-station) .ku-card__inner {
    display: grid;
    gap: clamped(18px, 30px);
}

:slotted(.ku-station) .ku-card__lead {
    margin: 0 0 clamped(10px, 16px);
    font-family: var(--font-family-newsreader);
    font-style: italic;
    font-size: clamped(19px, 28px);
    line-height: 1.32;
    color: var(--color-cashmere-1);
    text-wrap: balance;
}

:slotted(.ku-station) .ku-card__body {
    margin: 0;
    max-width: 62ch;
    color: var(--color-white-sand-3);
    font: var(--font-body-1);
}

/* Bildrahmen. Beschnitt und Positionierungskontext liegen hier, nicht auf der
   <figure>: die Bildunterschrift steht außerhalb und darf nicht mitbeschnitten
   oder von Overlays überdeckt werden. */
:slotted(.ku-station) .ku-frame {
    position: relative;
    display: block;
    overflow: hidden;

    // `img` statt `> img`: aus dem CMS kommt ein <picture> mit dem <img>
    // darin, der Fallback liefert ein nacktes <img>. Kein aspect-ratio, kein
    // object-fit mehr — das Bild läuft im hochgeladenen Seitenverhältnis, die
    // width/height-Attribute (setzt media-optimized-image) verhindern CLS.
    img {
        display: block;
        width: 100%;
        height: auto;
    }
}

:slotted(.ku-station) .ku-card__media {
    position: relative;
    margin: 0;

    figcaption {
        margin-top: clamped(8px, 12px);
        color: var(--color-dark-marble-2);
        font-size: var(--font-size-body-2);
        letter-spacing: 0.1em;
    }
}

/* ── Varianten: wo das Bild in der Karte sitzt ──────────────────────────── */

/* Bild neben dem Text, ab l zweispaltig. */
:slotted(.ku-station--media-side) .ku-card__inner {
    @include mq-from(l) {
        grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
        column-gap: clamped(30px, 60px, $min-bp: 1024px);

        .ku-card__text { grid-column: 1; grid-row: 1; }

        /*
          Das Bild füllt die rechte Spalte über die GANZE Kartenhöhe und sitzt
          randlos an drei Kanten (oben, rechts, unten). Deshalb hier `stretch`
          statt `start`: sonst endete das Bild irgendwo mitten in der Karte
          und hätte dort eine harte Kante — genau die, die es nicht geben
          soll. Weil die Höhe jetzt vorgegeben ist, braucht es hier
          `object-fit: cover` (das einzige Format, in dem das Bild beschnitten
          wird; das Band oben läuft weiterhin nativ).
        */
        .ku-card__media {
            grid-column: 2;
            grid-row: 1;
            align-self: stretch;
            margin: calc(var(--ku-card-pad) * -1) calc(var(--ku-card-pad) * -1) calc(var(--ku-card-pad) * -1) 0;
        }

        .ku-frame,
        .ku-frame picture,
        .ku-frame img {
            height: 100%;
        }

        .ku-frame img {
            object-fit: cover;
        }
    }

    // Darunter steht das Bild unter dem Text: randlos zu drei Seiten.
    @include mq-to(l) {
        .ku-card__media {
            margin: 0 calc(var(--ku-card-pad) * -1) calc(var(--ku-card-pad) * -1);
        }
    }
}

/* Bild über die volle Kartenbreite und ÜBER dem Text. */
:slotted(.ku-station--media-top) .ku-card__inner {
    .ku-card__media {
        order: -1;
        // Randlos an Oberkante und beiden Seiten.
        margin: calc(var(--ku-card-pad) * -1) calc(var(--ku-card-pad) * -1) 0;
    }
}

/*
  WEICHER AUSLAUF DER BILDKANTE — die Richtung folgt der Bildlage.

  Die Bilder sitzen randlos an der Kartenkante. Damit gibt es nur noch EINE
  Kante, die nichts trennt: die zum Text hin. Genau dort läuft das Bild aus,
  überall sonst schließt es an den Kartenrand an.

    Bild über dem Text   -> Auslauf nach UNTEN
    Bild neben dem Text  -> Auslauf nach LINKS (Desktop, Bild steht rechts)
                            bzw. nach OBEN (schmal, Bild steht unter dem Text)

  MEHRERE STUFEN statt zweier Endpunkte. Ein linearer Verlauf im Maskenkanal
  wird als Band sichtbar, weil Helligkeit nicht linear wahrgenommen wird — die
  Staffelung ist dieselbe wie in _stage-treatment.scss.

  Der Verlauf beginnt erst im letzten Viertel: weich wird die Kante, das Motiv
  bleibt unangetastet. Beim Jet auf dem Vorfeld steht die Spiegelung im nassen
  Asphalt genau dort, die soll nicht verschwinden.
*/
@mixin ku-fade($to) {
    --_fade: linear-gradient(
        to #{$to},
        #000 76%,
        rgb(0 0 0 / 88%) 85%,
        rgb(0 0 0 / 55%) 92%,
        rgb(0 0 0 / 18%) 97%,
        transparent 100%
    );

    /*
      MASKE AUSDRÜCKLICH AUF DIE ELEMENTBOX FESTNAGELN.

      Hier stand nur `mask-image`. Berechnet ergab das `mask-size: auto` und
      `mask-repeat: REPEAT` — und diese Kombination ist auf genau diesen
      Elementen eine Falle:

      Die Maske sitzt auf dem `<img>`, und dessen Höhe kommt aus `height: auto`
      über das Seitenverhältnis, ist also erst bekannt, wenn das (lazy
      geladene) Bild seine intrinsische Größe mitbringt. Wird der Verlauf
      gegen eine noch leere Box aufgelöst und die Box wächst danach, kachelt
      `repeat` das Ergebnis statt es neu aufzuziehen. Übrig bleibt ein
      waagerechter Streifen des Bildes — der Rest ist ausmaskiert.

      Gefunden bei der Suche nach einem Safari-Fehler, der genau so aussah:
      vom Bild der ersten Karte blieb ein Streifen, der Text darunter stand
      vollständig. Die Ursache war es NICHT — das war die waagerechte Kappung
      (siehe .ku-journey), und mit ihr ist auch das Kartenbild behoben.
      Nachgemessen am echten Safari 26.5: Kappung wieder eingeschaltet, oberes
      Bilddrittel Helligkeit 38 bei sonst 231, bei identischer Bildlage.

      Die Kombination `auto` + `repeat` ist trotzdem falsch und bleibt
      korrigiert. Die gemessenen Höhen sind gebrochen (307,555 px), was
      Kachelnähte zusätzlich begünstigen würde.

      `100% 100%` und `no-repeat` sind das, was `auto` + `repeat` im
      Gutfall ohnehin ergeben — die Optik ändert sich also nicht, nur die
      Abhängigkeit vom Zeitpunkt der Bildmaße fällt weg. Beide Schreibweisen,
      weil oben schon beide `mask-image` gesetzt werden.
    */
    -webkit-mask-image: var(--_fade);
    mask-image: var(--_fade);
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: 0 0;
    mask-position: 0 0;
}

:slotted(.ku-station--media-top) .ku-frame img {
    @include ku-fade(bottom);
}

:slotted(.ku-station--media-side) .ku-frame img {
    @include mq-from(l) {
        @include ku-fade(left);
    }

    @include mq-to(l) {
        @include ku-fade(top);
    }
}

/*
  KEIN Sonderstyling mehr für „die letzte Station": die frühere
  :last-of-type-Regel (Abenddämpfung + Beschnitt des Wolkenbands) war an die
  DOM-Position gebunden — mit CMS-gepflegten, umsortierbaren Karten hätte sie
  getroffen, was auch immer gerade zuletzt steht. Solche Bildstimmungen gehören
  ins Bild selbst.
*/

/* ── Terminus / Landung ─────────────────────────────────────────────────── */
/*
  Die Landung ist auf JEDER Breite mittig — anders als die Stationen darüber.

  Dort pendelt die Achse, weil die Reise Abschnitte hat; hier hört sie auf. Der
  Ring ist der Punkt, auf den die ganze Seite zuläuft, und ein Ende, das an
  einem Rand klebt, ist keines. Mobil stand er vorher in der Randspur wie ein
  weiterer Wegpunkt, mit Überschrift und Anzeigetafel linksbündig daneben.

  Damit ist die Landung der einzige Block hier ohne Umbruch-Unterscheidung.
*/
.ku-terminus {
    --axis: 50%;

    position: relative;
    z-index: 3;
    display: grid;
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    margin-top: clamped(88px, 200px);

    &__copy {
        grid-column: 1;
        margin-top: clamped(34px, 60px);
        // Breit genug, dass „Ein Tag. Zwei Flüge." und der Nachsatz je auf
        // eine Zeile passen; 52ch drückte beides in drei Zeilen.
        max-width: 68ch;
    }

}

/* ── Fallblattanzeige ────────────────────────────────────────────────────
   Bauart aus Variante A, hier klein und einmalig: vier Zeilen Eckdaten, die
   aufblättern, sobald die Tafel in den Blick kommt.

   Die Zellgröße hängt an der TAFELBREITE, nicht am Viewport — deshalb ist die
   Tafel selbst der Query-Container. Nur so stimmt die Größe auch dann, wenn
   die Breite durch das Seitenraster gedeckelt wird. */
.ku-board {
    grid-column: 1;
    justify-self: center;
    container-type: inline-size;
    margin: clamped(34px, 64px) 0 0;
    width: min(100%, 840px);

    &__frame {
        position: relative;
        padding: clamp(10px, 2cqw, 20px);
        background: linear-gradient(#0b0a09, #060606);
        border: 1px solid color-mix(in srgb, var(--color-cashmere-1) 20%, transparent);
        box-shadow: 0 30px 80px rgb(0 0 0 / 60%);
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 10px;
        padding-bottom: clamp(7px, 1.3cqw, 14px);
        margin-bottom: clamp(7px, 1.3cqw, 14px);
        border-bottom: 1px solid color-mix(in srgb, var(--color-cashmere-1) 20%, transparent);
        color: var(--color-cashmere-1);
        font-size: clamp(8px, 1.1cqw, 12px);
        letter-spacing: 0.24em;
        text-transform: uppercase;
        // Kopf erst zeigen, wenn die Tafel steht: sonst steht die Beschriftung
        // fertig über einem Raster, das noch blättert.
        opacity: 0;
        transition: opacity 1s ease;
    }

    &.is-ready &__head { opacity: 1; }

    &__rows {
        display: grid;
        gap: max(2px, 0.4cqw);
    }

    // Der lesbare Inhalt für Screenreader; das Zeichenraster selbst ist
    // aria-hidden, sonst liest die Ausgabe ~90 Einzelbuchstaben vor.
    &__sr {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: 0;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
    }
}

.ku-row {
    display: grid;
    grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
    gap: max(1px, 0.25cqw);

    // Hierarchie: Datum und Strecke hell, die Zusätze gedämpft.
    &:nth-child(2) .ku-flap__leaf { color: var(--color-white-sand-1); }
    &:nth-child(3) .ku-flap__leaf,
    &:nth-child(4) .ku-flap__leaf {
        color: color-mix(in srgb, var(--color-white-sand-1) 62%, transparent);
    }
}

.ku-flap {
    position: relative;
    // Fallblätter sind hochkant — das Verhältnis stammt von echten Modulen.
    aspect-ratio: 1 / 1.34;
    overflow: hidden;
    background: linear-gradient(#17150f 0 50%, #0c0b08 50% 100%);
    box-shadow: inset 0 0 0 1px rgb(255 255 255 / 3%);
    // Ohne Perspektive wäre rotateX nur ein vertikales Stauchen.
    perspective: 180px;

    // Die Trennfuge in der Mitte trägt den Effekt: sie sagt „zwei Halbblätter",
    // bevor sich überhaupt etwas bewegt.
    &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: 50%;
        height: 1px;
        z-index: 2;
        background: rgb(0 0 0 / 88%);
    }

    &__leaf {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: var(--font-family-body);
        font-weight: 500;
        line-height: 1;
        color: var(--color-cashmere-1);
        // Fallback zuerst: ohne cqw-Unterstützung bleibt diese Zeile stehen.
        font-size: clamp(7px, 1.6vw, 20px);
        font-size: calc(94cqw / var(--cols) * 0.66);
        transform-origin: 50% 50%;
        backface-visibility: hidden;
    }

    // Dauer gleich STEP_MS im Skript: Zeichenwechsel und Drehung bleiben damit
    // in Phase, ohne je Schritt neu gestartet zu werden.
    &.is-spinning &__leaf {
        animation: ku-flip 42ms linear infinite;
    }
}

@keyframes ku-flip {
    0% { transform: rotateX(0deg); }
    49% { transform: rotateX(-88deg); }
    50% { transform: rotateX(88deg); }
    100% { transform: rotateX(0deg); }
}

.ku-terminus .ku-node {
    top: 0;
    // Zentriert über dem Text, nicht am Rand: hier endet die Bahn.
    left: 50%;
}

/* Die Kontaktsektion legt einen Schleier über den Hintergrund. Beginnt der an
   der Sektionskante hart, entsteht genau die waagerechte Kante, die wir aus dem
   Bühnenübergang herausgenommen haben — also läuft er ein. Hier statt in der
   Komponente, weil er von dem abhängt, worauf die Sektion steht. */
:deep(.ku-contact) {
    background: linear-gradient(
        to bottom,
        transparent,
        color-mix(in srgb, #050505 82%, transparent) clamped(140px, 300px)
    );
}

/* ── Reduced Motion ─────────────────────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
    /* Ohne Bewegung löst sich nichts auf — also ist die Decke von vornherein
       dünn. applyDissolve(1) im Skript setzt die Flecken zusätzlich auf ihren
       Endzustand; das hier deckt den Fall ab, dass build() gar nicht erst
       durchläuft. */
    .ku-sky__deck { transform: none !important; }
    .ku-sky__chart { transform: none !important; opacity: 0.3 !important; }
    .ku-sky__haze { opacity: 0.2; }
    .ku-sky__puff { opacity: 0.3; }

    /* Der Vordergrund hat ohne Bewegung keinen Zweck: seine ganze Wirkung ist
       der Geschwindigkeitsunterschied zu den Ebenen dahinter. Stehend wäre er
       nur ein Schleier über dem Inhalt. */
    .ku-fore { display: none; }

    .ku-track { height: auto; }

    .ku-stage {
        position: static;
        height: auto;
        --padding-behavior-top: var(--dimension-extra-large);
        --padding-behavior-bottom: var(--dimension-extra-large);
    }

    .ku-stage__morph { position: static; transform: none; margin: 0 auto clamped(32px, 64px); }
    // Ohne Pin ist die Bühne nicht mehr bildschirmhoch — ein Band zwischen
    // zwei Kanten, die es so nicht gibt, hätte keinen Bezug mehr.
    .ku-stage__copy {
        position: static;
        inset: auto;
        padding-inline: 0;
        // Zurück ins Sektionsraster. Ohne Spaltenangabe fiele der Kopf als
        // Grid-Kind in die implizite erste Spur — eine schmale Randspalte.
        grid-column: grid;
    }
    .ku-stage__hint { display: none; }
    .ku-plane { display: none; }

    .ku-flap.is-spinning .ku-flap__leaf { animation: none; }
    .ku-board__head { opacity: 1; }

    /* Getrennt, weil die Liste zwei Herkuenfte mischt: .ku-hair und der
       Terminus-Punkt rendert die Komponente selbst, die Karten kommen als
       Slot-Inhalt aus dem Twig und brauchen deshalb :slotted(). */
    .ku-hair,
    .ku-node__dot {
        opacity: 1 !important;
        visibility: visible !important;
        transform: none !important;
        translate: none !important;
    }

    :slotted(.ku-station) .ku-card,
    :slotted(.ku-station) .ku-node__dot,
    :slotted(.ku-station) .ku-card__lead,
    :slotted(.ku-station) .ku-card__body,
    :slotted(.ku-station) .ku-facts,
    :slotted(.ku-station) .ku-card__media {
        opacity: 1 !important;
        visibility: visible !important;
        transform: none !important;
        translate: none !important;
    }
}

/* ── Slot-Texte ───────────────────────────────────────────────────────────
   Diese drei Regeln standen als &__headline / &__byline in den Bloecken
   .ku-stage bzw. .ku-terminus. Seit die Texte als Slot-Inhalt aus dem Twig
   kommen, tragen sie nur die Slot-Scope-ID (-s) und wurden von der
   verschachtelten Form nicht mehr getroffen. Inhaltlich unveraendert, nur
   ausgehaengt und dedentiert. */

:slotted(.ku-stage__headline) {
    /* Nur Behaelter: die Byline steckt mit drin und soll NICHT Schrift
       und Zeilenhoehe der Ueberschrift erben.

       font-weight MUSS hier stehen, nicht nur am Titel-Span: die
       Browser-Vorgabe fuer h1 ist bold, und die erbt sonst die Byline mit.
       Genau das war passiert, als die Typografie auf den Span wanderte. */
    margin: 0;
    font-weight: 400;
}

:slotted(.ku-stage__title) {
    display: block;
    /* unicode-bidi: isolate ist die Browser-Vorgabe fuer Blockelemente. Vorher
       waren das hier ein h1 und ein p, jetzt sind es Spans — die bekommen sie
       nicht. Bei deutschem und englischem Text faellt das nicht auf, in der
       arabischen Site sehr wohl: ohne Isolation koennen Richtungswechsel ueber
       die Elementgrenze bluten. */
    unicode-bidi: isolate;
    font-family: var(--font-family-newsreader);
    font-weight: 400;
    /*
      Zweite Deckelung über die Höhe, aus demselben Grund wie bei der
      Wortmarke: clamped() skaliert mit der BREITE, der Platz kommt aber aus
      der Höhe. Ohne sie stünde auf 1440 x 400 eine 44-px-Zeile in einem
      Band von 61 px. Greift ab rund 590 px Fensterhöhe.
    */
    font-size: min(clamped(26px, 44px), 7.5svh);
    line-height: 1.14;
    color: var(--color-white-sand-1);
    text-wrap: balance;
}

:slotted(.ku-stage__byline) {
    display: block;
    /* unicode-bidi: isolate ist die Browser-Vorgabe fuer Blockelemente. Vorher
       waren das hier ein h1 und ein p, jetzt sind es Spans — die bekommen sie
       nicht. Bei deutschem und englischem Text faellt das nicht auf, in der
       arabischen Site sehr wohl: ohne Isolation koennen Richtungswechsel ueber
       die Elementgrenze bluten. */
    unicode-bidi: isolate;
    margin: clamped(10px, 14px) 0 0;
    color: var(--color-white-sand-3);
    font-size: var(--font-size-body-2);
    letter-spacing: 0.22em;
    text-transform: uppercase;
}

:slotted(.ku-terminus__headline) {
    margin: clamped(14px, 22px) 0 clamped(12px, 20px);
    font-family: var(--font-family-newsreader);
    font-weight: 400;
    font-size: clamped(28px, 56px);
    line-height: 1.12;
    color: var(--color-white-sand-1);
    text-wrap: balance;

    i { font-style: italic; color: var(--color-cashmere-1); }
}

</style>
